/**
 * The Core PosterMyWall JavaScript object. Other attributes are added to this
 * object from additional JavaScript files.
 *
 * IMPORTANT: Any code in this file that executes before the DOM loads must be
 * independent of any external library. Code that executes after the DOM has loaded
 * can assume the presence of jQuery or Zepto (in a mobile environment).
 *
 * @type Object
 * @author Jaffer Haider <jaffer@250mils.com>
 */

/**
 * Similar to ES6's rest param (http://ariya.ofilabs.com/2013/03/es6-and-rest-parameter.html)
 * This accumulates the arguments passed into an array, after a given index.
 * @param func
 * @param startIndex
 * @returns {Function}
 */
var restArgs = function (func, startIndex) {
    startIndex = startIndex == null ? func.length - 1 : +startIndex;
    return function () {
        var length = Math.max(arguments.length - startIndex, 0);
        var rest = Array(length);
        for (var index = 0; index < length; index++) {
            rest[index] = arguments[index + startIndex];
        }
        switch (startIndex) {
            case 0:
                return func.call(this, rest);
            case 1:
                return func.call(this, arguments[0], rest);
            case 2:
                return func.call(this, arguments[0], arguments[1], rest);
        }
        var args = Array(startIndex + 1);
        for (index = 0; index < startIndex; index++) {
            args[index] = arguments[index];
        }
        args[startIndex] = rest;
        return func.apply(this, args);
    };
};

class SSORedirectHelper {
    /**
     * Constructor for the SSORedirectHelper class
     * @param {function} callback
     * @param {string} redirect
     */
    constructor(callback, redirect = "") {
        this._callback = callback;
        this._redirect = redirect;
    }

    /**
     * Prepares the class object from an existing object
     * @param {Object|SSORedirectHelper} vo
     */
    initFromVO(vo) {
        this._callback = vo._callback;
        this._redirect = vo._redirect;
    }

    /**
     * @returns {Function}
     */
    get callback() {
        return this._callback;
    }

    /**
     * @returns {string}
     */
    get redirect() {
        return this._redirect;
    }

    /**
     * Returns the callback or redirect url depending on the variables that exist
     * @returns {string|Function}
     */
    getPostLoginAction() {
        if (this._redirect === "") {
            return this._callback;
        }
        return this._redirect;
    }
}

/**
 * Extracts and forms an error for ajax response
 * @param {Object} response
 * @param {string} uri
 * @param {Object} data
 * @return {Error|*}
 */
const getErrorAjaxResponse = (response, uri, data) => {
    if (response.status === 'success') {
        return response.data;
    }

    let details = '';
    if (response.message) {
        details += ' Error Message: ' + response.message;
    }
    if (response.data) {
        details += ' Error data: ' + JSON.stringify(response.data);
    }

    const error = new Error(response.message ? response.message : '');
    error.code = response.code;
    error.data = response.data;
    error.status = response.status;
    error.detail = 'Failed to write ' + JSON.stringify(data) + ' to url: ' + PMW.util.site_url(uri) + '.' + (details ? details : '');
    return error;
};

if ('serviceWorker' in navigator) {
    window.addEventListener('load', function () {
        let pathToServiceWorker = '/serviceWorker.js';

        if (window.location.href.includes('/pmw/')) {
            pathToServiceWorker = '/pmw' + pathToServiceWorker;
        }

        navigator.serviceWorker
            .register(pathToServiceWorker)
            .catch(err => console.log('service worker not registered', err));
    });
}

/**
 * Constants to describe the animation states of modal dialogs
 * @type {string}
 */
const MODAL_ANIMATION_TYPE_OPEN = 'open';
const MODAL_ANIMATION_TYPE_CLOSE = 'close';

/**
 * Time in millisecond after which we timeout the long ajax polling function
 * @type {number}
 */
const LONG_AJAX_CALL_TIMEOUT = 1800000;

const NUMBER_FORMAT_TYPE_CURRENCY = 'currency';
const NUMBER_FORMAT_PART_TYPE_CURRENCY = 'currency';
const CURRENCY_FORMAT_MAX_FRACTION_DIGITS_NO_DECIMALS = 0;
const CURRENCY_FORMAT_MAX_FRACTION_DIGITS_WITH_DECIMALS = 2;
const CLIPBOARD_FALLBACK_KEY = 'pmw_cb_data';

if (typeof PMW === 'undefined') {
    window.PMW = {};
}

/**
 * Copies (own) properties of the source into target only if they aren't defined in the target.
 * @param {Object} target
 * @param {Object} source
 */
PMW.mergePMW = function (target, source) {
    for (var key in source) {
        if (source.hasOwnProperty(key) && typeof target[key] === 'undefined') {
            target[key] = source[key];
        }
    }
};

PMW.mergePMW(PMW, {
    /*************
     * Required constants
     *************/

    MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS: 10,
    MAX_NUMBER_OF_PAGES: 99,

    /**
     * A version number for invalidating browser caches of our JS. Set by PHP on page load in BaseHeadCommon.php.
     * @type {number}
     */
    VERSION: 1,
    /**
     * Same as site_url() in PHP, with the addition of a trailing slash.
     * Initialized in BaseHead.php
     * @type {string}
     */
    SERVER_ROOT: '',
    /**
     * WARNING: ONLY USE PMW.BASE_URL DIRECTLY IF ONLY THE BASEPATH FOR THE ENGLISH DOMAIN IS NEEDED, E.G., https://www.postermywall.com/
     * FOR OTHER CASES, PLEASE REFER TO USAGES OF PMW.util.site_url(), PMW.util.asset_url(), or PMW.util.homepage_url()
     *
     * Same as base_url() in PHP. Has a trailing slash.
     * Initialized in BaseHeadCommon.php
     * @type {string}
     */
    BASE_URL: '',
    /**
     * Our application ID with Facebook.
     * @type {string}
     */
    FB_APP_ID: '',
    /**
     * Our application ID with Google.
     * @type {string}
     */
    GOOGLE_APP_ID: '',
    /**
     * Ask for email by default, other permissions on a need basis
     * @type {string}
     */
    FB_PERMISSIONS: 'email',
    /**
     * Number of days for tracking cookie expiry
     * @type {number}
     */
    TRACKING_COOKIE_EXPIRY: 1095,   // 3 * 365, three years
    /**
     * Number of days for eligible country code cookie expiry
     * @type {number}
     */
    ELIGIBLE_COUNTRY_CODE_COOKIE_EXPIRY: 365,   // 1 * 365, 1 year
    COOKIE_TTL_SIX_HOURS: 0.25,
    COOKIE_TTL_TEN_MINUTES: 0.007,
    COOKIE_TTL_ONE_DAY: 1,
    /**
     * Cookie name for when a user makes a free download (controls post-free-download upselling dialog)
     * @type {string}
     */
    COOKIE_FREE_DOWNLOAD: 'pmwfdd',
    COOKIE_FREE_DOWNLOAD_VALUE_UPSELLING_SHOWN: '1',
    COOKIE_FREE_DOWNLOAD_VALUE_BLOCK_UPSELLING: '-1',
    /**
     * Used for enabling/disabling Google Tag Manager. Disabled in the dev environment.
     * @type {boolean}
     */
    enableGTM: true,
    /**
     * Used for enabling/disabling Google Analytics. Disabled in the dev environment.
     * @type {boolean}
     */
    enableGATracking: true,
    /**
     * Used for enabling/disabling Online Ads tracking. Services tracked:
     *  - Google Adwords
     *  - Facebook
     *  - Pinterest
     *  - Bing
     * Disabled in a non-production environment.
     * @type {boolean}
     */
    enableOnlineAdsTracking: true,
    /**
     * Const for the 'category' value of Google Analytics tracking for the Poster Builder (HTML5)
     * @type {string}
     */
    POSTERBUILDER: 'PosterBuilder',
    /**
     * Used to differentiate from which AddThis toolbox the addthis sharing event was fired from
     * @type {string}
     */
    sharingAfterAction: '',
    /**
     * Use jQuery Mobile's virtual click event for faster clicks across desktop and mobile browsers.
     * @type {string}
     */
    click: 'vclick',
    /**
     * Used for keeping track of open modals
     * @type {Array}
     */
    modals: [],
    /**
     * Used as a key for the flag to keep as a check in the cache that a new subscription has been tracked for GA4
     */
    SUBSCRIPTION_PURCHASE_TRACKING_CACHE_KEY: 'pmw_subscription_purchase',
    /**
     * Used for holding one instance of OpenModalDialog to be called when the closing animation of the modal is complete
     * @type {function}
     */
    queuedOpenModal: null,
    /**
     * Time after which a value in localStorage expires.
     * @type {number} 3 weeks in milliseconds
     * @const
     */
    DEFAULT_CACHE_EXPIRY: 259200000,
    /**
     * Cache expiry timeout for 30 minutes in milliseconds
     */
    CACHE_EXPIRY_HALF_HOUR: 1800000,
    /**
     * Cache expiry timeout for one hour in milliseconds
     */
    CACHE_EXPIRY_ONE_HOUR: 3600000,
    /**
     * Cache expiry timeout for 5 minutes in milliseconds
     */
    CACHE_EXPIRY_FIVE_MINUTES: 300000,
    /**
     * The maximim width breakpoint we consider for a screen to be a "mobile" screen.
     * A mobile phone screen is typically 480px wide or less (derived from Bootstrap's mobile breakpoint).
     * @type {number}
     * @const
     */
    MOBILE_SCREEN_BREAKPOINT: 480,
    /**
     * The maximim width breakpoint we consider for a screen to be a "tablet/ipad" screen.
     * @type {number}
     * @const
     */
    TABLET_SCREEN_BREAKPOINT: 780,
    /**
     * The namespace of the cache we are currently working with
     * @type {string}
     */
    cacheNamespace: '',
    /**
     * An object mapping ajax requests (if any) currently under execution with their calling contexts
     * @type {Object}
     */
    currentAjaxRequest: {},
    /**
     * This variable is used as a cache in case the browser doesn't support localStorage. It is also used if localStorage
     * is full.
     * @type {Object}
     * @private
     */
    fallbackCache_: {},
    /**
     * These are the pages which are shown when a user does not have access to the site.
     */
    accessDeniedPages: ['blocked'],
    /**
     * Anonymous user id. To be used in requests to Storyblocks when a user is not logged in.
     * It is a random alphanumeric string.
     * @type {string}
     * @private
     */
    anonUserId_: '',

    /**
     * Turn this flag on to offer premium users free getty content. This is initialized from freeGettyContentForPremiumUsers config in postermywall
     */
    freeGettyContentForPremiumUsers: null,

    /**
     * Constant for csrf token source if obtained from the csrf cookie
     */
    csrfSourceCookie: 0,
    /**
     * Constant for csrf token source if obtained via an ajax request to the server
     */
    csrfSourceCookieAfterAjaxRequest: 1,
    /**
     * Constant for csrf token source if obtained via an ajax request to the server but the value could not be read from the cookie,
     * so we fall back to the value in the response to the ajax request
     */
    csrfSourceAjaxRequestResponse: 2,
    /**
     * Constants for data types when sending post requests (added for csrf testing)
     */
    formDataType: 0,
    objectDataType: 1,
    stringDataType: 2,
    /**
     * Const for the index.php portion of site urls
     */
    indexPage: 'index.php',
    /**
     * Default language for postermywall
     */
    defaultLanguage: 'en',
    /**
     * List of langauges supported as url subfolders under the structure: default_domain/<lang_code>/<controller>/<method>
     */
    languagesSupportedAsUrlSubfolders: ['pl'],
    /**
     * The value of the following constant controls whether special ajax params need to be added for subdomain to subfolder request
     * to satisfy the CORS policy
     */
    areSubdomainToSubfolderAjaxParamsNeeded: true,
    /**
     * The value of the following constant controls whether the origin of the page needs to be compared with the origin of redirected requests
     */
    shouldSubdomainOriginBeCheckedForRedirects: true,
    /**
     * The uri of the embedded editor layover authentication window
     */
    EMBEDDED_EDITOR_AUTHENTICATION_LAYOVER_URI: 'authenticate/embeddededitorlayover',

    /**
     * error response sent by our server, typically used like this: r.status === 'error'
     */
    AJAX_RESPONSE_STATUS_ERROR: 'error',
    /**
     * success response sent by our server, typically used like this: r.status === 'success'
     */
    AJAX_RESPONSE_STATUS_SUCCESS: 'success',
    /**
     * fail response sent by our server, typically used like this: r.status === 'fail'
     */
    AJAX_RESPONSE_STATUS_FAIL: 'fail',
    /**
     * User preferred language
     */
    USER_PREFERRED_LANGUAGE: 'en',

    i18next_LOADED_EVENT: 'i18next_loaded',
    /**
     * Utility functions namespace.
     * @type {Object}
     */
    util: {
        loadedFrontendBundles: {},
        /**
         * String that contains the browser appropriate animation end event
         */
        animationEndEvents: (function () {
            let t,
                el = document.createElement('fakeelement');

            const animations = {
                'animation': 'animationend',
                'OAnimation': 'oAnimationEnd',
                'MozAnimation': 'animationend',
                'WebkitAnimation': 'webkitAnimationEnd',
            };

            for (t in animations) {
                if (el.style[t] !== undefined) {
                    return animations[t];
                }
            }
        })(),
        /**
         * String that contains the browser appropriate transition end event
         */
        transitionEndEvents: (function () {
            // Function from David Walsh: http://davidwalsh.name/css-animation-callback
            var t,
                el = document.createElement('fakeelement');

            var transitions = {
                'transition': 'transitionend',
                'OTransition': 'oTransitionEnd',
                'MozTransition': 'transitionend',
                'WebkitTransition': 'webkitTransitionEnd',
            };

            for (t in transitions) {
                if (el.style[t] !== undefined) {
                    return transitions[t];
                }
            }
        })(),
        isInBuilder: function () {
            return location.href.indexOf('/posterbuilder/') !== -1;
        },
        isRedirectedFromGoogleOAuth: function () {
            return this.getOAuthRedirectedValue('id_token') !== '';
        },
        getOAuthRedirectedValue: function (key) {
            let queryString = '?' + location.href.substring(location.href.indexOf('#') + 1),
                value = this.getQueryStringValue(key, queryString);
            return key === 'state' ? JSON.parse(value) : value;
        },
        /**
         * Helper for loading JS asynchronously. If the test is true, it loads JS from the URL and executes the callback.
         * @param {boolean} test Usually a typeof X === 'undefined' check
         * @param {string} assetGroupName name of asset group which contains the required file/files
         * @param {boolean} hasCss pass true if you also want to load css file from asset group
         * @param {function} cb The callback function to execute when the JS is loaded or test is false.
         */
        require: function (test, assetGroupName, hasCss, cb) {
            if (test) {
                if (hasCss) {
                    PMW.getJsAndCssAssetGroup(assetGroupName, cb);
                } else {
                    PMW.getJsAssetGroup(assetGroupName, cb);
                }
            } else {
                cb();
            }
        },
        /**
         * @param {string} bundleName
         * @param {Function|undefined} callback
         */
        requireFrontendBundle: function (bundleName, callback = undefined) {
            if (PMW.util.loadedFrontendBundles[bundleName] !== undefined) {
                if (callback !== undefined) {
                    callback();
                }
                return;
            }

            $.when(
                $.ajax({
                    dataType: 'html',
                    cache: true,
                    url: PMW.addVersion(PMW.util.site_url('asset/getFrontendJsEntry?bundleName=' + bundleName), PMW.WEBPACK_VERSION),
                }),
                $.ajax({
                    dataType: 'html',
                    cache: true,
                    url: PMW.addVersion(PMW.util.site_url('asset/getFrontendCssEntry?bundleName=' + bundleName), PMW.WEBPACK_VERSION),
                })
            )
                .then(function (data, css) {
                    const jsTags = PMW.util.getNonTextNodesFromJqueryElements($(data[0]));
                    const existingScriptSrcs = new Set(
                        Array.from(document.querySelectorAll('script[src]')).map((s) => s.src)
                    );

                    const scriptsToLoad = [];
                    jsTags.each(function () {
                        const src = this.src;
                        if (!src) return;
                        if (existingScriptSrcs.has(src)) {
                            return; // skip runtime/vendors already on the page
                        }
                        const type = this.type || '';
                        existingScriptSrcs.add(src);
                        scriptsToLoad.push({src, type});
                    });

                    if (!scriptsToLoad.length) {
                        PMW.util.loadedFrontendBundles[bundleName] = 1;

                        if (callback !== undefined) {
                            callback();
                        }
                        return;
                    }

                    let numLoadedTags = 0;
                    const numTags = scriptsToLoad.length;

                    scriptsToLoad.forEach(function ({src, type}) {
                        const scriptTag = document.createElement('script');
                        scriptTag.src = src;
                        scriptTag.type = type;
                        scriptTag.onload = function () {
                            numLoadedTags += 1;
                            if (numLoadedTags === numTags && callback !== undefined) {
                                callback();
                            }
                        };
                        document.head.appendChild(scriptTag);
                    });

                    const cssLinkTags = PMW.util.getNonTextNodesFromJqueryElements($(css[0]));
                    const styleEl = document.createElement('style');
                    cssLinkTags.each(function () {
                        styleEl.appendChild(this);
                    });
                    document.head.appendChild(styleEl);

                    PMW.util.loadedFrontendBundles[bundleName] = 1;
                })
                .fail(function () {
                    alert(window.i18next.t('pmwjs_problem_check_internet_connection_reload'));
                });
        },
        /**
         * @param {jQuery} jQueryElements
         */
        getNonTextNodesFromJqueryElements: function (jQueryElements) {
            const JQUERY_TEXT_NODE = 3;
            let validNodes = [];
            jQueryElements
                .each(function () {
                    if (this.nodeType !== JQUERY_TEXT_NODE) { // Node.TEXT_NODE
                        validNodes.push(this);
                    }
                });
            return $(validNodes);
        },
        /**
         * Converts a url into embedded editor or gallery dialog url depending on where it is
         * @param {String} u part of the URI
         * @returns {*}
         */
        embedded_site_url: function (u) {
            let embeddedEditorUrlSegment = PMW.util.getEmbeddedURLSegment(),
                galleryDialogUrlSegment = PMW.util.getGalleryDialogUrlSegment(),
                urlSegmentToAppend = '';

            if (typeof embeddedEditorUrlSegment !== 'undefined' && embeddedEditorUrlSegment.length > 0) {
                urlSegmentToAppend = embeddedEditorUrlSegment;
            } else if (typeof galleryDialogUrlSegment !== 'undefined' && galleryDialogUrlSegment.length > 0) {
                urlSegmentToAppend = galleryDialogUrlSegment;
            }

            return this.site_url(u + urlSegmentToAppend);
        },
        /**
         * Identical functionality to the CodeIgniter function of the same name.
         *
         * !! NOTE !! If any change is made to this function, make sure to replicate it for the equivalent function on the php side as needed.
         *
         * @param {String} u part of the URI
         * @returns String the URL
         */
        site_url: function (u = '') {
            let lang = PMW.util.getLanguage();

            if (this.isActiveLanguageSupportedAsUrlSubfolder() && !PMW.SERVER_ROOT.includes(lang)) {
                return PMW.SERVER_ROOT + lang + '/' + u;
            }

            return PMW.SERVER_ROOT + u;
        },
        /**
         * Build the asset url path for the given asset file
         *
         * !! NOTE !! If any change is made to this function, make sure to replicate it for the equivalent function on the php side as needed.
         *
         * @param {String} u
         */
        asset_url: function (u = '') {
            return PMW.BASE_URL + 'assets' + '/' + u;
        },
        /**
         * Identical functionality to the CodeIgniter function of the same name.
         *
         * !! NOTE !! If any change is made to this function, make sure to replicate it for the equivalent function on the php side as needed.
         *
         * @param {String} u part of the URI
         * @returns String the URL
         */
        gradient_url: function (u = '') {
            const gradient_base_url = 'https://www.postermywall.com/blog/';

            return gradient_base_url + '/' + u;
        },
        /**
         * Returns the url for the homepage. For languages supported as subfolders in urls, the returned path will look like
         * https://www.postermywall.com/index.php/pl/
         *
         * !! NOTE !! If any change is made to this function, make sure to replicate it for the equivalent function on the php side as needed.
         *
         * @returns {string}
         */
        homepage_url: function () {
            let baseUrl = PMW.BASE_URL,
                lang = PMW.util.getLanguage();

            if (PMW.util.isActiveLanguageSupportedAsUrlSubfolder()) {
                return baseUrl + lang + '/';
            }
            return baseUrl;
        },
        /**
         * Checks if the active language is allowed as a subfolder in urls
         */
        isActiveLanguageSupportedAsUrlSubfolder() {
            return PMW.languagesSupportedAsUrlSubfolders.includes(PMW.util.getLanguage());
        },
        /**
         * Checks if a given language is allowed as a subfolder in urls
         * @param {String} lang
         */
        isLanguageSupportedAsUrlSubfolder(lang) {
            return PMW.languagesSupportedAsUrlSubfolders.includes(lang);
        },
        isDefaultLanguage() {
            return PMW.util.getLanguage() === PMW.defaultLanguage;
        },
        /**
         *
         * @param {String} targetLang
         * @param {String} currentDomain
         * @param {String} targetDomain
         */
        changeLanguage(targetLang, currentDomain, targetDomain) {
            const currentLang = PMW.util.getLanguage();
            let redirectUrl = window.location.href;

            if (PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && !PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                redirectUrl = redirectUrl.replace('/' + currentLang + '/', '/' + PMW.indexPage + '/');
                window.location.href = redirectUrl.replace(window.location.hostname, targetDomain);
                return;
            }

            if (!PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                let segments = window.location.href.split(PMW.indexPage);
                redirectUrl = segments[0] + targetLang + segments[1];
                window.location.href = redirectUrl.replace(currentDomain, this.removeLanguageFromDomain(targetDomain, targetLang));
                return;
            }

            if (!PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && !PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                window.location.href = redirectUrl.replace(currentDomain, targetDomain);
                return;
            }

            window.location.href = redirectUrl.replace('/' + currentLang + '/', '/' + targetLang + '/');
        },
        /**
         * @param {String} domain
         * @param {String} lang
         */
        removeLanguageFromDomain(domain, lang) {
            let stringToRemove = lang;
            if (!domain.includes('beta')) {
                stringToRemove += '.';
            }
            return domain.replace(stringToRemove, '');
        },
        /**
         * @param {String} url
         * @param {String} targetLang
         */
        updateLanguageDomainForUrl(url, targetLang) {
            const currentLang = PMW.util.getLanguage();
            const currentDomain = window.location.hostname;

            if (currentLang === targetLang) {
                return url;
            }

            if (PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && !PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                return this.updateSubfolderUrlToSubdomainUrl(url, currentLang, targetLang, currentDomain);
            }

            if (!PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                return this.updateSubdomainUrlToSubfolderUrl(url, currentLang, targetLang, currentDomain);
            }

            if (!PMW.util.isActiveLanguageSupportedAsUrlSubfolder() && !PMW.util.isLanguageSupportedAsUrlSubfolder(targetLang)) {
                return this.updateSubdomainInSubdomainUrl(url, currentLang, targetLang, currentDomain);
            }

            return this.updateSubfolderInSubfolderUrl(url, currentLang, targetLang);
        },
        /**
         *
         * @param {String} url
         * @param {String} currentLang
         * @param {String} targetLang
         * @param {String} currentDomain
         */
        updateSubfolderUrlToSubdomainUrl(url, currentLang, targetLang, currentDomain) {
            url = url.replace('/' + currentLang + '/', '/' + PMW.indexPage + '/');

            if (targetLang === PMW.defaultLanguage) {
                return url;
            }

            if (PMW.isLocal()) {
                url = url.replace(currentDomain, targetLang + '.' + currentDomain);
            } else if (currentDomain.includes('beta.')) {
                url = url.replace(currentDomain, targetLang + currentDomain);
            } else {
                url = url.replace('www.', targetLang + '.');
            }

            return url;
        },
        /**
         * @param {String} url
         * @param {String} currentLang
         * @param {String} targetLang
         * @param {String} currentDomain
         */
        updateSubdomainUrlToSubfolderUrl(url, currentLang, targetLang, currentDomain) {
            let segments = url.split(PMW.indexPage);
            url = segments[0] + targetLang + segments[1];

            if (currentLang === PMW.defaultLanguage) {
                return url;
            }

            if (PMW.isLocal()) {
                url = url.replace(currentLang + '.', '');
            } else if (currentDomain.includes('beta.')) {
                url = url.replace(currentLang + 'beta.', 'beta.');
            } else {
                url = url.replace(currentLang + '.', 'www.');
            }

            return url;
        },
        /**
         * @param {String} url
         * @param {String} currentLang
         * @param {String} targetLang
         * @param {String} currentDomain
         */
        updateSubdomainInSubdomainUrl(url, currentLang, targetLang, currentDomain) {
            if (currentLang === targetLang) {
                return url;
            }

            if (PMW.isLocal()) {
                if (currentLang === PMW.defaultLanguage) {
                    return url.replace(currentDomain, targetLang + '.' + currentDomain);
                }

                if (targetLang === PMW.defaultLanguage) {
                    return url.replace(currentLang + '.', '');
                }

                return url.replace(currentLang + '.', targetLang + '.');
            }

            if (currentDomain.includes('beta.')) {
                if (currentLang === PMW.defaultLanguage) {
                    return url.replace(currentDomain, targetLang + currentDomain);
                }

                if (targetLang === PMW.defaultLanguage) {
                    return url.replace(currentLang + 'beta.', 'beta.');
                }

                return url.replace(currentLang + 'beta.', targetLang + 'beta.');
            }

            if (currentLang === PMW.defaultLanguage) {
                return url.replace('www.', targetLang + '.');
            }
            if (targetLang === PMW.defaultLanguage) {
                return url.replace(currentLang + '.', 'www.');
            }
            return url.replace(currentLang + '.', targetLang + '.');
        },
        /**
         *
         * @param {String} url
         * @param {String} currentLang
         * @param {String} targetLang
         */
        updateSubfolderInSubfolderUrl(url, currentLang, targetLang) {
            return url.replace('/' + currentLang + '/', '/' + targetLang + '/');
        },
        /**
         * @param {String} url
         */
        ignoreWindowHistoryUpdateForSubdomain(url) {
            if (!PMW.shouldSubdomainOriginBeCheckedForRedirects) {
                return false;
            }

            let validDomains = ['dabeta.postermywall.com', 'da.localpostermywall.com', 'da.postermywall.com'],
                subfolderUris = ['postermywall.com/da/', 'localpostermywall.com/pmw/da/', 'beta.postermywall.com/da/'];

            return validDomains.includes(window.location.hostname) && subfolderUris.some((subfolderUri => url.includes(subfolderUri)));
        },
        /**
         * Returns the redirect url from a url containing the "redirect=" url parameter
         * @param {string} locationHref
         * @returns {string}
         */
        getRedirectURLFromHref(locationHref) {
            if (!locationHref.includes('?redirect=')) {
                return '';
            }

            const urlObj = new URL(locationHref),
                searchParams = urlObj.searchParams;

            let redirectUrl = searchParams.get('redirect');
            searchParams.delete('redirect');
            return redirectUrl + '?' + searchParams.toString();
        },
        /**
         * Creates a URL to a resource in the S3 repository.
         * @param {String} p Path to the resource on S3
         * @param {String} b The bucket to use in the URL
         * @returns {String}
         */
        repoURL: function (p, b) {
            var url = 'https://' + b + '/' + p;
            url += (url.indexOf('?') === -1 ? '?' : '&');
            return url;
        },
        /**
         * Returns the lang code set.
         * @returns {string}
         */
        getLanguage: function () {
            return document.documentElement.getAttribute('lang') || 'en';
        },
        getCurrentPagePath: function () {
            return PMW.util.getPagePath(new URL(window.location.href));
        },
        /**
         * @param {string} url
         * @return {string}
         */
        getPagePathAndSearchParamsFromUrl: function (url) {
            const pagePath = PMW.util.getPagePath(new URL(url)),
                queryParams = (new URL(url)).searchParams.toString();

            if (queryParams !== '') {
                return `${pagePath}?${queryParams}`;
            }
            return pagePath;
        },
        /**
         * Retrieves the part of the url after the language code for urls where the language code is a valid subfolder,
         * otherwise retrieves the part of the url after 'index.php'
         *
         * @param {URL} url
         * @return {*}
         */
        getPagePath: function (url) {
            let separator = this.getPagePathSeparatorToken();

            // This case is necessary to ensure that the separator is set correctly for any puppeteer related applications
            // or any use case that involves PMConfig in case there is a mismatch between the language set in PMConfig and the currently active
            // language. In such cases, the lang property in PMConfig is given priority

            if (this.isLangMismatchWithPMConfig()) {
                separator = PMConfig.lang;
            }

            const regexExp = new RegExp(separator + '(.*)');
            return url.pathname.match(regexExp)[1];
        },
        /**
         * @param {string} url
         * @return {string}
         */
        getControllerFromUrl: function (url) {
            try {
                const pagePath = PMW.util.getPagePath(new URL(url)),
                    pagePathUris = pagePath.split('/');
                return pagePathUris[1];
            } catch (e) {
                return '';
            }
        },
        /**
         * @param {string} url
         * @return {string}
         */
        getMethodFromUrl: function (url) {
            try {
                const pagePath = PMW.util.getPagePath(new URL(url)),
                    pagePathUris = pagePath.split('/');
                return pagePathUris[2];
            } catch (e) {
                return '';
            }
        },
        /**
         * @returns {boolean}
         */
        isLangMismatchWithPMConfig: function () {
            return typeof PMConfig !== 'undefined' && PMConfig !== null &&
                typeof PMConfig.lang !== 'undefined' && PMConfig.lang &&
                PMW.util.isLanguageSupportedAsUrlSubfolder(PMConfig.lang) && PMConfig.lang !== PMW.util.getLanguage();
        },
        /**
         * Returns the token in the url that separates the domain from the page path, which can be either
         * 1- The language code for urls where language codes are subfolders
         * 2- 'index.php' otherwise
         *
         * @returns {String}
         */
        getPagePathSeparatorToken: function () {
            if (PMW.util.isActiveLanguageSupportedAsUrlSubfolder()) {
                return PMW.util.getLanguage();
            }
            return PMW.indexPage;
        },
        /**
         * Creates a URL to a resource in the S3 repository.
         * @param {String} p Path to the resource on S3
         * @param {String} b The bucket to use in the URL
         * @returns {String}
         */
        repoURL: function (p, b) {
            var url = 'https://' + b + '/' + p;
            url += (url.indexOf('?') === -1 ? '?' : '&');
            return url;
        },
        /**
         * Returns the lang code set.
         * @returns {string}
         */
        getLanguage: function () {
            return document.documentElement.getAttribute('lang') || PMW.defaultLanguage;
        },
        /**
         * Adds query string param to the passed url.
         * @param {string} url
         * @param {string} key
         * @param {string} value
         * @returns {string}
         */
        addQueryStringParam: function (url, key, value) {
            return url + ((url.indexOf('?') != -1) ? '&' : '?') + encodeURIComponent(key) + '=' + encodeURIComponent(value);
        },
        /**
         * Returns a specific query string param. Returns an empty string if the key did not exist.
         * @param {string} key
         * @param {string} [query_string]
         * @returns {string}
         */
        getQueryStringValue: function (key, query_string) {
            if (query_string === undefined || query_string === false) {
                query_string = window.location.search;
            }
            return decodeURIComponent(query_string.replace(new RegExp('^(?:.*[&\\?]' + encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&') + '(?:\\=([^&]*))?)?.*$', 'i'), '$1'));
        },
        /**
         * Returns an object with all of the query string parameters.
         * @returns {object}
         */
        getQueryStringValues: function () {
            var vars = {}, hash,
                hashIndex = window.location.href.indexOf('#'),
                hashes;

            // If there is a hash value, remove it
            if (hashIndex > 0) {
                hashes = window.location.href.slice(window.location.href.indexOf('?') + 1, hashIndex).split('&');
            } else {
                hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');
            }
            for (var i = 0; i < hashes.length; i++) {
                hash = hashes[i].split('=');
                if (hash.length == 2) {
                    vars[hash[0]] = hash[1];
                }
            }
            return vars;
        },
        /**
         * Returns if the provided url contains the embedded editor authentication layover uri
         */
        doesUrlContainEmbeddedEditorAuthenticationLayoverUri: function (url) {
            return url.includes(PMW.EMBEDDED_EDITOR_AUTHENTICATION_LAYOVER_URI);
        },
        /**
         * Returns a number that can be used for benchmarking using two instances of this function.
         * @returns {number}
         */
        getTimeForBenchmark: function () {
            if (typeof Date.now === 'function') {
                return Date.now();
            }

            return +new Date();
        },

        /**
         * Converts the local file path to an accesible URL
         * @param {string} filePath
         * @return {string}
         */
        getLocalURLFromFilePath: function (filePath) {
            const serverFolderName = 'server/';
            return filePath.substring(filePath.indexOf(serverFolderName) + serverFolderName.length);
        },
        /**
         * Checks if the given string is a well formed email address.
         * @param {String} v The email
         * @returns Boolean
         */
        isValidEmail: function (v) {
            return /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i.test(v);
        },

        /**
         * Checks if the string is PMW graphic item clipboard data.
         * @param {String} str
         */
        isPMWGraphicItemJSON: function (str) {
            let parsedObject;

            try {
                parsedObject = JSON.parse(str);
            } catch (e) {
                return false;
            }

            return Object.keys(parsedObject).includes('graphicItems');
        },

        /**
         * Creates a new cookie.
         * @param {String} name The key of the cookie
         * @param {String} value The value to store in the cookie
         * @param {Number} days Cookie expires after these many days
         * @param {boolean} [isStrict] If this is set as true, the cookie is made with "SameSite=strict"
         */
        setCookie: function (name, value, days, isStrict) {
            if (typeof isStrict === 'undefined') {
                isStrict = false;
            }

            var expires, date = new Date(), cookieStr;
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toGMTString();
            cookieStr = name + '=' + value + expires + '; path=/' + ';domain=' + PMW.DOMAIN;


            if (PMW.isChrome() && parseInt(PMW.getBrowserVersion(), 10) < 79) {
                cookieStr += ';Secure;';
            } else {
                /**
                 * IMPORTANT: This is a custom change from PosterMyWall, where we're adding the SameSite, Partitioned, and Secure attribute to cookies since they will NOT be served if they
                 * don't contain the two attributes in later versions of Chrome.
                 */
                if (isStrict) {
                    cookieStr += ' ;SameSite=Strict;Secure;';
                } else {
                    if ((PMW.isChrome() || PMW.isEdge()) && parseInt(PMW.getBrowserVersion(), 10) < 104) {
                        cookieStr += ' ;SameSite=None;Secure;';
                    } else {
                        cookieStr += ' ;SameSite=None;Secure;Partitioned;';
                    }
                }
            }

            document.cookie = cookieStr;
        },
        /**
         * Retrieves the value of a cookie.
         * @param {String} name Name of cookie
         * @returns Mixed Value of cookie or null
         */
        readCookie: function (name) {
            let nameEQ = name + '=',
                ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ')
                    c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0)
                    return c.substring(nameEQ.length, c.length);
            }
            return null;
        },
        /**
         * Deletes the value of the cookie.
         * @param {string} name Name of the cookie to delete.
         */
        deleteCookie: function (name) {
            let additionalAttributes = ';SameSite=None;Secure;Partitioned;';
            if ((PMW.isChrome() || PMW.isEdge()) && PMW.getBrowserVersion() < 104) {
                additionalAttributes = '';
            }
            document.cookie = encodeURIComponent(name) + '=deleted; expires=' + new Date(0).toUTCString() + ';path=/ ;domain=' + PMW.DOMAIN + additionalAttributes;
        },
        setFreeDownloadCookie: function (value, ttl) {
            PMW.util.setCookie(PMW.COOKIE_FREE_DOWNLOAD, value, ttl);
        },
        /**
         * Performs a simple async POST request to the server. You can send URL encoded POST params formatted as a string.
         * @param {string} uri The controller/method URI to hit
         * @param {string} params A query string
         */
        doPost: function (uri, params) {
            PMW.getCsrfToken(function (token_value) {
                var token_name = 'csrf_token';
                var csrfMetaTag = document.querySelector('meta[name="data-csrf-name"]');
                if (csrfMetaTag) {
                    token_name = csrfMetaTag.content;
                }
                if (params !== '') {
                    params += '&';
                }
                params += (token_name + '=' + encodeURIComponent(token_value));
                var xmlHTTP = new XMLHttpRequest();
                xmlHTTP.open('POST', PMW.util.site_url(uri), true);
                xmlHTTP.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
                xmlHTTP.send(params);
            });
        },
        /**
         * Replaces unsafe HTML characters in a string
         * @param {string} unsafeStr
         * @returns {XML|string}
         */
        escapeHTML: function (unsafeStr) {
            if (unsafeStr === null || unsafeStr === '') {
                return unsafeStr;
            }

            return unsafeStr
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\"/g, '&quot;')
                .replace(/\'/g, '&#39;')
                .replace(/\//g, '&#x2F;');
        },

        /**
         * Checks if the given string is a valid URL or not.
         * @param {string} str
         * @returns {boolean}
         */
        isValidURL: function (str) {
            var regexp = new RegExp('^(http|https)://' + // protocol
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+\\#]*)*' + // port and path
                '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

            return regexp.test(str.toLowerCase());
        },

        /**
         * Performs a simple check whether the given string is a valid URL or not.
         * @param {string} url
         * @returns {boolean}
         */
        isValidURLSimple: function (url) {
            const simpleRegex = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/{2}=]*)/;
            return simpleRegex.test(url);
        },
        /**
         * Performs a simple check whether the given string is a valid phone number or not.
         * @param {string} phoneNumber
         * @returns {boolean}
         */
        isValidPhoneSimple: function (phoneNumber) {
            const simpleRegex = /^(tel:|\+|\d)+[\d\s()x-]+$/;
            return simpleRegex.test(phoneNumber);
        },

        /**
         * Helper function to return the matching dom element using native javascript
         * @param elem
         * @param {string} className
         * @returns {*}
         */
        getClosestByClass: function (elem, className) {
            if (elem.closest) {
                return elem.closest('.' + className);
            }

            for (; elem && elem !== document; elem = elem.parentNode) {
                if (elem.classList.contains(className))
                    return elem;
            }
            return null;
        },
        /**
         * Creates a random id which can be used as an anonoymous user id or poster id.
         * It is created by generating a ranom number, converting it to base 36 and removing the prefix "0.".
         * https://stackoverflow.com/a/12502559/10155534
         */
        createRandomId: function () {
            return Math.random().toString(36).slice(2);
        },
        /**
         * Helper function to copy properties from source to target object
         * @param {object} source
         * @param {object} target
         * @param {Array<string>} properties
         */
        copyProperties: function (source, target, properties) {
            properties.forEach(ppt => {
                if (source.hasOwnProperty(ppt)) {
                    target[ppt] = source[ppt];
                }
            });
        },
        /**
         * Makes a deep copy of array or object including all iterable properties of type "object"
         * @param {object|array} toCopy
         */
        copyObject: function (toCopy) {
            const copy = toCopy instanceof Array ? [] : {};
            for (let key in toCopy) {
                if (!toCopy.hasOwnProperty(key)) {
                    continue;
                }
                copy[key] = typeof toCopy[key] === 'object' ? this.copyObject(toCopy[key]) : toCopy[key];
            }
            return copy;
        },

        /**
         * Test emptiness in O(1)
         * @param {*} toTest
         * @return {boolean}
         */
        isEmpty: function (toTest) {
            let isEmpty = true;
            for (let key in toTest) {
                isEmpty = false;
                break;
            }
            return isEmpty;
        },

        /**
         * Substitute all uses of replaceThis with replaceWith, inside targetString.
         * Replacement for String.replaceAll, since old browser versions don't support it
         * @param {string} replaceThis
         * @param {string} replaceWith
         * @param {string} targetString
         * @return {string}
         */
        replaceAll: function (replaceThis, replaceWith, targetString) {
            return targetString.split(replaceThis).join(replaceWith);
        },

        /**
         *
         * @param {string|null} redirectToAfterLogin a URL to redirect to after a successful login
         */
        redirectToLoginPage: function (redirectToAfterLogin = null) {
            const loginURL = PMW.util.site_url('authenticate/showlogin');
            window.location.href = redirectToAfterLogin ? PMW.util.addQueryStringParam(loginURL, 'redirect', redirectToAfterLogin) : loginURL;
        },

        /**
         * @param {Object} [opts]
         * @returns {Intl.NumberFormat}
         */
        getNumberFormatter: function (opts) {
            if (opts === undefined) {
                opts = {};
            }
            return Intl.NumberFormat(navigator.language, opts);
        },

        /**
         * @param {Number} number
         * @returns {string}
         */
        numberFormat: function (number) {
            return this.getNumberFormatter().format(number);
        },

        /**
         * @param {Number} number
         * @param {Number} [maxDigits]
         * @returns {string}
         */
        percentageFormat: function (number, maxDigits) {
            if (maxDigits === undefined) {
                maxDigits = 0;
            }
            return this.getNumberFormatter({style: 'percent', maximumFractionDigits: maxDigits}).format(number);
        },

        /**
         * @param {string} currencyCode
         * @returns {Intl.NumberFormat}
         */
        getCurrencyFormatter: function (currencyCode) {
            return Intl.NumberFormat(navigator.language, {
                style: NUMBER_FORMAT_TYPE_CURRENCY,
                currency: currencyCode,
                maximumFractionDigits: PMW.util.getMaximumFractionDigitsForCurrencyCode(currencyCode),
            });
        },

        /**
         * @param {string} currencyCode
         * @returns {number}
         */
        getMaximumFractionDigitsForCurrencyCode: function (currencyCode) {
            if (PMW.currencyShowsDecimals.includes(currencyCode)) {
                return CURRENCY_FORMAT_MAX_FRACTION_DIGITS_WITH_DECIMALS;
            }

            return CURRENCY_FORMAT_MAX_FRACTION_DIGITS_NO_DECIMALS;
        },

        /**
         * @param {number} amount
         * @param {string} currencyCode
         * @returns {string}
         */
        currencyFormat: function (amount, currencyCode) {
            return this.getCurrencyFormatter(currencyCode).format(amount);
        },

        /**
         * @param {number} amount
         * @param {string} currencyCode
         * @returns {string}
         */
        marketingCurrencyFormat: function (amount, currencyCode) {
            const formatter = this.getCurrencyFormatter(currencyCode);
            const parts = formatter.formatToParts(amount);

            let isCurrencyAtStart = false;
            let formattedAmount = '';
            let formattedCurrency = '';

            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                if (part.type === NUMBER_FORMAT_PART_TYPE_CURRENCY) {
                    if (i === 0) {
                        isCurrencyAtStart = true;
                    }
                    formattedCurrency = part.value;
                } else {
                    formattedAmount += part.value;
                }
            }

            if (isCurrencyAtStart) {
                return '<span class="body-xl-bold-deprecated">' + formattedCurrency + '</span><span class="heading-h2">' + formattedAmount + '</span>';
            }

            return '<span class="heading-h2">' + formattedAmount + '</span><span class="body-xl-bold-deprecated">' + formattedCurrency + '</span>';
        },
    },
    /**
     * Any google integration related functions that are needed system-wide
     * should go in this object.
     *
     * @type Object
     */
    google: {
        /**
         * The URL to google's JS api used for API calls.
         * @param {string}
         */
        FEATURES_API_URL: 'https://apis.google.com/js/api.js',
        /**
         * The URL to google's accounts API
         * @param {string}
         */
        ACCOUNTS_API_URL: 'https://accounts.google.com/gsi/client',
        /**
         * The default width of google button
         */
        DEFAULT_GOOGLE_BUTTON_WIDTH: 255,
        /**
         * Params for the renderButton API call to Google
         */
        GOOGLE_RENDER_BUTTON_PARAMS: {
            type: 'standard',
            shape: 'rectangle',
            theme: 'filled_blue',
            text: 'continue_with',
            size: 'large',
            logo_alignment: 'left',
            width: 255,
        },
        /**
         * Only loads the accounts API script
         * @param {function} cb callback function
         */
        loadAccountsAPI: function (cb) {
            $.getScript(PMW.google.ACCOUNTS_API_URL, cb);
        },
        /**
         * Loads the google API script for different features (in our case, the Google picker)
         * @param {function} cb callback function
         */
        loadFeaturesAPI: function (cb) {
            $.getScript(PMW.google.FEATURES_API_URL, cb);
        },
        /**
         * Loads the client with the scope to upload files. The OAUTH functionality will be attached to the button
         * passed.
         *
         * @param {function} cb callback function
         * @param {jQuery} btn The selector for the button on which we'll apply the sign in functionality.
         */
        initUploadPermissions: function (cb, btn) {
            PMW.google.loadAccountsAPI(function () {
                try {
                    const client = google.accounts.oauth2.initTokenClient({
                        client_id: PMW.GOOGLE_APP_ID,
                        scope: 'https://www.googleapis.com/auth/drive.file',
                        callback: (tokenResponse) => {
                            if (tokenResponse && tokenResponse.access_token) {
                                cb(tokenResponse);
                            } else {
                                PMW.google.onError();
                            }
                        },
                    });

                    PMW.onClick(btn, null, function () {
                        client.requestAccessToken();
                        btn.off(PMW.click);
                    });
                } catch (e) {
                    PMW.google.onError();
                }
            });
        },
        onError: function (r) {
            let message = i18next.t('pmwjs_error_google_signin');
            if (typeof r !== 'undefined' && typeof r.message !== 'undefined' && r.message !== '') {
                message = r.message;
            }
            PMW.openErrorModal(message)
        },
    },
    /**
     * Any Facebook integration related functions that are needed system-wide
     * should go in this object.
     *
     * @type Object
     */
    fb: {
        /**
         * Loads fb JS sdk script and initializes it
         * @returns {Promise<null|ErrorEvent>}
         */
        init: function () {
            window.fbAsyncInit = function () {
                FB.init({
                    appId: PMW.FB_APP_ID,
                    cookie: true, // enable cookies to allow the server to access the session
                    version: PMW.FB_GRAPH_VERSION,
                });
            };

            return new Promise((resolve, reject) => {
                (function (d, s, id) {
                    let js, fjs = d.getElementsByTagName(s)[0];
                    if (d.getElementById(id)) {
                        resolve();
                        return;
                    }

                    js = d.createElement(s);
                    js.id = id;
                    js.src = 'https://connect.facebook.net/' + PMW.FB_LOCALE_CODE + '/sdk.js';
                    fjs.parentNode.insertBefore(js, fjs);

                    js.onload = function () {
                        resolve();
                    };

                    js.addEventListener('error', function (e) {
                        reject(e);
                    });

                }(document, 'script', 'facebook-jssdk'));
            });
        },

        /**
         * Loads FB Sdk, enables fb login button and attaches the passed click handler to fb login button
         * @param {jQuery} fbButton
         * @param {function} [onClickHandler]
         */
        initLoginButton: function (fbButton, onClickHandler) {
            PMW.fb.init().then(function () {
                fbButton.removeClass('disabled');
                PMW.onClick(fbButton, null, onClickHandler);
            }).catch(() => alert(i18next.t('pmwjs_error_logging_in_via_fb')));
        },

        /**
         * Wrapper for the FB.login() function.
         *
         * @param {Function} cb
         * @param {Object} [perms]
         */
        login: function (cb, perms) {
            if (PMW.isStudent()) {
                alert(i18next.t('pmwjs_fb_features_not_for_students'));
                return;
            }

            if (perms === undefined) {
                perms = null;
            }
            PMW.fb.init().then(function () {
                FB.login(cb, perms);
            }).catch(() => alert(i18next.t('pmwjs_error_logging_in_via_fb')));
        },
    },
    /**
     * @param {Object} r
     * @param {Function} cb
     * @param {string} redirectUrl
     * @param {Function} onAuthSuccess
     * @param {string} ssoType
     */
    ssoAccountAuthenticationCallback: function (r, cb, redirectUrl, onAuthSuccess, ssoType) {
        let redirectAfterSuccess = redirectUrl;
        if (redirectUrl === '' || !redirectUrl) {
            redirectAfterSuccess = PMW.util.getRedirectURLFromHref(location.href);
        }

        if (redirectAfterSuccess) {
            try {
                const hostname = new URL(redirectAfterSuccess).hostname;
                if (hostname && !(hostname === 'postermywall.com' || hostname.endsWith('.postermywall.com'))) {
                    redirectAfterSuccess = PMW.util.site_url();
                }
            } catch (e) {
                redirectAfterSuccess = PMW.util.site_url();
            }
        }

        const redirectVO = new SSORedirectHelper(onAuthSuccess, redirectAfterSuccess);
        if (r.status === PMW.AJAX_RESPONSE_STATUS_SUCCESS) {
            PMW.handleSSOAccountSuccessResponse(r,
                function () {
                    PMW.gtm.trackSignupEvents(ssoType);
                    if (r.data.pictureURL) {
                        PMW.setLoading();
                        PMW.write(PMW.util.site_url('user/updateProfilePictureFromGoogle'), {
                            imageURL: r.data.pictureURL,
                        }, PMW.openEmailOptInModal.bind(this, r.data.action, redirectVO, r.data.handledEmailMarketingPreference));
                    } else {
                        PMW.openEmailOptInModal(r.data.action, redirectVO, r.data.handledEmailMarketingPreference);
                    }
                },
                function () {
                    PMW.onConnectSuccess(r.data.action, redirectVO, r.data.currentDomain, r.data.newDomain);
                },
            );
        } else if (r.status === PMW.AJAX_RESPONSE_STATUS_FAIL) {
            PMW.handleSSOAccountFailResponse(r, ssoType, cb.bind(null, r.data.identityToken));
        } else {
            let title;
            let message = i18next.t('pmwjs_user_login_failed');
            if (r.redirectMessage && r.redirectMessage !== '') {
                message = r.redirectMessage;
            }
            if (r.message && r.message !== '') {
                message = r.message;
            }
            if (r.reason === 'access_denied') {
                message = i18next.t('pmwjs_user_login_insufficient_permissions', {
                    account: ssoType
                });
                title = i18next.t('pmwjs_insufficient_permissions');
            }
            PMW.openErrorModal({
                title: title,
                message: message,
                log: false
            })
        }
    },
    isPWA: function () {
        const mqStandAlone = '(display-mode: standalone)';
        return !!(navigator.standalone || window.matchMedia(mqStandAlone).matches);
    },
    /**
     * @returns {boolean}
     */
    isLocal: function () {
        return window.location.href.indexOf('/pmw/') > -1 || window.location.href.indexOf('storybook.localpostermywall') > -1;
    },
    /**
     * @param {Object} r
     * @param {function} newAccountHandler
     * @param {function} connectHandler
     */
    handleSSOAccountSuccessResponse: function (r, newAccountHandler, connectHandler) {
        function onLoginVerificationCallback(response) {
            if (PMW.util.doesUrlContainEmbeddedEditorAuthenticationLayoverUri(PMW.util.getRedirectURLFromHref(window.location.href))) {
                window.close();
                return;
            }

            if (response.data.isnew === 1) {
                newAccountHandler();
            } else if (response.data.redirect !== 'undefined' && response.data.redirect !== '') {
                if (response.data.errorMsg !== 'undefined' && response.data.errorMsg) {
                    PMW.setLoading(null);
                    alert(r.data.errorMsg);
                } else {
                    window.location.href = r.data.redirect;
                }
            } else {
                connectHandler();
            }
        }

        if ('pendingMFA' in r.data && r.data.pendingMFA) {
            PMW.openTwoFactorAuthenticationVerificationModal({
                onVerificationSuccessCallback: onLoginVerificationCallback.bind(this, r)
            });
        } else {
            onLoginVerificationCallback(r);
        }
    },
    /**
     * @param {Object} r
     * @param {string} ssoLoginType
     * @param {Function} onConnectCallback
     */
    handleSSOAccountFailResponse: function (r, ssoLoginType, onConnectCallback) {
        if (r.message && r.message !== 'undefined' && r.message !== '') {
            PMW.openErrorModal({
                message: r.message,
                log: false
            })
            return;
        }

        if (r.data.showEmailRequiredDialog !== 'undefined' && r.data.showEmailRequiredDialog === true) {
            let panelProps;
            switch (r.reason) {
                case 'invalid-email':
                    panelProps = {
                        isEmailRequired: true,
                        onConnectCallback: onConnectCallback,
                        onPasswordRequiredCallback: PMW.onPromptPasswordForEmail.bind(null, onConnectCallback, ssoLoginType)
                    };
                    break;
                case 'non-sso-account-exists':
                    panelProps = {
                        email: r.data.email,
                        onConnectCallback: onConnectCallback,
                        errorMessage: i18next.t('pmwjs_account_with_email_exists_enter_password_to_connect', {ssoName: ssoLoginType}),
                        onPasswordRequiredCallback: PMW.onPromptPasswordForEmail.bind(null, onConnectCallback, ssoLoginType)
                    };
                    break;
                default:
                    panelProps = {
                        isEmailRequired: true,
                        onConnectCallback: onConnectCallback,
                        showSubscribeToEmailsCheckbox: true,
                        onPasswordRequiredCallback: PMW.onPromptPasswordForEmail.bind(null, onConnectCallback, ssoLoginType)
                    };
                    break;
            }
            PMW.openConnectSSOAccountModal(panelProps);
        }
    },

    onPromptPasswordForEmail: function (onConnectCallback, ssoLoginType, email) {
        let panelProps = {
            email: email,
            onConnectCallback: onConnectCallback,
            panelId: 'prompt-password-panel',
            errorMessage: i18next.t('pmwjs_account_with_email_exists_enter_password_to_connect', {ssoName: ssoLoginType}),
        };

        PMW.openConnectSSOAccountModal(panelProps);
    },

    initGA4TrackingForNavBarSearch: function () {
        addGATrackingForMobileFloatingNav();
        addGATrackingForTabletNav();
        addGATrackingForDeskopNav();
    },

    /**
     * Namespace for Google Tag Manager.
     */
    gtm: {
        // Event types for specific events that are to be tracked for online ads
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_POSTER_SAVE: 'poster_save',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_FREE_DOWNLOAD: 'free_download',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_DOWNLOAD_PURCHASE: 'download_purchase',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_SUBSCRIPTION_PURCHASE: 'subscription_purchase',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_TRIAL_CONVERTED: 'trial_converted',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_RESUBSCRIBED: 'resubscribed',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_SIGNUP: 'signup',
        /**
         * @type {String}
         */
        ONLINE_ADS_EVENT_TYPE_TRIAL_START: 'trial_start',
        // Possible values for the 'orderType' dataLayer variable when a download
        // purchase is to be tracked for online ads
        /**
         * @type {String}
         */
        PURCHASE_ORDER_TYPE_IMAGE: 'image',
        /**
         * @type {String}
         */
        PURCHASE_ORDER_TYPE_VIDEO: 'video',
        /**
         * @type {String}
         */
        PURCHASE_ORDER_TYPE_SUBSCRIPTION: 'subscription',
        // Values for tracking signup-events from the client side
        /**
         * @type {String}
         */
        SIGNUP_EVENT_CATEGORY: 'Signup',
        /**
         * @type {String}
         */
        SIGNUP_EVENT_TYPE: 'sign_up',
        /**
         * @type {String}
         */
        CUSTOM_CLICK_EVENT_TYPE: 'custom_click',
        NAVBAR_SEARCH: 'Nav_Search',

        CREATE_DESIGN_FROM_MEDIA_CLICKED: 'CreatePosterFromUpload_Clicked',
        CREATE_DESIGN_FROM_MEDIA_CREATED: 'CreatePosterFromUpload_Created',
        CREATE_DESIGN_FROM_CUSTOM_DIMENSIONS_CLICKED: 'CreatePosterFromCustomDimensions_Clicked',
        CREATE_DESIGN_FROM_CUSTOM_DIMENSIONS_CREATED: 'CreatePosterFromCustomDimensions_Created',
        CREATE_DESIGN_FROM_AI_IMAGE_CLICKED: 'CreatePosterFromAiImage_Clicked',
        CREATE_DESIGN_FROM_AI_IMAGE_CREATED: 'CreatePosterFromAiImage_Created',
        AI_IMAGE_MENU_OPEN: 'AIImageMenu_Open',
        CREATE_DESIGN_FROM_SIZE_PREFIX: 'CreatePosterFromSize_',
        /**
         * Event type for the social media wizard publishing intro slide video
         */
        PUBLISHING_INTRO_VIDEO: 'PublishingIntroVideo',
        /**
         * Object containing different labels for signup events
         */
        SIGNUP_LABELS: {
            WEB: 'web',
            ASYNC: 'async',
            GOOGLE: 'Google',
            FACEBOOK: 'Facebook',
            APPLE: 'Apple',
            EMAIL: 'Email',
        },
        /**
         * Flag that tells us whether or not the GTM base code has been loaded in.
         * @type {boolean}
         * @private
         */
        isGTMScriptLoaded_: false,
        /**
         * Object containing different event parameter names for GA4 event tracking via GTM
         */
        GA4_EVENT_PARAM_NAMES: {
            USER_PREMIUM_LEVEL: 'userPremiumLevel',
            SEARCH_TERM: 'searchTerm',
            TYPE: 'type',
            PLATFORM: 'platform',
            SOURCE: 'source',
            HASHED_ID: 'hashedId',
            SLIDE_NUMBER: 'slideNumber',
            SIGNUP_PARAM_NAME: 'method',
            TERM: 'term',
            SEATS: 'seats',
            NAME: 'name'
        },
        GA4_MYSTUFF_CATEGORY: 'MyStuff',
        GA4_NEED_MORE_X_PREFIX: 'NeedMore',
        GA4_DESIGN_GALLERY_FILTERS_PREFIX: 'DesignGallery_',
        GA4_NEED_MORE_X_MIN_RESULTS: 10,
        GA4_CUSTOM_SEARCH_DEFINITION: {
            UNKNOWN: 'Unknown',
            DESIGN_GALLERY: 'Templates',
            EMAIL_GALLERY: 'EmailTemplates',
            AUDIO: 'Audio',
            STICKERS: 'Stickers',
            SHAPES: 'Shapes',
            CLIPART: {
                STORYBLOCKS: 'StoryblocksIllustrations',
                PIXABAY: 'PixabayIllustrations',
            },
            STOCK_IMAGE: {
                GETTY: 'GettyImages',
                STORYBLOCKS: 'StoryblocksImages',
                PIXABAY: 'PixabayImages',
            },
            STOCK_VIDEO: {
                GETTY: 'GettyVideos',
                STORYBLOCKS: 'StoryblocksVideos',
                PIXABAY: 'PixabayVideos',
            },
            DESIGN_GALLERY_FILTERS: {
                CONTENT_TYPE: 'FilterType',
                COLOR: 'FilterColor',
                MOTION: 'FilterMotion',
                ORIENTATION: 'FilterOrientation',
                DISPLAY_SIZE: 'FilterSize',
                CATEGORY: 'FilterCategory',
                SUBCATEGORY: 'FilterSubcategory',
            },
        },
        CAROUSEL_ANALYTICS_EVENT_NAME: {
            CAROUSEL: 'Carousel',
            NEXT_SUFFIX: 'CarouselNext',
            PREVIOUS_SUFFIX: 'CarouselPrevious',
            TOGGLE_SUFFIX: 'Toggle',
            TOGGLE_VIEW_ALL: 'ToggleViewAll',
            ITEM_CLICK: 'ItemClick',
            GALLERY: 'Gallery',
            ONLINE_GRAPHIC_DESIGN_LANDING_PAGE: 'OnlineGraphicDesignLandingPage',
            DESIGN_TEMPLATES: 'DesignTemplates',
            DESIGN_TEMPLATE_CATEGORIES: 'DesignTemplateCategories',
            DESIGN_TEMPLATE_SIZES: 'DesignTemplateSizes',
            PRODUCTS: 'Products',
            EMAIL_TEMPLATES: 'EmailTemplates',
            EMAIL_TEMPLATE_THEMES: 'EmailTemplateThemes',
            EMAIL_TEMPLATE_TYPES: 'EmailTemplateTypes',
            WALKTHORUGH: 'Walkthrough',
            RECENT_POSTERS: 'RecentPosters',
            TESTIMONIES: 'Testimonies',
            DEVICES: 'Devices',
            SCREENS: 'Screens',
            THUMBNAIL: 'Thumbnail',
            DESIGN_TIPS_SLIDES: 'DesignTipsSlides',
            REVIEWS: 'Reviews',
            SUBCATEGORY: 'Subcategory',
        },

        EVENTS_CREATE_CLICK_GA_EVENT: 'Events_Create_Click',
        EVENTS_POST_DOWNLOAD_DIALOG_GA_EVENT: 'Events_Create_Postdownload_Dialog',
        AI_CHAT_START_GA_EVENT: 'AIChat_Start',
        AI_CHAT_HOMEPAGE_GA_EVENT: 'AIChat_Homepage',
        AI_CHAT_HOMEPAGE_GA_EVENT_SOURCE: {
            GALLERY: 'Gallery',
        },
        AI_CHAT_HOMEPAGE_GA_EVENT_SOURCE_GALLERY: 'Gallery',
        // These constants appear unused but DO NOT remove, as they are needed by GTM (see Cometly Fingerprint extractor tag)
        COMETLY_FINGERPRINT_TOKEN_COOKIE: 'pmwcfpt',
        COMETLY_FINGERPRINT_TOKEN_COOKIE_SEPARATOR: '~',
        eventQueue: [],
        /**
         * Initializes the Google Tag Manager if we need to, and if it has not been initialized
         * yet. Also pushes global user variables to the dataLayer.
         */
        init: function () {
            if (PMW.enableGTM) {
                if (!this.isGTMScriptLoaded_) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push(this.onGTMWindowLoaded_);
                    this.isGTMScriptLoaded_ = true;
                    (function (w, d, s, l, i) {
                        w[l] = w[l] || [];
                        w[l].push({
                            'gtm.start':
                                new Date().getTime(), event: 'gtm.js',
                        });
                        var f = d.getElementsByTagName(s)[0],
                            j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
                        j.async = true;
                        j.src =
                            'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                        f.parentNode.insertBefore(j, f);
                    })(window, document, 'script', 'dataLayer', PMW.GTM_CONTAINER_ID);
                }

                this.init_();
            }
        },
        /**
         * @param {Object} purchaseData
         * @param {String} [sutm]
         */
        trackGA4PurchaseEvent: function (purchaseData, sutm) {
            if (!PMW.enableGATracking || !this.isGTMScriptLoaded_) {
                return;
            }

            if (sutm === undefined || sutm === null) {
                sutm = '';
            }

            this.pushEventToQueue_({ecommerce: null});  // Clear the previous ecommerce object.

            let params = {};
            if (sutm !== '') {
                params = {'sutm': sutm};
            }

            this.pushEventToQueue_({
                'event': 'purchase',
                'ecommerce': {'purchase': purchaseData},
                'params': params
            });
        },
        /**
         * @param {Object} data
         */
        trackGA4PremiumDownloadEvents: function (data) {
            const ga4CustomEventData = data.ga4CustomEventData;
            if (ga4CustomEventData) {
                if (ga4CustomEventData.gettyPurchaseEventName) {
                    PMW.gtm.trackGA4CustomEvent(ga4CustomEventData.gettyPurchaseEventName);
                }
                if (ga4CustomEventData.newOrderEventName) {
                    PMW.gtm.trackGA4CustomEvent(ga4CustomEventData.newOrderEventName);
                }
            }
        },
        /**
         * Currently only tracking design request
         * @param idPopup
         */
        trackGA4EventForClickedPopupCta: function (idPopup) {
            switch (idPopup) {
                case PMW.marketing.POPUP_ID_DESIGN_REQUEST:
                    PMW.gtm.trackGA4CustomEvent('Gallery_DesignRequest');
                    break;
                default:
                    break;
            }
        },
        /**
         * @param {String} eventName
         * @param {Object} params
         */
        trackGA4CustomEvent: function (eventName, params = {}) {
            if (!PMW.enableGATracking || !this.isGTMScriptLoaded_) {
                return;
            }

            this.pushEventToQueue_({
                'event': 'custom_event',
                'events': {
                    'name': eventName,
                    'params': params,
                },
            });
        },
        /**
         * @param {String} searchTerm
         * @param {String} searchType
         * @param {number} numResults
         * @param {number} [minResults]
         */
        trackGA4Search: function (searchTerm, searchType, numResults, minResults) {
            if (!PMW.enableGATracking || !this.isGTMScriptLoaded_) {
                return;
            }
            if (minResults === undefined) {
                minResults = PMW.gtm.GA4_NEED_MORE_X_MIN_RESULTS;
            }

            PMW.gtm.trackGA4CustomEvent('view_search_results', {search_term: searchTerm});
            PMW.gtm.trackGA4NeedMoreXEvent(searchTerm, searchType, numResults, minResults);
        },
        /**
         * Tracks an Online Ads Tracking event by pushing event data to the dataLayer
         * for GTM to pick up.
         * @param {String} eventType Must be one of the ONLINE_ADS_EVENT_TYPE_* constants defined above
         * @param {Object} data
         */
        trackOnlineAdsTrackingEvent: function (eventType, data) {
            if (!PMW.enableOnlineAdsTracking || !this.isGTMScriptLoaded_) {
                return;
            }

            data.event = 'online_ads_event';
            data.onlineAdsTrackingEventType = eventType;
            if (this.isGAdsEnhancedConversion_(eventType)) {
                const hashedEmail = PMW.getUserHashedEmail();
                if (hashedEmail !== null && hashedEmail.length > 0) {
                    data.userHashedEmail = hashedEmail;
                }
            }
            this.pushEventToQueue_(data);
        },
        /**
         * Pushes the user persona value to the dataLayer for GTM to pick up.
         */
        setUserPersona: function () {
            if (!this.isGTMScriptLoaded_) {
                return;
            }

            let userPersonaId = PMW.getUserPersonaId();
            if (userPersonaId !== null) {
                window.dataLayer.push({'userPersonaId': userPersonaId});
            }
        },
        trackGA4NeedMoreXEvent: function (searchTerm, searchType, numResults, minResults) {
            if (minResults === undefined) {
                minResults = PMW.gtm.GA4_NEED_MORE_X_MIN_RESULTS;
            }

            if (numResults <= minResults) {
                PMW.gtm.trackGA4CustomEvent(`${PMW.gtm.GA4_NEED_MORE_X_PREFIX}${searchType}`, {
                    searchTerm: searchTerm,
                    numResults: numResults,
                });
            }
        },
        /**
         * @param {string} carouselName
         */
        trackGA4CarouselNextEvent: function (carouselName) {
            PMW.gtm.trackGA4CustomEvent(`${carouselName}${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.NEXT_SUFFIX}`);
        },
        /**
         * @param {string} carouselName
         */
        trackGA4CarouselPreviousEvent: function (carouselName) {
            PMW.gtm.trackGA4CustomEvent(`${carouselName}${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.PREVIOUS_SUFFIX}`);
        },
        /**
         * @param {string} carouselName
         * @param {string} itemName
         */
        trackGA4CarouselItemClick: function (carouselName, itemName) {
            PMW.gtm.trackGA4CustomEvent(`${carouselName}${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.ITEM_CLICK}`, {itemName: itemName});
        },
        /**
         * @param {string} toggleBtnName
         */
        trackGA4CarouselToggleButtonClick: function (toggleBtnName) {
            PMW.gtm.trackGA4CustomEvent(`${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.CAROUSEL}${toggleBtnName}${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.TOGGLE_SUFFIX}`);
        },
        /**
         * @param {string} toggleBtnName
         */
        trackGA4CarouselToggleViewAllClick: function (toggleBtnName) {
            PMW.gtm.trackGA4CustomEvent(`${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.CAROUSEL}${toggleBtnName}s${PMW.gtm.CAROUSEL_ANALYTICS_EVENT_NAME.TOGGLE_VIEW_ALL}`);
        },
        trackGA4CustomClick: function (clickName) {
            PMW.gtm.trackGA4CustomEvent(PMW.gtm.CUSTOM_CLICK_EVENT_TYPE, {[PMW.gtm.GA4_EVENT_PARAM_NAMES.NAME]: clickName});
        },
        trackSignupEvents: function (signupMethod) {
            if (!PMW.enableGATracking || !this.isGTMScriptLoaded_) {
                return;
            }

            this.setUserProps_();
            this.trackGA4CustomEvent(PMW.gtm.SIGNUP_EVENT_TYPE, {[PMW.gtm.GA4_EVENT_PARAM_NAMES.SIGNUP_PARAM_NAME]: signupMethod});
            this.trackOnlineAdsTrackingEvent(this.ONLINE_ADS_EVENT_TYPE_SIGNUP, {});
        },
        trackGA4DebugEvent: function () {
            if (PMW.util.readCookie('pmwsu') === '1') {
                const eventName = 'debug';
                this.trackGA4CustomEvent(eventName);
                PMW.write(PMW.util.site_url('user/recordGA4DebugEvent'), {eventName: eventName});
            }
        },
        /**
         * Enables tracking via GA and GAds based on user consent.
         * @private
         */
        initTrackingOnGTM_: function () {
            if (PMW.enableGATracking) {
                window.dataLayer.push({'enableGA': 1});
            }

            if (PMW.enableOnlineAdsTracking && PMW.ONLINE_ADS_TRACKING_ENABLED) {
                window.dataLayer.push({'enableOnlineAdsTracking': 1});
            }

            window.dataLayer.push({'personalizationConsent': PMW.enablePersonalizationTracking ? 'granted' : 'denied'});
        },
        /**
         * Pushes user properties (hashed ID, persona, sales funnel state) to the
         * dataLayer for GTM to pick up.
         * @private
         */
        setUserProps_: function () {
            let userHashedId = PMW.getUserHashedId(),
                userHashedEmail = PMW.getUserHashedEmail(),
                userPersonaId = PMW.getUserPersonaId(),
                data = {
                    'userType': PMW.getUserType(),
                    'userPremiumLevel': PMW.getUserPremiumLevel().toString(),
                    'userLifeCycleState': PMW.getUserSalesFunnelState(),
                    'userTrialState': PMW.getUserTrialState(),
                    'userCountryCode': PMW.getUserEligibleCountryCode(),
                };

            if (userHashedId !== null) {
                data.userHashedId = userHashedId;
            }
            if (userPersonaId !== null) {
                data.userPersonaId = userPersonaId;
            }
            if (userHashedEmail !== null) {
                data.userHashedEmail = userHashedEmail;
            }
            window.dataLayer.push(data);
        },
        /**
         * @param {String} eventName
         * @returns {boolean}
         * @private
         */
        isGAdsEnhancedConversion_: function (eventName) {
            return (
                eventName === PMW.gtm.ONLINE_ADS_EVENT_TYPE_SUBSCRIPTION_PURCHASE ||
                eventName === PMW.gtm.ONLINE_ADS_EVENT_TYPE_TRIAL_CONVERTED ||
                eventName === PMW.gtm.ONLINE_ADS_EVENT_TYPE_TRIAL_START
            );
        },
        /**
         * Pushes user variables, in addition to flags that enable GA and GAds
         * tracking, to the dataLayer for GTM to pick up. Also additionally tracks
         * a signup event for online ads tracking (if enabled) if the new user
         * cookie is found.
         * @private
         */
        init_: function () {
            if (!this.isGTMScriptLoaded_) {
                return;
            }

            this.initTrackingOnGTM_();
            this.setUserProps_();
        },
        isGTMWindowLoaded_: function () {
            return (
                typeof window['google_tag_manager'] === 'object' &&
                typeof window['google_tag_manager'].dataLayer === 'object' &&
                window['google_tag_manager'].dataLayer.gtmLoad
            );
        },
        onGTMWindowLoaded_: function () {
            if (PMW.gtm.eventQueue.length > 0 && window.dataLayer.some(item => item.event === 'gtm.load')) {
                PMW.gtm.fireAllEventsInQueue_();
            }
        },
        pushEventToQueue_: function (eventData) {
            if (this.isGTMWindowLoaded_()) {
                window.dataLayer.push(eventData);
            } else {
                this.eventQueue.push(eventData);
            }
        },
        fireAllEventsInQueue_: function () {
            while (PMW.gtm.eventQueue.length > 0) {
                const eventData = PMW.gtm.eventQueue.shift();
                window.dataLayer.push(eventData);
            }
        }
    },
    /**
     * Namespace for functions related to the user tracking consent dialog.
     */
    userConsent: {
        /**
         * Flag that tells us whether or not the handlers for the manage consent form have been initialized.
         * @type {boolean}
         * @private
         */
        initManageConsentForm_: false,
        /**
         * Returns an object with the consent cookie values
         * @returns {*} Returned object contains four boolean keys:
         *  1. auto - auto consent, if we auto consented user (so all consent is given)
         *  2. googleAnalytics - if user has allowed google analytics tracking
         *  3. onlineAds - if user has allowed online ads tracking
         *  4. personalization - if user has allowed personalization
         */
        getConsentCookieValues: function () {
            let consentCookieValues = PMW.util.readCookie('pmwuc');

            if (consentCookieValues === null) {
                return null;
            }

            let values = consentCookieValues.split(',');

            return {
                auto: values[0] === '1',
                googleAnalytics: values[1] === '1',
                onlineAds: values[2] === '1',
                personalization: values[3] === '1',
            };
        },
        /**
         * Checks the server for the current user's tracking consent values. If the server
         * responds telling us we need to ask for consent, the consent form is shown. If the
         * server responds with valid consent values, the consent cookie will be set and
         * the Google Tag Manager will be inited (or not) accordingly.
         * NOTE: No error handling done as there was no user initiated action and it would
         *       look weird, so in error cases, just wait for the user to browse to another
         *       page and try again.
         */
        checkConsent: function () {
            let request = new XMLHttpRequest(),
                res;

            request.open('GET', PMW.util.site_url('user/checkConsent'), true);
            request.onload = function () {
                if (this.status >= 200 && this.status < 400) {
                    try {
                        res = JSON.parse(this.response);
                    } catch (err) {
                        return;
                    }
                    if (res.status === 'success' && typeof res.data !== 'undefined') {
                        let data = res.data,
                            auto = data.auto,
                            googleAnalytics = data.googleAnalytics,
                            onlineAds = data.onlineAds,
                            personalization = data.personalization;

                        if (data.ask === true) {
                            PMW.userConsent.showUserConsentForm();
                        } else {
                            PMW.userConsent.setConsentCookieValues(auto, googleAnalytics, onlineAds, personalization);
                            if (auto || googleAnalytics) {
                                // If enableGATracking was false, then we are in dev environment, keep it false
                                PMW.enableGATracking = PMW.enableGATracking !== false;
                            } else {
                                PMW.enableGATracking = false;
                            }

                            if (auto || onlineAds) {
                                // If enableOnlineAdsTracking was false, then we are in dev environment, keep it false
                                PMW.enableOnlineAdsTracking = PMW.enableOnlineAdsTracking !== false;
                            } else {
                                PMW.enableOnlineAdsTracking = false;
                            }

                            if (auto || personalization) {
                                // If enablePersonalizationTracking was false, then we are in dev environment, keep it false
                                PMW.enablePersonalizationTracking = PMW.enablePersonalizationTracking !== false;
                            } else {
                                PMW.enablePersonalizationTracking = false;
                            }

                            PMW.gtm.init();
                        }
                    }
                }
            };

            request.send();
        },
        /**
         * Click handler for when a user clicks 'manage' on the cookie consent form.
         */
        manageUserConsent: function (e) {
            let btn = $(e.currentTarget);

            PMW.userConsent.hideUserConsentForm();
            PMW.userConsent.showManageConsentDialog({
                googleAnalytics: btn.attr('data-google-analytics-consent') === '1',
                onlineAds: btn.attr('data-online-ads-consent') === '1',
                personalization: btn.attr('data-personalization-consent') === '1',
            });
        },
        /**
         * Shows and inits the user consent form.
         */
        showUserConsentForm: function () {
            let form = $('#user-consent-form');
            // Until we can fix the root cause for CSS failing to load on the homepage, we've added a style attribute (display:none) on the form
            // which should be removed in the normal course of things, but if CSS fails to load, we don't want this form visible
            form.removeClass('_hidden').css('display', '');

            if (!PMW.initConsentForm_) {
                PMW.initConsentForm_ = true;
                PMW.onClick(form.find('.btn-manage-consent'), null, PMW.userConsent.manageUserConsent);
                PMW.onClick(form.find('.btn-give-consent'), null, PMW.userConsent.sendUserConsent.bind(this, {
                    googleAnalytics: true,
                    onlineAds: true,
                    personalization: true,
                }));
            }
        },
        /**
         * Hides the user consent form.
         */
        hideUserConsentForm: function () {
            let form = $('#user-consent-form');
            if (form.length > 0) {
                form.addClass('_hidden').removeClass('-processing');
            }
        },
        /**
         * Displays the modal dialog for managing consent options
         * @param {object} [consentDefaults]
         */
        showManageConsentDialog: function (consentDefaults) {
            let modal = $('#manage-cookie-modal');

            // Populate the checkboxes as needed
            $('#chk-google-analytics').prop('checked', consentDefaults.googleAnalytics);
            $('#chk-online-ads').prop('checked', consentDefaults.onlineAds);
            $('#chk-personalization').prop('checked', consentDefaults.personalization);

            // Until we can fix the root cause for CSS failing to load on the homepage, we've added a style attribute (display:none) on the modal
            // which should be removed in the normal course of things, but if CSS fails to load, we don't want this modal visible
            modal.removeClass('_hidden').css('display', '');
            modal.css('z-index', 10000);
            if (!this.initManageConsentForm_) {
                this.initManageConsentForm_ = true;

                PMW.onClick(modal.find('.btn-done'), null, PMW.userConsent.onManageConsentDone.bind(this, consentDefaults));
                PMW.onClick(modal.find('.btn-cancel'), null, PMW.userConsent.hideManageConsentDialog);
            }
        },
        /**
         * Click handler for the done button on the manage consent dialog
         */
        onManageConsentDone: function (consentDefaults) {
            let userConsent = {
                    googleAnalytics: $('#chk-google-analytics').is(':checked'),
                    onlineAds: $('#chk-online-ads').is(':checked'),
                    personalization: $('#chk-personalization').is(':checked'),
                },
                consentCookie = PMW.userConsent.getConsentCookieValues();

            // If the user has not set their consent or if the setting of consent is different than the current
            // update the server with new values
            if (!consentCookie || JSON.stringify(consentDefaults) !== JSON.stringify(userConsent)) {
                PMW.userConsent.sendUserConsent(userConsent);
            } else {
                // Otherwise the values the user selected are the same as before, just hide the dialog
                PMW.userConsent.hideManageConsentDialog();
            }
        },
        /**
         * Hides the manage consent form
         */
        hideManageConsentDialog: function () {
            $('#manage-cookie-modal').addClass('_hidden');

            // If the cookie is not set, then the user has not given their consent yet, just opened the manage dialog and clicked cancel.
            // If this is the case, then show the consent form
            let consentCookie = PMW.userConsent.getConsentCookieValues();
            if (!consentCookie) {
                PMW.userConsent.showUserConsentForm();
            }
        },
        /**
         * Success handler for setConsent, updates the cookie and hides the dialog
         * @param {object} userConsent
         */
        onSetConsentDone: function (userConsent) {
            PMW.userConsent.setConsentCookieValues(false, userConsent.googleAnalytics, userConsent.onlineAds, userConsent.personalization);
            PMW.userConsent.hideManageConsentDialog();
            PMW.userConsent.hideUserConsentForm();
            PMW.initTracking();
        },
        /**
         * Sets user consent cookie values and also updates the server if a user is logged in.
         */
        sendUserConsent: function (consent) {
            // If logged in, update the server
            if (PMW.getUserId() !== 'notloggedin') {
                $('#user-consent-form').addClass('-processing');
                PMW.write(PMW.util.site_url('user/setConsent'), consent, PMW.userConsent.onSetConsentDone.bind(this, consent));
            } else {
                PMW.userConsent.onSetConsentDone(consent);
            }
        },
        /**
         * Sets the consent cookie value. Only use this function to set the consent cookie value as it knows
         * how to properly format the value.
         * @param {boolean} auto
         * @param {boolean} googleAnalytics
         * @param {boolean} onlineAds
         * @param {boolean} personalization
         */
        setConsentCookieValues: function (auto, googleAnalytics, onlineAds, personalization) {
            let autoStr = auto ? '1' : '0',
                googleAnalyticsStr = googleAnalytics ? '1' : '0',
                onlineAdsStr = onlineAds ? '1' : '0',
                personalizationStr = personalization ? '1' : '0';

            if (window.PMW.isEmbedded() && 'requestStorageAccess' in document && (PMW.isSafari() || PMW.isIOS())) {
                document.requestStorageAccess().then(() => {
                    PMW.util.setCookie('pmwuc', autoStr + ',' + googleAnalyticsStr + ',' + onlineAdsStr + ',' + personalizationStr, PMW.TRACKING_COOKIE_EXPIRY);
                });
            } else {
                PMW.util.setCookie('pmwuc', autoStr + ',' + googleAnalyticsStr + ',' + onlineAdsStr + ',' + personalizationStr, PMW.TRACKING_COOKIE_EXPIRY);
            }
        },
    },
    /*************
     * Public functions that are required before the DOM completely loads or are
     * required on most pages.
     *************/

    /**
     * Any initialization code that needs to run in the <head> tag goes here.
     * This function is called after the required constants are initialized.
     */
    init: function () {
        window.onerror = PMW.onWindowError;
        PMW.initializeCache('pmw', PMW.VERSION);
        PMW.initPremiumCookieForNavLinks();
        PMW.setupNavExperiment();
        PMW.setupJavaScriptExperiment();
        PMW.initEligibleCountryCode();
        PMW.updateFooterContent();
        PMW.initGA4TrackingForNavBarSearch();
        PMW.getAppleAttributionToken();
        PMW.syncAppleSubscriptionOnAppLaunch();
        PMW.initI18next(PMW.LANGUAGE, () => {
            void PMW.getIOSUpdateAlert();
        });
    },

    csrfSelector: 'input[class=js-csrf-input]',

    /**
     * An alternative for jquery.load() function. It embeds the csrf data with the outgoing ajax requests.
     * @param {object} target
     * @param {string} url
     * @param {object|string|string} params
     * @param {function}[callback]
     * @param {string} context the calling side context- where the AJAX request is being initiated from
     * @returns {object}
     */
    load: function (target, url, params, callback, context) {
        let selector, response, type,
            self = target,
            off = url.indexOf(' '),
            ajaxRequest = null;

        let jsVersion = PMW.VERSION,
            htmlVersion = PMW.HTML_VERSION,
            userId = PMW.getUserId(),
            jsVersionKey = '_jv',
            htmlVersionKey = '_v',
            userIdKey = '_rand',
            csrfSourceKey = 'csrf_token_source',
            dataTypeKey = 'data_type';

        if (off >= 0) {
            selector = url.slice(off, url.length);
            url = url.slice(0, off);
        }

        // If it's a function
        if (jQuery.isFunction(params)) {

            // We assume that it's the callback
            callback = params;
            params = undefined;

            // Otherwise, build a param string
        } else if (params && typeof params === 'object') {
            type = 'POST';
        }

        // If we have elements to modify, make the request
        if (self.length > 0) {
            PMW.getCsrfToken(function (token_value, token_source) {
                var token_name = 'csrf_token';
                var csrfMetaTag = document.querySelector('meta[name="data-csrf-name"]');
                if (csrfMetaTag) {
                    token_name = csrfMetaTag.content;
                }

                if (typeof params === 'object') {
                    if (params === null) {
                        params = {};
                    }

                    let customOrderedParams = [
                        [token_name, token_value],
                        [htmlVersionKey, htmlVersion],
                        [jsVersionKey, jsVersion],
                        [userIdKey, userId],
                        [csrfSourceKey, token_source],
                        [dataTypeKey, PMW.objectDataType],
                    ];

                    for (const [key, value] of Object.entries(params)) {
                        customOrderedParams.push([key, value]);
                    }
                    params = Object.fromEntries(customOrderedParams);
                }

                const ajaxOpts = {
                    url: url,

                    // if "type" variable is undefined, then "GET" method will be used
                    type: type,
                    dataType: 'html',
                    data: params,
                    beforeSend: function () {
                        if (context && PMW.currentAjaxRequest[context] != null) {
                            PMW.currentAjaxRequest[context].abort();
                        }
                    },
                };
                addLocalOnlyParamsToAjax(ajaxOpts);
                PMW.addAjaxParamsForSubdomainToSubfolderRequests(ajaxOpts);

                ajaxRequest = jQuery.ajax(ajaxOpts).done(function (responseText) {

                    // Save response for use in complete callback
                    response = arguments;

                    self.html(selector ?

                        // If a selector was specified, locate the right elements in a dummy div
                        // Exclude scripts to avoid IE 'Permission Denied' errors
                        jQuery('<div>').append(jQuery.parseHTML(responseText)).find(selector) :

                        // Otherwise use the full result
                        responseText);

                }).complete(callback && function (jqXHR, status) {
                    self.each(callback, response || [jqXHR.responseText, status, jqXHR]);
                    PMW.currentAjaxRequest[context] = null;
                });
            }.bind(this));

            if (context) {
                PMW.currentAjaxRequest[context] = ajaxRequest;
            }
        }
        return target;
    },
    /**
     * returns csrf token by reading the csrf cookie and if cookie
     * is not found then it makes a GET request to get csrf data
     * @param {Function} callback
     */
    getCsrfToken: function (callback) {
        var csrf_hash = this.util.readCookie('csrf_cookie');
        if (csrf_hash === null) {
            return this.getRequest('authenticate/getCsrfData').then(function (response) {
                csrf_hash = PMW.util.readCookie('csrf_cookie');
                if (csrf_hash === null) {
                    return callback(response.data['csrf-token-hash'], PMW.csrfSourceAjaxRequestResponse);
                }
                return callback(csrf_hash, PMW.csrfSourceCookieAfterAjaxRequest);
            });
        } else {
            return callback(csrf_hash, PMW.csrfSourceCookie);
        }
    },
    /**
     * wrapper for post requests. It embeds the csrf token in all the requests.
     * @param {string} url
     * @param {string|object} [data]
     * @param {function} [callback]
     * @param {string} [type]
     * @param {Object} [ajaxOpts]
     */
    write: function (url, data, callback, type, ajaxOpts = {}) {
        var jsVersion = PMW.VERSION,
            htmlVersion = PMW.HTML_VERSION,
            userId = PMW.getUserId(),
            jsVersionKey = '_jv',
            htmlVersionKey = '_v',
            userIdKey = '_rand',
            csrfSourceKey = 'csrf_token_source',
            dataTypeKey = 'data_type';


        userId = (userId === 'notloggedin') ? 'Anon' : userId;

        if (typeof data === 'function') {
            type = type || callback;
            callback = data;
            data = {};
        }
        if (typeof data !== 'object' && typeof data !== 'string') {
            data = {};
        }
        return PMW.getCsrfToken(function (token_value, token_source) {
            var token_name = 'csrf_token';
            var csrfMetaTag = document.querySelector('meta[name="data-csrf-name"]');
            if (csrfMetaTag) {
                token_name = csrfMetaTag.content;
            }

            if (typeof data === 'object') {
                if (data === null) {
                    data = {};
                }
                if (data instanceof FormData) {
                    let customOrderedFormData = new FormData();
                    customOrderedFormData.append(token_name, token_value);
                    customOrderedFormData.append(htmlVersionKey, htmlVersion);
                    customOrderedFormData.append(jsVersionKey, jsVersion);
                    customOrderedFormData.append(userIdKey, userId);
                    customOrderedFormData.append(csrfSourceKey, token_source);
                    customOrderedFormData.append(dataTypeKey, PMW.formDataType);

                    for (const item of data.entries()) {
                        customOrderedFormData.append(item[0], item[1]);
                    }
                    data = customOrderedFormData;
                    ajaxOpts.processData = false;
                    ajaxOpts.contentType = false;
                } else {
                    let customOrderedData = [
                        [token_name, token_value],
                        [htmlVersionKey, htmlVersion],
                        [jsVersionKey, jsVersion],
                        [userIdKey, userId],
                        [csrfSourceKey, token_source],
                        [dataTypeKey, PMW.objectDataType],
                    ];

                    for (const [key, value] of Object.entries(data)) {
                        customOrderedData.push([key, value]);
                    }
                    data = Object.fromEntries(customOrderedData);
                }
            }
            if (typeof data === 'string') {
                if (data.search(token_name) >= 0) {
                    data = data.replace(token_name + '=' + PMW.util.getQueryStringValue(token_name, '?' + data), token_name + '=' + token_value);
                } else {
                    if (data !== '') {
                        data += '&';
                    }

                    data += (htmlVersionKey + '=' + htmlVersion + '&');
                    data += (jsVersionKey + '=' + jsVersion + '&');
                    data += (userIdKey + '=' + userId + '&');

                    data += (token_name + '=' + token_value + '&');
                    data += (csrfSourceKey + '=' + token_source + '&');
                    data += (dataTypeKey + '=' + PMW.stringDataType);
                }
            }

            ajaxOpts.type = 'POST';
            ajaxOpts.url = url;
            if (typeof data !== 'undefined') {
                ajaxOpts.data = data;
            }
            if (typeof callback !== 'undefined') {
                ajaxOpts.success = callback;
            }
            if (typeof type !== 'undefined') {
                ajaxOpts.dataType = type;
            }

            addLocalOnlyParamsToAjax(ajaxOpts);
            PMW.addAjaxParamsForSubdomainToSubfolderRequests(ajaxOpts);
            return $.ajax(ajaxOpts).fail(function (response) {
                if (response.status === 403) {
                    response.data = response.responseJSON;
                    const errorMessage = i18next.t('pmwjs_unauthorized_request_error');
                    if (callback !== $.noop && typeof callback !== 'undefined') {
                        alert(errorMessage);
                    }
                } else if (callback !== $.noop && typeof callback !== 'undefined') {
                    callback(response);
                }
            });
        });
    },

    /**
     * A better version of PMW.write used when hitting our own instances
     * @param {string} uri
     * @param {any} [data]
     * @param {string} [type]
     * @param {Object} [ajaxOpts]
     * @returns {Promise<*>}
     */
    writeLocal: async function (uri, data, type, ajaxOpts = {}) {
        let response = await PMW.write(PMW.util.site_url(uri), data, $.noop, type, ajaxOpts);
        if (response.status === 'success') {
            return response.data;
        }

        throw getErrorAjaxResponse(response, uri, data);
    },
    /**
     * A better version of PMW.getRequest used when hitting our own instances
     * @param {string} uri
     * @param {any} [data]
     * @param {string} [key]
     * @param {number} [cacheControlAge]
     * @param {number} [cacheExpiry]
     * @returns {Promise<*>}
     */
    readLocal: async function (uri, data, key, cacheControlAge, cacheExpiry = undefined) {
        let response = await PMW.getRequest(uri, data, key, cacheControlAge, cacheExpiry);
        if (response.status === 'success') {
            return response.data;
        }

        throw getErrorAjaxResponse(response, uri, data);
    },
    /**
     * wrapper for all form submits' which do not get intercepted anywhere else. It updates the csrf token value present
     * in the hidden input field.
     * @param {Event} e
     */
    formSubmit: function (e) {
        if ((window.location.href).includes('index.php/manageapp/')) {
            return;
        }

        e.preventDefault();
        var form = $(e.currentTarget);
        PMW.getCsrfToken(function (token) {
            form.find(PMW.csrfSelector).val(token);
            form.get(0).submit();
        });
    },

    /**
     * This function handles long ajax calls i.e above 1 minute because browsers timeout an ajax call after 1 minute.
     * This function sends an initial ajax call which should return the key before it starts executing the long job.
     * The key is used for polling the status of the initial ajax call till its done.
     * Check the startPollingAjaxStatus function for the server side implementation
     * @param {function} ajaxFunction The initial ajax function, should be using writeLocal or readLocal
     * @param {function} pollingFunction The ajax function for polling the status of initial ajax call, should be using writeLocal or readLocal
     * @param {int} [timeout] timeout in millisecond
     * @param {string|null} [eventId] eventId for which polling function is called
     */
    pollingAjaxCallAsync: async function (ajaxFunction, pollingFunction, timeout = LONG_AJAX_CALL_TIMEOUT, eventId = null) {
        let initialData = await ajaxFunction(),
            startingTime = performance.now(),
            halt = false,
            listener = () => halt = true;

        if (eventId) {
            document.addEventListener(this.getPollingStopEvent(eventId), listener);
        }

        try {
            while (!halt && performance.now() - startingTime < timeout) {
                let data = await pollingFunction(initialData);
                if (data.isAvailable) {
                    return data.data;
                }
            }
        } finally {
            if (eventId) {
                document.removeEventListener(this.getPollingStopEvent(eventId), listener);
            }
        }
    },

    /**
     *
     * @param eventId
     * @returns {`polling:stop${string}`}
     */
    getPollingStopEvent: function (eventId) {
        return `polling:stop:${eventId}`;
    },

    /**
     * This function handles long ajax calls i.e above 1 minute because browsers timeout an ajax call after 1 minute.
     * This function sends an initial ajax call which should return the key before it starts executing the long job.
     * The key is used for polling the status of the initial ajax call till its done.
     * Check the startPollingAjaxStatus function for the server side implementation
     * @param {function} ajaxFunction The initial ajax function
     * @param {function} pollingFunction The ajax function for polling the status of initial ajax call
     * @param {function} onSuccess handler for when the job is completed
     * @param {function} onError handler for when an error occurs
     * @deprecated use pollingAjaxCallAsync instead
     */
    pollingAjaxCall: function (ajaxFunction, pollingFunction, onSuccess, onError) {
        ajaxFunction()
            .done(this.onInitialAjaxCallSuccess_.bind(this, pollingFunction, onSuccess, onError))
            .fail(onError);
    },
    /**
     * Handler for when initial ajax call returns successfully
     * @param {function} pollingFunction The ajax function for polling the status of initial ajax call
     * @param {function} onSuccess handler for when the job is completed
     * @param {function} onError handler for when an error occurs
     * @param {string} response response data from server
     */
    onInitialAjaxCallSuccess_: function (pollingFunction, onSuccess, onError, response) {
        if (response.status === 'error') {
            onError(response.message);

        } else {
            this.pollAjaxCall_(pollingFunction, onSuccess, onError, response.data);
        }
    },

    /**
     * Do the ajax call to check the status of the initial ajax call
     * @param {function} pollingFunction The ajax function for polling the status of initial ajax call
     * @param {function} onSuccess handler for when the job is completed
     * @param {function} onError handler for when an error occurs
     * @param {object} initialData data received from initial ajax call
     */
    pollAjaxCall_: function (pollingFunction, onSuccess, onError, initialData) {
        pollingFunction(initialData)
            .done(this.isPollingAjaxCallFinished_.bind(this, pollingFunction, onSuccess, onError, initialData))
            .fail(onError);
    },

    /**
     * Handler for when the polling ajax call finishes. In case of success call the success function else recall
     * poll ajax function
     * @param {function} pollingFunction The ajax function for polling the status of initial ajax call
     * @param {function} onSuccess handler for when the job is completed
     * @param {function} onError handler for when an error occurs
     * @param {object} initialData data received from initial ajax call
     * @param {object} response response data from server telling whether the initial ajax call was completed or not
     */
    isPollingAjaxCallFinished_: function (pollingFunction, onSuccess, onError, initialData, response) {
        if (response.status === 'success' && response.data) {
            if (response.data.isAvailable) {
                onSuccess(response.data.data);
            } else {
                this.pollAjaxCall_(pollingFunction, onSuccess, onError, initialData);
            }
        } else {
            onError();
        }
    },
    /**
     * Logs JavaScript errors onto the server. (does not log errors in JS loaded from other domains)
     * @param {string} msg
     * @param {string} url
     * @param {number} line
     * @param {number} col
     * @param {string} error
     * @returns {boolean} Return false to not prevent the browser's default behavior
     */
    onWindowError: function (msg, url, line, col, error) {
        // Only log errors for our own JS.
        if (typeof url === 'string' && url.length > 0 && url.indexOf(PMW.BASE_URL) > 0) {
            // col and error are in the new HTML5 spec and not supported by all browsers yet
            var extra = !col ? '' : '\ncolumn: ' + col;
            extra += !error ? '' : '\nerror: ' + error;

            var log = 'Error: ' + msg + '\nurl: ' + url + '\nline: ' + line + extra;
            PMW.log(log);
        }
        return false;
    },
    /**
     * Initializes the premium nav options on the header if no cookie is set and
     * if the user is premium.
     * We do not check/set the cookie if the user type is anything other than regular (designer, teacher, student, notloggedin)
     */
    initPremiumCookieForNavLinks: function () {
        if (PMW.getUserPremiumLevel() === null && PMW.getUserType() === 'regular') {
            if (typeof window.appendNavLinksForPremium !== 'undefined') {

                PMW.getCsrfToken(function (token_value) {
                    var token_name = 'csrf_token';
                    var csrfMetaTag = document.querySelector('meta[name="data-csrf-name"]');
                    if (csrfMetaTag) {
                        token_name = csrfMetaTag.content;
                    }

                    var ajax = new XMLHttpRequest();
                    ajax.onreadystatechange = function () {
                        if (this.readyState == 4 && this.status == 200) {
                            if (this.responseText.length > 0) {
                                var response = JSON.parse(this.responseText);
                                // there's only one property set, premium. and we check if the responsetext is > 0
                                if (response.data.premium === '1') { // Will always be success, regardless of premium value.
                                    window.appendNavLinksForPremium();
                                }
                            }
                        }
                    };
                    ajax.open('POST', PMW.util.site_url('premium/getUserPremiumLevelJSON'), true);
                    ajax.send(token_name + '=' + encodeURIComponent(token_value));
                });
            }
        }
    },
    processConsentValue: function (consent) {
        PMW.enableGATracking = PMW.enableGATracking !== false && consent;
        PMW.enableOnlineAdsTracking = PMW.enableOnlineAdsTracking !== false && consent;
        PMW.enablePersonalizationTracking = PMW.enablePersonalizationTracking !== false && consent;
        PMW.gtm.init();
    },
    initTrackingForIOSApp: function () {
        if (window.webkit && window.webkit.messageHandlers) {
            window.webkit.messageHandlers.trackingConsent.postMessage({});
            const handler = async (event) => {
                if (event.detail.message === 'askAgain') {
                    return;
                }
                const consent = event.detail.message === 'allowed' ? '1' : '0';
                PMW.processConsentValue(consent);
                document.removeEventListener('trackingConsent', handler);
            }
            document.addEventListener('trackingConsent', handler);
        } else {
            PMW.openErrorModal({log: false});
        }
    },
    isIOSApp: function () {
        return navigator.userAgent.includes('iosPwaApp');
    },
    /**
     * Initializes the tracking consent flow. Checks for tracking consent cookie, if not found asks the server
     * what to do. If the server replies with 'ask', we show the tracking consent pop up.
     *
     * Otherwise uses the cookie values to init (or not) the google tag manager.
     */
    initTracking: function () {
        if (PMW.isIOSApp()) {
            PMW.initTrackingForIOSApp()
            return;
        }

        let consentCookieValues = PMW.userConsent.getConsentCookieValues();
        // If the consent cookie does not exist, check with the server
        if (consentCookieValues === null) {
            PMW.userConsent.checkConsent();
        } else {
            // If enableGATracking was false, then we are in dev environment, keep it false
            PMW.enableGATracking = PMW.enableGATracking !== false && (consentCookieValues.googleAnalytics || consentCookieValues.auto);
            // If enableOnlineAdsTracking was false, then we are in dev environment, keep it false
            PMW.enableOnlineAdsTracking = PMW.enableOnlineAdsTracking !== false && (consentCookieValues.onlineAds || consentCookieValues.auto);
            // If enablePersonalizationTracking was false, then we are in dev environment, keep it false
            PMW.enablePersonalizationTracking = PMW.enablePersonalizationTracking !== false && (consentCookieValues.personalization || consentCookieValues.auto);
            PMW.gtm.init();
        }
    },
    /**
     * If this is a logged in user and eligible country code cookie is not set, this function checks with the server and
     * initilaizes the cookie.
     */
    initEligibleCountryCode: function () {
        if (PMW.getUserId() === 'notloggedin') {
            return;
        }

        let eligibleCountryCodeCookieValue = PMW.util.readCookie('pmwuecc');
        if (eligibleCountryCodeCookieValue === null) {
            PMW.getRequest('user/getUserEligibleCountryCode').done(function (r) {
                if (r.status === 'success') {
                    PMW.util.setCookie('pmwuecc', r.data.eligibleCountryCode, PMW.ELIGIBLE_COUNTRY_CODE_COOKIE_EXPIRY);
                }
            });
        }
    },
    updateFooterContent: function () {
        if (PMW.isIOSApp()) {
            $('.js-pmw-footer').addClass('_hidden');
        }
    },
    getIOSAppVersion: function () {
        if (!navigator.userAgent) {
            return '';
        }
        const appVersion = navigator.userAgent.split('appVersion: ');
        if (appVersion[1]) {
            return appVersion[1].split(';')[0];
        }
        return '';
    },
    isLatestIOSAppVersion: async function () {
        const installedVersion = PMW.getIOSAppVersion();
        if (!installedVersion) {
            return true;
        }
        try {
            const currentVersion = await PMW.readLocal('user/getLatestIOSAppVersion');
            return installedVersion >= currentVersion;
        } catch (e) {
            return true;
        }
    },
    getIOSUpdateAlert: async function () {
        if (PMW.isIOSApp() && !(await PMW.isLatestIOSAppVersion())) {
            alert(i18next.t('pmwjs_update_ios_app'));
            const appStoreLink = 'https://apps.apple.com/app/id' + PMW.APPLE_APP_ID;
            const linkEl = document.createElement('a');
            linkEl.href = appStoreLink;
            linkEl.target = "_top";
            document.body.appendChild(linkEl);
            linkEl.click();
            document.body.removeChild(linkEl);
            window.location.reload();
        }
    },
    getAppleAttributionToken: function () {
        if (!navigator.userAgent.includes('iosPwaApp')) {
            return;
        }
        const cookieValue = PMW.util.readCookie('pmwaat');
        if (cookieValue) {
            return;
        }
        if (window.webkit && window.webkit.messageHandlers) {
            window.webkit.messageHandlers.attributionTokenResponse.postMessage({});
            const handler = (event) => {
                if (event.detail.message) {
                    setTimeout(() => {
                        void PMW.writeLocal('user/saveAppleAdsAttributionToken', {
                            attributionToken: event.detail.message,
                        });
                    }, 10000);
                    PMW.util.setCookie('pmwaat', 1, 365);
                }
                document.removeEventListener('attributionTokenResponse', handler);
            };
            document.addEventListener('attributionTokenResponse', handler);
        }
    },
    syncAppleSubscriptionOnAppLaunch: function () {
        if (!PMW.isUserPremium() && PMW.isUserLoggedIn() && PMW.isIOSApp()) {
            window.webkit.messageHandlers.activeSubscriptionData.postMessage({});
            document.addEventListener('activeSubscriptionData',
                (event) => {
                    const data = event.detail.message;
                    if (data.status === 'active') {
                        window.PMW.writeLocal('appleNotificationHandler/syncSubscriptionStatus', {
                            transactionId: data.originalTransactionId,
                        }).then((response) => {
                            if (response.synced) {
                                window.location.reload();
                            }
                        }).catch((e) => {
                            window.PMW.log(`Could not sync subscription status for user: ${PMW.getUserId()}, subscription: ${JSON.stringify(data)}. Error message: ${e.message}`);
                        });
                    }
                },
                {once: true}
            );
        }
    },

    /**
     * Inits Google reCAPTCHA v3
     */
    initRecaptcha: function () {
        if (!PMW.isRecaptchaInit) {
            PMW.isRecaptchaInit = true;

            var recaptchaLoaded = function () {
                PMW.grecaptchaReady = true;

                if (PMW.isPosterMaker()) {
                    $('.grecaptcha-badge').css('visibility', 'hidden');
                }

                //handling login button
                var loginBtnSelector = $('.js-btn-login');
                loginBtnSelector.parent().removeClass('default -disabled'); // for black text color in disabled login button
                loginBtnSelector.removeAttr('-disabled');

                //handling signup button
                var signupBtnSelector = $('.js-btn-signup');
                signupBtnSelector.removeClass('-disabled');
                signupBtnSelector.removeAttr('-disabled');

                //handling student login button
                $('.js-btn-student-login').removeClass('-disabled');

                //handling async login, async student login, async create account
                var disabledBtns = ['.js-btn-login-async', '.js-btn-create-async', '.js-btn-student-async'],
                    disabledBtnsSelector = $(disabledBtns.join(', '));
                disabledBtnsSelector.removeClass('-disabled');
                disabledBtnsSelector.removeAttr('-disabled');
                PMW.setLoading(null);
            };

            var recaptchaReadyScript = document.createElement('script'),
                m = document.getElementsByTagName('script')[0];

            recaptchaReadyScript.text = 'var recaptchaLoaded = ' + String(recaptchaLoaded);
            m.parentNode.insertBefore(recaptchaReadyScript, m);

            var script = document.createElement('script');
            m = document.getElementsByTagName('script')[0];

            script.async = 1;
            script.src = '//www.recaptcha.net/recaptcha/api.js?onload=recaptchaLoaded&render=' + PMW.RECAPTCHA_V3_KEY;
            m.parentNode.insertBefore(script, m);
        }
    },
    initRecaptchav2: function () {
        let script = document.createElement('script');
        m = document.getElementsByTagName('script')[0];

        script.async = 1;
        script.src = 'https://www.google.com/recaptcha/api.js';
        m.parentNode.insertBefore(script, m);
    },
    /**
     * Checks for active Nav experiments (split tests) and executes the corresponding
     * code for the user's variant stored in the cookie.
     */
    setupNavExperiment: function () {
        if (PMW.NAV_EXPERIMENTS && PMW.NAV_EXPERIMENTS.length > 0) {
            for (var i = 0; i < PMW.NAV_EXPERIMENTS.length; i++) {
                var exp = PMW.NAV_EXPERIMENTS[i],
                    id = exp['experimentId'],
                    key = 'nevk_' + id,
                    variant = PMW.util.readCookie(key);

                if (variant) {
                    switch (id) {
                        case 'EXPERIMENT_ID':
                            break;
                    }
                }
            }
        }
    },
    /**
     * Sets up JavaScript experiments that are active on the current page
     */
    setupJavaScriptExperiment: function () {
        if (PMW.JAVASCRIPT_EXPERIMENT && PMW.EXPERIMENT_ID && PMW.VARIATION_NUMBER) {
            // EXPERIMENT_ID specific code here
        }
    },
    /**
     * Opens the emailoption-modal for the user to select whether to receive promotional emails or not.
     * @param {string|number} action
     * @param {SSORedirectHelper} redirectVO
     * @param {boolean} emailMarketingPreferenceHandled
     */
    openEmailOptInModal: function (action, redirectVO, emailMarketingPreferenceHandled = false) {
        if (emailMarketingPreferenceHandled) {
            PMW.onConnectSuccess(action, redirectVO);
            return;
        }

        PMW.setLoading(null);
        PMW.openCompleteSignupModal({
            onConnect: PMW.onConnectSuccess.bind(this, action, redirectVO),
        });
    },
    /**
     * Processes the callback of a Google or Facebook connect operation when it is
     * successfully completed.
     *
     * @param {string|number} action Response from the server
     * @param {SSORedirectHelper} redirectVO See PMW.fb.connect() or PMW.google.connect().
     * @param {string} [currentDomain] Current domain user is on
     * @param {string} [newDomain] Domain to switch to because of user language preference
     * @param {string} [preferredLanguage] The user's preferred language
     * @returns undefined
     */
    onConnectSuccess: function (action, redirectVO, currentDomain, newDomain, preferredLanguage = '') {
        if (action === 'refresh') {
            window.location.reload();
        } else if (action > 0) {
            let postLoginAction = redirectVO.getPostLoginAction(); // make this an action
            if (typeof postLoginAction === 'string' && postLoginAction !== '') {
                if (typeof currentDomain !== 'undefined' && typeof newDomain !== 'undefined') {
                    postLoginAction = postLoginAction.replace(currentDomain, newDomain);
                }
                window.location.href = decodeURIComponent(postLoginAction);
            } else if ($.isFunction(postLoginAction) || typeof postLoginAction === 'function') {
                if (preferredLanguage) {
                    PMW.USER_PREFERRED_LANGUAGE = preferredLanguage;
                } else {
                    PMW.USER_PREFERRED_LANGUAGE = PMW.util.getLanguage();
                }
                PMW.closeCompleteSignupModal();
                PMW.closeTwoFactorAuthenticationVerificationModal();
                postLoginAction(action);
            } else {
                window.location.href = PMW.util.site_url('posters/mine');
            }
        }
    },
    /**
     * Use this function to attach handlers to the PMW.click event which uses jQuery Mobile's synthetic vclick
     * event. This function wraps handlers in a function that calls preventDefault() on the event object which fixes
     * duplicate clicks on touch devices.
     *
     * If the element clicked has the 'disabled' class, then the handler will not be called.
     *
     * @see https://api.jquerymobile.com/vclick/
     * @param {jQuery} el Bind click handler to this DOM element
     * @param {string|null} s An optional selector. Pass null if you don't want to specify this.
     * @param {Function} h The handler function
     * @param {number} [d=0] Delay in milliseconds to block a double click
     * @param {string} [ev='click'] The event, defaults to click
     * @returns {jQuery} The element passed
     */
    onClick: function (el, s, h, d = 0, ev = 'click') {
        if (typeof $ === 'undefined' || typeof $.vmouse == 'undefined') {
            PMW.click = ev;
        }

        if (!isNaN(d) && d !== 0) {
            el.data('dblTimer', -1);
            el.on(PMW.click, s, function (e) {
                e.preventDefault();
                if (!$(e.currentTarget).hasClass('disabled') && el.data('dblTimer') === -1) {
                    el.data('dblTimer', setTimeout(function () {
                        el.data('dblTimer', -1);
                    }, d));
                    h.apply(this, [e]);
                }

            });
        } else {
            el.on(PMW.click, s, function (e) {
                e.preventDefault();

                var el = $(e.currentTarget);
                if (!el.hasClass('disabled') && el.prop('disabled') != true) {
                    h.apply(this, [e]);
                }

                if (typeof PMW.gtm !== undefined && el.is('[ga4-click-name]')) {
                    PMW.gtm.trackGA4CustomClick(el.attr('ga4-click-name'));
                }
            });
        }

        return el;
    },
    /**
     * Since we're not using vclick anymore, adding support for clicks with touchstart as well.
     * Chrome (v56+) defaults all mobile click handlers to passive: true, which can't use e.preventDefault and
     * we can't pass passive: false to jQuery.on() to offset this.
     *
     * @see https://api.jquerymobile.com/vclick/
     * @param {jQuery} el Bind click handler to this DOM element
     * @param {string|null} s An optional selector. Pass null if you don't want to specify this.
     * @param {Function} h The handler function
     * @param {number} [d=0] Delay in milliseconds to block a double click
     * @returns {jQuery} The element passed
     */
    onClickV2: function (el, s, h, d = 0) {
        PMW.onClick(el, s, h, d, 'click touchstart');
    },
    /**
     * Transitions a button into the 'loading' state, and executes the provided
     * callback function.
     * NOTE: Only works for buttons created with the components/cta view file.
     * @param {jQuery} btn
     * @param {Function} cb
     */
    startButtonLoading: function (btn, cb) {
        if (typeof cb !== 'function') {
            cb = function () {
            };
        }

        var defaultLabel = btn.find('.default-label'),
            loadingState = btn.find('.loading-state');

        // Transition to 'loading' state
        btn.parent().addClass('-loading');
        defaultLabel.addClass('animated -fill-mode-forwards slideOut -to-bottom')
            .one(PMW.util.animationEndEvents, function () {
                defaultLabel.addClass('_hidden').removeClass('slideOut -to-bottom');
                loadingState.removeClass('_hidden').addClass('animated -fill-mode-forwards slideIn -from-top')
                    .one(PMW.util.animationEndEvents, function () {
                        loadingState.removeClass('slideIn -from-top');
                        cb();
                    });
            });
    },
    /**
     * Transitions a button from the 'loading' state to the 'loaded' state, and
     * then back to the initial default state after hanging on the 'loaded' state
     * for a short while.
     * NOTE: Only works for buttons created with the components/cta view file.
     * @param {jQuery} btn
     * @param {string} status
     * @param {Function} successCallback
     * @param {Function} errorCallback
     */
    completeButtonLoading: function (btn, status, successCallback, errorCallback) {
        if (typeof successCallback !== 'function') {
            successCallback = function () {
            };
        }

        if (typeof errorCallback !== 'function') {
            errorCallback = function () {
            };
        }

        var cb = (status === 'success' ? successCallback : errorCallback),
            loaderAnimationClass = (status === 'success' ? '-animate-loaded' : '-animate-failed'),
            loaderContainer = btn.find('.loader-container'),
            loadingState = btn.find('.loading-state'),
            loadingLabel = loadingState.find('.loading-label'),
            loadedLabel = loadingState.find('.loaded-label' + '.-' + status),
            defaultLabel = btn.find('.default-label');

        // Transition to 'loaded' state
        loaderContainer.removeClass('-animate-loading').addClass(loaderAnimationClass);
        loadingLabel.addClass('animated -fill-mode-forwards slideOut -to-bottom')
            .one(PMW.util.animationEndEvents, function () {
                loadingLabel.addClass('_hidden').removeClass('slideOut -to-bottom');
                loadedLabel.removeClass('_hidden').addClass('animated -fill-mode-forwards slideIn -from-top')
                    .one(PMW.util.animationEndEvents, function () {
                        loadedLabel.removeClass('slideIn -from-top');

                        // Transition back to initial state after a short delay
                        setTimeout(function () {
                            btn.parent().removeClass('-loading');
                            loadingState.addClass('animated -fill-mode-forwards slideOut -to-top')
                                .one(PMW.util.animationEndEvents, function () {
                                    loadingState.addClass('_hidden').removeClass('slideOut -to-top');
                                    defaultLabel.removeClass('_hidden').addClass('animated -fill-mode-forwards slideIn -from-bottom')
                                        .one(PMW.util.animationEndEvents, function () {
                                            loadingState.removeClass('slideIn -from-bottom');
                                        });
                                });
                            // reset spinner and labels
                            loaderContainer.removeClass(loaderAnimationClass).addClass('-animate-loading');
                            loadingLabel.removeClass('_hidden');
                            loadedLabel.addClass('_hidden');

                            cb();
                        }, 1500);
                    });
            });
    },
    /**
     * This function is called inline in header.php and uses the user's cookie
     * to determine if they're logged in and render their name and utility links
     * for logged in users in the header of the website. This technique is used
     * to allow us to use the same cached HTML for landingpages, regardless of
     * whether the user is logged in and new.
     */
    initHeader: function () {
        /**
         * Given an ID, clears the class attribute for that element and returns it.
         * @param {String} id
         * @returns {Element}
         */
        function c(id) {
            var e = document.getElementById(id);
            e.className = '';
            return e;
        }

        /**
         * Sets the HTML of an element.
         * @param {String} id The element's ID
         * @param {String} h HTML string
         */
        function i(id, h) {
            var e = document.getElementById(id);
            if (e) {
                e.innerHTML = h;
            }
        }

        /**
         * Removes an element from the DOM.
         * @param {String} id The element's ID
         */
        function del(id) {
            var e = document.getElementById(id);
            if (e) {
                e.parentNode.removeChild(e);
            }
        }

        let pmwn = PMW.util.readCookie('pmwn'), n = document.getElementById('nav');

        if (n) {
            // a user is logged in
            if (pmwn !== null && pmwn !== 'new+user') {

                pmwn = PMW.util.escapeHTML(decodeURIComponent(pmwn.replace(/\+/g, ' ')));

                del('navauth');
                del('nav-auth-small');
                document.getElementById('navuser').setAttribute('title', pmwn);

                i('ulname', pmwn);
                i('ulname-toggled', pmwn);

                if (n) {
                    n.className += ' -loggedin';
                }
            }

            var la = PMW.util.readCookie('pmwla');
            if (la) {
                if (n) {
                    n.className += ' -su';
                }
            }
        }
    },
    /**
     * Returns either 'notloggedin', 'regular', 'designer', 'student' or 'teacher'.
     * @see Accounts.php
     * @returns {String}
     */
    getUserType: function () {
        var c = PMW.util.readCookie('pmwi2');
        if (typeof c === 'string') {
            var p = c.split('.');
            if (p.length > 1) {
                if (p[1] === 'premium' || p[1] === 'premium-plus') {
                    // Updating userinfo cookie for already logged in premium users.
                    // Changes made on: (04.01.2019)
                    // We need this code until there is no userinfo cookie left with value('premium' or 'premium-plus')

                    p[1] = 'regular';
                    PMW.util.setCookie('pmwi2', p.join('.'), 14);
                }
                return p[1];
            }
        }
        return 'notloggedin';
    },

    /**
     * Returns true if user is coming from some external source
     * @returns {boolean}
     */
    isReferrer: function () {
        return !document.referrer.includes(PMW.BASE_URL)
    },

    /**
     * Returns either 0 or 1 depending upon user is a suspected bot or not.
     * @returns {number}
     */
    isVerificationNeeded: function () {
        var c = PMW.util.readCookie('pmwi2');
        if (typeof c === 'string') {
            var p = c.split('.');
            if (p.length > 3) {
                return parseInt(p[3]);
            }
        }
        return 0;
    },

    /**
     * Returns the premium level of the user
     * @returns {Number}
     */
    getUserPremiumLevel: function () {
        let premiumLevel = PMW.getValueAtIndexFromSubscriptionDetailsCookie(1);
        if (premiumLevel === null) {
            return 0;
        }

        return parseInt(premiumLevel);
    },

    /**
     *
     * @returns {boolean}
     */
    isUserPremiumLevel0: function () {
        let premiumLevel = PMW.getValueAtIndexFromSubscriptionDetailsCookie(1);
        if (premiumLevel === null) {
            return true;
        }

        return parseInt(premiumLevel) === 0;
    },

    /**
     *
     * @returns {boolean}
     */
    isUserPremiumLevel1: function () {
        let premiumLevel = PMW.getValueAtIndexFromSubscriptionDetailsCookie(1);
        if (premiumLevel === null) {
            return false;
        }

        return parseInt(premiumLevel) === 1;
    },

    /**
     *
     * @returns {boolean}
     */
    isUserPremiumLevel2: function () {
        let premiumLevel = PMW.getValueAtIndexFromSubscriptionDetailsCookie(1);
        if (premiumLevel === null) {
            return false;
        }

        return parseInt(premiumLevel) === 2;
    },

    /**
     * Returns the trial status of the user
     * @returns {Number}
     */
    getUserTrialState: function () {
        let isTrial = PMW.getValueAtIndexFromSubscriptionDetailsCookie(3);
        if (isTrial === null) {
            return 0;
        }

        return parseInt(isTrial);
    },

    /**
     * Returns true if the current user is the member of a reseller client type team
     * @returns {Boolean}
     */
    isUserResellerClientTeamMember: function () {
        return PMW.getValueAtIndexFromSubscriptionDetailsCookie(2) === '2';
    },

    /**
     * Returns the number of months for the current user's subscription plan
     * @returns {number}
     */
    getUserSubscriptionPlanMonths: function () {
        let value = PMW.getValueAtIndexFromSubscriptionDetailsCookie(0);
        if (value === null) {
            value = 0;
        }

        return parseInt(value);
    },

    /**
     * Returns the value present at the specified index from the subscription details cookie.
     * @param {number} index
     * @return {string|null}
     */
    getValueAtIndexFromSubscriptionDetailsCookie: (index) => {
        const c = PMW.util.readCookie('pmwsd');
        if (typeof c === 'string') {
            let p = c.split('~');
            return p[index];
        }

        return null;
    },

    /**
     * Returns true if the current user is PAYG (i.e. not premium)
     * @return {boolean}
     */
    isPAYGUser: function () {
        return PMW.getUserPremiumLevel() === 0;
    },

    isUserPremium: function () {
        return PMW.getUserPremiumLevel() > 0;
    },

    /**
     * @returns {number}
     */
    getMaxMultipagePosterSelectionNumber: function () {
        if (window.PMW.isMultipageAvailable) {
            return PMW.MAX_NUMBER_OF_PAGES;
        }
        return 1;
    },

    /**
     * Returns user's eligible country code by reading from user's country code cookie
     *
     * @returns {string}
     */
    getUserEligibleCountryCode: function () {
        let cookieName = PMW.getUserId() === 'notloggedin' ? 'pmwaecc' : 'pmwuecc',
            cookieValue = PMW.util.readCookie(cookieName);

        if (cookieValue) {
            return cookieValue;
        } else {
            return 'US';
        }
    },

    /**
     * Returns id of the user if a user is logged in.
     * @see Accounts.php
     * @returns {Number|String}
     */
    getUserId: function () {
        var c = PMW.util.readCookie('pmwi2');
        if (typeof c === 'string') {
            var p = c.split('.');
            if (p.length > 1) {
                return parseInt(p[0]);
            }
        }
        return 'notloggedin';
    },

    /**
     * Whether user is logged in or not
     * @return {boolean}
     */
    isUserLoggedIn: function () {
        return PMW.getUserId() !== 'notloggedin';
    },

    toggleLoggedInClass: function () {
        if (PMW.isUserLoggedIn()) {
            $('html').addClass('-loggedin');
        }
    },
    toggleBannerClass: function () {
        if (PMW.isUserLoggedIn() || !PMW.isReferrer()) {
            $('html').addClass('-hide-banner');
        }
    },
    /**
     * Returns encrypted id of the user if a user is logged in.
     * @returns {*}
     */
    getUserHashedId: function () {
        var c = PMW.util.readCookie('pmwi2');
        if (typeof c === 'string') {
            var p = c.split('.');
            if (p.length > 4) {
                return p[4];
            }
        }
        return null;
    },
    /**
     * @returns {string|null}
     */
    getUserHashedEmail() {
        const c = PMW.util.readCookie('pmwuhe');
        if (typeof c === 'string') {
            return c;
        }
        return null;
    },
    /**
     * Returns anonymous user Id. If it has not been created yet then it is firstly created and then returned.
     * To be used when a user is not logged in.
     * @returns {string}
     */
    getAnonUserId: function () {
        if (this.anonUserId_ === '') {
            this.anonUserId_ = this.util.createRandomId();
        }
        return this.anonUserId_;
    },
    /**
     * Returns the persona id of the user if the cookie is available
     * @returns {string|null}
     */
    getUserPersonaId: function () {
        var c = PMW.util.readCookie('pmwpi');
        if (typeof c === 'string') {
            return c;
        }
        return null;
    },
    /**
     * Returns the current user's state in the sales funnel if the user info cookie is available.
     * @returns {string|null}
     */
    getUserSalesFunnelState: function () {
        let c = PMW.util.readCookie('pmwi2');
        if (typeof c === 'string') {
            let p = c.split('.');
            if (p.length > 5) {
                return p[5];
            }
        }
        return null;
    },
    /**
     * Returns true if the current user has just signed up (and false
     * otherwise) by checking for the presence of the new user cookie.
     * @returns {boolean}
     */
    isNewUser: function () {
        let c = PMW.util.readCookie('pmwnu');
        if (typeof c === 'string') {
            return c === '1';
        }
        return false;
    },
    /**
     * Deletes the new user cookie.
     */
    deleteNewUserCookie: function () {
        PMW.util.deleteCookie('pmwnu');
    },
    /**
     * Returns true if a cookie for educational users is present.
     * Used on all page tracked by Google Analytics.
     * @returns {Boolean}
     */
    isEDU: function () {
        var userType = this.getUserType();
        return (userType === 'teacher' || userType === 'student');
    },
    /**
     * When the PosterMyWall window is open within an iframe, we initiate a two way communication chain between
     * the iframe and the parent (plugin file).
     */
    sendPosterMakerMessageIframe: function () {
        if (this.isEmbedded()) {
            if (typeof PMW.plugin === 'undefined') {
                PMW.getJsAssetGroup('iframePlugin', function () {
                    if (typeof PMW.plugin !== 'undefined') {
                        PMW.plugin.initiateComWithIframe();
                    }
                }.bind(this));
            } else {
                PMW.plugin.initiateComWithIframe();
            }
        }
    },
    /**
     * Returns true if the current window is in an iframe, false otherwise.
     * @returns {boolean}
     */
    isEmbedded: function () {
        if (window.PMW.isStorybook()) {
            return false;
        }

        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    },

    isStorybook: function () {
        return window.location.href.indexOf('storybook.localpostermywall') > -1;
    },
    /**
     * Initialization code for the embedded UI. Hiding elements as well as removing URLs that lead a user out of the builder.
     */
    initEmbeddedModeUI: function () {
        if (this.isEmbedded()) {
            $('body').addClass('-embedded');
            $('.embedded-remove-url').attr('href', 'javascript:void(0);').attr('title', '');
        }
    },
    /**
     * Returns true if the current logged in user is a Teacher account.
     * @returns {boolean}
     */
    isTeacher: function () {
        return this.getUserType() === 'teacher';
    },

    /**
     * This function checks if the environment is local (served by Vite over native ESM). If so,
     * it waits for the specified global function to be available before executing the callback function.
     * This ensures that the necessary frontend file is loaded and all functions are available.
     */
    executeOnGlobalFunctionLoaded: function (gloablFnName, callback) {
        if (PMW.isLocal()) {
            // On local as files are served by vite over native ESM. Make sure globals/[filename].tsx file is loaded first so that all avaiable function are loaded
            PMW.waitForGlobalFunction(gloablFnName)
                .then(callback)
                .catch(function (e) {
                    console.error(e)
                })
        } else {
            callback();
        }
    },

    waitForGlobalFunction: function (fnName, timeout = 90000) {
        return new Promise((resolve, reject) => {
            const checkInterval = 50;
            const maxChecks = timeout / checkInterval;

            let checks = 0;

            const intervalId = setInterval(() => {
                if (window.PMW[fnName]) {
                    clearInterval(intervalId);
                    resolve();
                } else if (checks >= maxChecks) {
                    clearInterval(intervalId);
                    reject(new Error(`Timeout: ${fnName} not available. Refresh the page`));
                }
                checks++;
            }, checkInterval);
        });
    },
    /**
     * Returns true if the current logged in user is a Student account.
     * @returns {Boolean}
     */
    isStudent: function () {
        return this.getUserType() === 'student';
    },
    /**
     * Hides elements with .eduhide using the passed element as the parent
     * @param {jQuery} [ctx] Optionally pass a context for selectors
     */
    hideForEDU: function (ctx) {
        if (PMW.isEDU()) {
            if (!ctx) {
                ctx = document;
            }
            $('.eduhide', ctx).hide();
        }
    },
    /**
     * If the element is scrolled into view, this function returns true. Else false.
     *
     * @param {jQuery} element The element that's being checked for visibility in the viewport
     * @param {boolean} partialElement If it's true, true will be returned if even 2/3 of the element is in the view, not necessarily the whole of it.
     * @param {int} offset Adds the offset from the container width while calculating if the element is in view. Negative offset = smaller container than the original, and vice versa.
     * @returns {boolean}
     */
    isScrolledIntoView: function (element, partialElement, offset) {
        if (!offset) {
            offset = 0;
        }

        var win = $(window),
            elem = $(element);

        var viewport = {
            top: win.scrollTop(),
            left: win.scrollLeft(),
        };

        viewport.right = viewport.left + win.width();
        viewport.bottom = viewport.top + win.height();

        var bounds = elem.offset();
        bounds.right = bounds.left + elem.outerWidth();
        bounds.bottom = bounds.top + elem.outerHeight();

        var elemBottom = bounds.top + elem.height(),
            elemHeight = elem.height();

        if (partialElement) {
            elemHeight = elem.height() / 3;
        }

        if (bounds.bottom < viewport.top || bounds.top > viewport.bottom) {
            return false;
        }

        return (((elemBottom - (elemHeight - offset)) <= viewport.bottom) && !(viewport.right < bounds.left || viewport.left > bounds.right));
    },
    /**
     * If the current logged in user is a designer, this function removes DOM elements
     * that have the 'designer-toggle' class and makes hidden elements with that class
     * visible.
     * @param {Element} [ctx] Optionally pass a context for selectors.
     */
    toggleForDesigner: function (ctx) {
        if (PMW.getUserType() === 'designer') {
            if (!ctx) {
                ctx = document;
            }
            $('.designer-toggle:visible').remove();
            $('.designer-toggle', ctx).removeClass('hidden').show();
        }
    },

    /**
     * Adds class at document's body depending upon user premium level
     * @param ctx
     */
    setUserPremiumLevel: function (ctx) {
        if (!ctx) {
            ctx = document;
        }

        var userPremiumLevel = PMW.getUserPremiumLevel(),
            premiumClass;
        switch (userPremiumLevel) {
            case 1:
                premiumClass = 'premium-plan-one';
                break;
            case 2:
                premiumClass = 'premium-plan-two';
                break;
            default:
                premiumClass = 'non-premium';
        }

        $('body', ctx).removeClass('premium-plan-one premium-plan-two non-premium')
            .addClass(premiumClass);
    },
    /**
     * Hides content and adds redirects for certain links that should be hidden
     * from students. Returns true if a student is logged in.
     * @param {Element} [ctx]
     * @returns {boolean}
     */
    hideForStudent: function (ctx) {

        if (PMW.isStudent()) {
            if (!ctx) {
                ctx = document;
            }

            var a = $(ctx).find('a.student-redirect');
            $.each(a, function () {
                $(this).attr('href', PMW.util.site_url('edu/denied'));
            });
            $('.student-hide', ctx).removeClass('visible-desktop').hide();
            return true;
        }
        return false;
    },
    /**
     * Toggles visibility of a sliding panel that has .in and .out styles defined
     * in CSS by using these classes.
     * @param {jQuery} el The element to show or hide
     * @param {boolean} show True if the element should be shown, false if it is to
     * be hidden
     * @return {jQuery} The element that was passed
     */
    toggleInOut: function (el, show) {
        if (show && el.hasClass('out')) {
            el.removeClass('out').addClass('in');
        } else if (!show && el.hasClass('in')) {
            el.removeClass('in').addClass('out');
        }
        return el;
    },
    /**
     * Updates search forms to use background search if a student is logged in.
     * @param {Element} [ctx]
     */
    changeSearchForStudent: function (ctx) {
        if (!ctx) {
            ctx = $('#nav form').not('.js-list-item-form');
        }
        ctx.attr('action', PMW.util.site_url('posters/searchBackgrounds'));
    },
    /**
     * Returns true if the current page is the Poster Maker. False otherwise.
     * //TODO: This is mainly used for styling download/checkout modal in postermaker
     * //differently because gumby wasn't loaded there. This function should no longer be used
     * //and should be removed once we're using react editor
     * @deprecated
     * @returns {boolean}
     */
    isOldPosterMaker: function () {
        return (typeof PMConfig !== 'undefined');
    },

    isReactPosterMaker: function () {
        return window.isPosterMaker === true;
    },

    isPosterMaker: function () {
        return this.isOldPosterMaker() || this.isReactPosterMaker();
    },
    /**
     * Logs an error message in the server log file. Does not rely on jQuery for async server calls.
     * @param {String} t
     */
    log: function (t) {
        PMW.write(PMW.util.site_url('logerror/clientlog'), {
            message: encodeURIComponent(t),
        });
    },

    /**
     * Sends data to all online ads services for tracking
     * @param {String} eventType
     * @param {String|null} [orderType]
     * @param {String|null} [orderValue]
     * @param {String|null} [orderId]
     */
    trackOnlineAdsEvent: function (eventType, orderType, orderValue, orderId) {
        let data = {};
        if (orderType !== null) {
            data.orderType = orderType;
        }
        if (orderValue !== null) {
            data.orderValue = orderValue;
        }
        if (orderId !== null) {
            data.orderId = orderId;
        }
        PMW.gtm.trackOnlineAdsTrackingEvent(eventType, data);
    },
    /**
     * Expects the list item in a grid of poster template thumbnails. Checks if it
     * has the attribute set which implies that a view for this template has already
     * been recorded on the server.
     * @param {jQuery} li The grid item that contains the template to view
     * @returns {Boolean}
     */
    isNotAlreadyViewed: function (li) {
        return li.attr('data-viewed') != 1;
    },
    /**
     * This function sends the passed parameters to the server which increments views
     * for the poster, if applicable.
     * @param {int} id The numeric id, not hashedId.
     * @param {int} idcreator
     * @param {array} ignoredIds Ids of thumbnails that appear before this poster.
     * @param {string} pageType param to categorize page types e.g categories, search etc
     */
    incrementPosterView: function (id, idcreator, ignoredIds, pageType) {
        if (id !== null) {
            PMW.write(PMW.util.site_url('poster/viewed'), {
                'id': id,
                'idcreator': idcreator,
                'ignored[]': ignoredIds,
                'type': pageType,
            });
        }
    },
    /**
     * Increment views for templates
     * @param {array} templateIds Ids of thumbnails that appear before this poster.
     * @param {string} uri The uri of seen controller
     */
    incrementPosterSeenStat: function (templateIds, uri = 'poster/seen') {
        if (templateIds.length === 0) {
            return;
        }
        PMW.write(PMW.util.site_url(uri), {
            'templateIds': templateIds,
        });
    },
    /**
     * Sets an attribute on the li that marks it as viewed. Also returns an array of
     * ids of thumbnails preceding this one that haven't already been viewed.
     * @param {jQuery} li The grid item who's thumbnail is being viewed by the user
     * @param {string} filter A selector within the context of li for the element which
     * has the data-seq attribute. If it is li, pass an empty string.
     * @param {string} [attr='data-seq'] The attribute to use for the posterId
     * @returns {Array}
     */
    doThumbnailView: function (li, filter, attr) {
        if (typeof attr === 'undefined') {
            attr = 'data-seq';
        }

        var type = li.hasClass('video-item') ? '.video-item' : '.image-item';
        li.attr('data-viewed', 1);
        var sibs = li.prevAll(type + '[data-viewed!="1"]').attr('data-viewed', 1),
            ignoredIds = [];

        for (var i = 0; i < sibs.length; i++) {
            ignoredIds[ignoredIds.length] = $(filter.length > 0 ? $(filter, sibs[i]) : sibs[i]).attr(attr);
        }
        return ignoredIds;
    },
    /**
     * Shows the error message while increasing the size of the dialog smoothly. On subsequent errors, flashes the error message.
     * @param {jQuery} messageNode selected for the DIV with the HTML as the message.
     */
    showEmailDialogMessage: function (messageNode) {
        var emailSignupModal = $('#emailsignup-modal');

        if (!emailSignupModal.hasClass('-with-message')) {
            emailSignupModal.addClass('-with-message');
            messageNode.show(250);
        } else {
            messageNode.show(250);
            messageNode.addClass('animated flash')
                .one(PMW.util.animationEndEvents, function () {
                    messageNode.removeClass('animated flash');
                });
        }
    },
    /**
     * Displays a modal dialog requiring the user to enter their email address to be associated with their account
     * @param {function} cb
     * @param {string} [existingEmail='']
     * @param {string} [ssoName='']
     */
    showEmailRequiredDialog: function (cb, existingEmail, ssoName) {
        let txtEmailSignup,
            passwordField,
            errorEmptyPass,
            errorIncorrectPass,
            errorResponse,
            errorExistingEmail,
            modalContent,
            errorInvalidEmail,
            modalCloseBtn,
            esDlg,
            buttons = {};

        buttons[i18next.t('pmwjs_continue')] = function () {
            txtEmailSignup = $('#emailsignup');
            passwordField = $('.content .password-field');
            errorEmptyPass = $('.error-empty-password');
            errorIncorrectPass = $('.error-wrong-password');
            errorResponse = $('.error-response');
            errorExistingEmail = $('.error-existing-email');
            errorInvalidEmail = $('#invalidemail');
            modalContent = esDlg.find('.modal-content');
            modalCloseBtn = esDlg.find('.modal-btn-close');

            /**
             * Hides the 4 errors in the modal pertaining to incorrect passwords/email
             */
            function hideErrors() {
                errorIncorrectPass.hide();
                errorResponse.hide();
                errorEmptyPass.hide();
                errorExistingEmail.hide();
            }

            hideErrors();

            var email = $.trim(txtEmailSignup.val()),
                passwordTextField = $('#verify-password'),
                password = $.trim(passwordTextField.val());

            txtEmailSignup.on('keyup', function () {
                passwordField.hide();
                hideErrors();
                modalContent.removeClass('add-password-row');
            });

            if (PMW.isOldPosterMaker()) {
                PMW.onClick(modalCloseBtn, null, function () {
                    openExitConfirmationDialog();
                });
            }

            function openExitConfirmationDialog() {
                var buttons = {};
                buttons[i18next.t('pmwjs_cancel')] = function () {
                    PMW.closeModalDialog('emailsignup-confirmation-modal');
                };
                buttons[i18next.t('pmwjs_close')] = function () {
                    PMW.closeModalDialog('emailsignup-confirmation-modal');
                    PMW.closeModalDialog('emailsignup-modal');
                };
                var confirmDialog = PMW.openModalDialog('emailsignup-confirmation-modal', i18next.t('pmwjs_cancel_login_using_fb'), buttons, false, '-modal-250 -flex', false);
                confirmDialog.find('.content').html('<h5>' + i18next.t('pmwjs_sure_cancel_logging_in_with_fb') + '</h5>');
                confirmDialog.find('.close').hide();
            }

            if (email.length > 0) {
                // PMW.closeModalDialog('emailsignup-modal');
                // No need to verify password
                if (passwordField.is(':hidden')) {
                    PMW.write(PMW.util.site_url('authenticate/verifyUserEmailState'), {
                        'email': email,
                    }, function (r) {
                        if (r.status === 'success') {
                            $('#invalidemail').hide();
                            switch (r.data.state) {
                                case 'allow':
                                    cb(email);
                                    PMW.closeModalDialog('emailsignup-modal');
                                    hideErrors();
                                    break;
                                case 'ask-password':
                                    passwordField.show();
                                    modalContent.addClass('add-password-row');
                                    break;
                                case 'redirect':
                                    if (PMW.isPosterMaker()) {
                                        PMW.showEmailDialogMessage(errorExistingEmail);
                                        modalCloseBtn.show();
                                    } else {
                                        PMW.closeModalDialog('emailsignup-modal');
                                        modalContent.removeClass('add-password-row');
                                        window.location.href = PMW.util.site_url('authenticate/showlogin');
                                    }
                                    return;
                                case 'invalid':
                                    PMW.showEmailDialogMessage(errorInvalidEmail);
                                    passwordTextField.focus();
                                    break;
                            }
                        } else {
                            PMW.showEmailDialogMessage(errorResponse);
                            txtEmailSignup.focus();
                        }
                    });
                } else {
                    // now verify if password is entered
                    if (password.length > 0) {
                        // verify if password correct
                        PMW.write(PMW.util.site_url('authenticate/verifyUserEmailAndPassword'), {
                            'email': email,
                            'password': password,
                        }, function (r) {
                            if (r.status === 'success') {
                                if (r.data.match) {
                                    PMW.closeModalDialog('emailsignup-modal');
                                    cb(email, password);
                                } else {
                                    PMW.showEmailDialogMessage(errorIncorrectPass);
                                    passwordTextField.focus();
                                }
                            } else {
                                PMW.showEmailDialogMessage(errorResponse);
                                txtEmailSignup.focus();
                            }
                        });
                    } else {
                        PMW.showEmailDialogMessage(errorEmptyPass);
                        passwordTextField.focus();
                    }
                }
            } else {
                PMW.showEmailDialogMessage(errorInvalidEmail);
                txtEmailSignup.focus();
            }
        };

        let modalTitle = i18next.t('pmwjs_email_required'),
            modalClass = '',
            emailFieldLabel = i18next.t('pmwjs_enter_email_to_login'),
            emailFieldSpamFine = '<span class="fineprint">' + i18next.t('pmwjs_hate_spam_much_you_do') + '</span>',
            passwordFieldLabel = i18next.t('pmwjs_account_with_email_exists_enter_password', {ssoName: ssoName}),
            passwordFieldClass = 'hidden',
            hideCloseBtn = true;

        if (existingEmail !== 'undefined' && existingEmail !== '') {
            modalTitle = i18next.t('pmwjs_account_already_exists');
            modalClass = ' -long';
            emailFieldLabel = i18next.t('pmwjs_email');
            emailFieldSpamFine = '';
            passwordFieldLabel = i18next.t('pmwjs_password');
            passwordFieldClass = '';
            hideCloseBtn = false;
        }

        esDlg = PMW.openModalDialog('emailsignup-modal', modalTitle, buttons, false, '-modal-500 -flex' + modalClass, false);

        if (hideCloseBtn) {
            esDlg.find('.close').hide();
        }

        esDlg.find('.content').html(
            '<div id="invalidemail" class="danger alert body-lg-regular -top hidden">' + i18next.t('pmwjs_valid_email_address') + '</div>' +
            '<div id="nonssoaccount" class="danger alert body-lg-regular -top hidden">' + i18next.t('pmwjs_account_with_email_already_exists_use_email_password_to_link', {
                email: existingEmail,
                socialAccount: ssoName,
            }) + '</div>' +
            '<div class="field"><label for="emailsignup">' + emailFieldLabel + '</label><input type="email" class="text input" id="emailsignup" placeholder="' + i18next.t('pmwjs_enter_your_email_address') + '" autocapitalize="off" autocorrect="off" autocomplete="email" value="' + existingEmail + '">' + emailFieldSpamFine + '</div>' +
            '<div class="field password-field ' + passwordFieldClass + '"><label for="verify-password">' + passwordFieldLabel + '</label>' +
            '<input type="password" class="text input" id="verify-password" placeholder="' + i18next.t('pmwjs_enter_your_password') + '" autocapitalize="off" autocorrect="off" /></div>' +
            '<div class="error-empty-password danger alert hidden">' + i18next.t('pmwjs_please_enter_a_password') + '</div>' +
            '<div class="error-wrong-password danger alert hidden">' + i18next.t('pmwjs_incorrect_password') + '</div>' +
            '<div class="error-response danger alert hidden">' + i18next.t('pmwjs_error_try_again_reload_page') + '</div>' +
            '<div class="error-existing-email danger alert hidden">' + i18next.t('pmwjs_fb_account_already_associated_with_email') + '</div>');
    },
    /**
     * Displays a modal dialog requesting the user for an alternate email as the one they provided is already associated with an account.
     * @param {string} email
     * @param {function} cb
     */
    showAlternativeEmailRequiredDialog: function (email, cb) {
        var buttons = {};
        buttons[i18next.t('pmwjs_cancel')] = function () {
            PMW.closeModalDialog('alternative-email-modal');
        };
        buttons[i18next.t('pmwjs_continue')] = function () {
            txtEmail = aerDialog.find('.email');
            var email = $.trim(txtEmail.val());
            if (email.length > 0) {
                PMW.closeModalDialog('alternative-email-modal');
                cb(email);
            } else {
                PMW.showEmailDialogMessage($('#invalidemail'));
                txtEmail.focus();
            }
        };
        var txtEmail,
            aerDialog = PMW.openModalDialog('alternative-email-modal', i18next.t('pmwjs_alternative_email_required'), buttons, false, '-modal-550 -flex');

        aerDialog.find('.content').html('<p>' + i18next.t('pmwjs_account_with_email_exists_provide_alternative', {email: PMW.util.escapeHTML(email)}) + '</p><div class="field"><input type="email" class="text input email" placeholder="' + i18next.t('pmwjs_enter_your_email_address') + '" autocapitalize="off" autocorrect="off" autocomplete="email"/></div><p><a href="javascript:void(0)" class="existing-account-info">' + i18next.t('pmwjs_click_here_for_more_info') + '</a></p><div id="invalidemail" class="danger alert hidden">' + i18next.t('pmwjs_valid_email_address') + '</div>');
        PMW.onClick(aerDialog.find('.existing-account-info'), null, PMW.showExistingAccountDialog.bind(this, email));
    },
    /**
     * Displays a modal dialog giving the user more information about what they can do if their entered email address is already associated with an account
     * @param {string} email
     */
    showExistingAccountDialog: function (email) {
        var buttons = {};
        buttons[i18next.t('pmwjs_ok')] = function () {
            PMW.closeModalDialog('existing-account-modal');
        };
        var eaDialog = PMW.openModalDialog('existing-account-modal', i18next.t('pmwjs_account_already_exists'), buttons, false, '-modal-550 -modal-h-450 -flex');

        eaDialog.find('.content').html('<p>' + i18next.t('pmwjs_account_with_email_exists_provide_alternative', {email: PMW.util.escapeHTML(email)}) + '</p><p>' + i18next.t('pmwjs_if_forgot_password_use_forgot_your_password', {email: PMW.util.escapeHTML(email)}) + '</p><p>' + i18next.t('pmwjs_access_designs_another_account_use_share') + '</p><p>' + i18next.t('pmwjs_if_transfer_designs_contact_support', {href: PMW.util.site_url('user/feedbackHelpCenter')}) + '</p>');
    },
    /**
     * Informs user that they cannot make a purchase with the premium stock background they have used
     * @param {function} [onClose] function to run when user clicks 'close' link
     */
    premiumContentBlockMessage: function (onClose) {
        var idModal = 'premium-cannot-purchase-modal',
            buttons = {};
        buttons[i18next.t('pmwjs_close')] = function () {
            if (typeof onClose === 'function') {
                onClose();
            }
            PMW.closeModalDialog(idModal);
        };
        PMW.openModalDialog(idModal, i18next.t('pmwjs_cannot_purchase_premium_content'), buttons, false, PMW.isPosterMaker() ? '-flex' : '')
            .find('.content').html('<p>' + i18next.t('pmwjs_cannot_buy_only_getty_images') + '</p>');
    },
    /**
     * Informs user that their design has been resized to new poster dimensions
     */
    posterResizedWarningMessage: function (poster) {
        var idModal = 'poster-resized-warning-modal',
            buttons = {};
        buttons[i18next.t('pmwjs_ok')] = function () {
            PMW.closeModalDialog(idModal);
        };
        PMW.openModalDialog(idModal, i18next.t('pmwjs_poster_resized_warning'), buttons, true, PMW.isPosterMaker() ? '-flex' : '')
            .find('.content').html('<div class="icon-wrapper"><i class="icon-info"></i></div><p class="message">' + i18next.t('pmwjs_poster_resized_to_new_dimensions', {
            posterType: PMW.util.escapeHTML(poster.type.displayName),
            description: poster.type.description,
        }) + '</p>');
    },
    /**
     * @param {string} downloadUrl
     * @param {string} posterName
     * @param {string} extension
     */
    downloadPoster: function (downloadUrl, posterName, extension) {
        if (PMW.isMobileOS() && !PMW.isFirefox()) {
            fetch(downloadUrl).then(response => {
                return response.blob().then(blob => {
                    const timestamp = new Date().getTime();
                    const uniqueFilename = `${posterName}_${timestamp}.${extension}`;
                    if (PMW.isIOS() && navigator.canShare) {
                        const file = new File([blob], uniqueFilename, {type: blob.type});
                        if (navigator.canShare({files: [file]})) {
                            navigator.share({files: [file]}).catch(() => {
                            });
                            return;
                        }
                    }
                    const objectUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = objectUrl;
                    a.download = uniqueFilename;
                    document.body.appendChild(a);
                    a.click();
                    URL.revokeObjectURL(objectUrl);
                    document.body.removeChild(a);
                });
            });
        } else {
            window.location.href = downloadUrl;
        }
    },
    /**
     * Loads the required JS and CSS for opening the Download Dialog
     * @param {object} data contains required data for related to poster (hashedId, postertype ....)
     * @param {puremvc.Facade} [facade] Reference to the PureMVC facade if this dialog is being opened from the PosterMaker.
     */
    openDownloadDialog: function (data, facade) {
        if (data.panelToSelect === 'download') {
            PMW.openNewDownloadModal({
                id: 'download-panel',
                posterHashedId: data.idPosterHash,
                isOpenedInModal: true,
                isOpenedInEditor: false,
                isOpenedInPrintShop: false,
            })
            return;
        }
        PMW.util.require((typeof PMW.doOpenDownloadDialog === 'undefined'),
            'downloadDialog', true, function () {
                PMW.doOpenDownloadDialog(data, facade);
            });
    },
    /**
     * Loads the required JS and CSS for opening the Summary Dialog
     * @param {string} idPosterHash
     * @param {int} idProduct
     * @param {string} mode either 'publish' or 'download'
     * @param {function} callBack pay button handler
     * @param {function} [onError]
     * @param {function} [onClose]
     */
    openSummaryDialog: function (idPosterHash, idProduct, mode, callBack, onError, onClose) {
        PMW.setLoading(i18next.t('pmwjs_loading'));
        PMW.util.require(true,
            'summaryDialog', true,
            PMW.doOpenSummaryDialog.bind(this, idPosterHash, idProduct, mode, callBack, onError, onClose));
    },

    /**
     * Displays the Summary Dialog
     * @param {string} idPosterHash
     * @param {int} idProduct
     * @param {string} mode Either 'publish' or 'download'
     * @param {function} onPayButtonClick function to call when 'Pay Credits' button is clicked.
     * @param {function} onError
     * @param {function} onClose
     */
    doOpenSummaryDialog: function (idPosterHash, idProduct, mode, onPayButtonClick, onError, onClose) {
        PMW.write(PMW.util.site_url('cart/purchaseSummary'), {
            hid: idPosterHash,
            prodid: idProduct,
            mode: mode,
            isPosterMaker: PMW.isPosterMaker() ? 1 : 0,
        }).done(function (r) {
            PMW.setLoading(null);
            if (r.status === 'success') {
                var summaryDialog = PMW.openModalDialog('summary-dialog', (mode === 'download') ? i18next.t('pmwjs_pay_and_download') : i18next.t('pmwjs_pay_and_publish'), {}, true, '-auto');
                summaryDialog.find('.content').html(r.data);
                PMW.onClick(summaryDialog, '.pay-credits', function () {
                    $(this).find('.btn-continue-checkout').prop('disabled', true);
                    PMW.setLoading(i18next.t('pmwjs_processing'));
                    onPayButtonClick();
                });

                PMW.onClick(summaryDialog, '.btn-buy-credits', function () {
                    function creditPurchaseCallback(data) {
                        summaryDialog.find('.count.-credits').html(parseInt(data.credits));
                    }

                    PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(creditPurchaseCallback);
                });

                if (typeof onClose === 'function') {
                    PMW.onClick(summaryDialog, '.header .modal-btn-close', onClose);
                }

                if (window.initResponsiveToolTips) {
                    initResponsiveToolTips();
                }
            } else {
                if (typeof onError === 'function') {
                    onError(r.message);
                } else {
                    alert(r.message);
                }
            }
        });
    },

    /**
     * Loads the required JS and CSS for opening the invite collaborators dialog
     * @param idPoster
     * @param hideLoading
     */
    openCollaboratorsDialog: function (idPoster, hideLoading = false) {
        if (!hideLoading) {
            PMW.setLoading(i18next.t('pmwjs_loading'));
        }
        PMW.util.require((typeof PMW.openInviteDialog === 'undefined'),
            'inviteCollaborators', true,
            PMW.doOpenInviteCollaboratorsDialog.bind(PMW, idPoster));

    },

    /**
     * Displays the invite collaborators dialog
     * @param idPoster
     */
    doOpenInviteCollaboratorsDialog: function (idPoster) {
        PMW.openInviteDialog(idPoster);
    },
    /**
     * Initializes the script for the accessibility menu, and then opens it up.
     */
    initAccessibilityMenu: function () {
        var btn = $(this);
        btn.html(btn.attr('data-opening-text'));

        if (typeof PMW.openAccessibilityMenu === 'undefined') {
            PMW.getJsAssetGroup('accessibilityWidget', function () {
                PMW.openAccessibilityMenu();
            }.bind(this));
        } else {
            PMW.openAccessibilityMenu();
        }
    },
    /**
     * Binds events related to accessibility improvements.
     */
    bindAccessibilityEvents: function () {
        var el = document.getElementsByClassName('js-open-userway-accessibility-menu');
        if (el.length > 0) {
            for (let i = 0; i < el.length; i++) {
                el[i].addEventListener('click', PMW.initAccessibilityMenu);
            }
        }
    },
    /**
     * Constants defining currency codes
     */
    CURRENCY_CODE_USD: 'USD',

    /**
     * Constants defining feature names for premium upselling dialogs
     */
    PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_BRANDS: 'more-brands',
    PREMIUM_PLUS_ONLY_FEATURE_NAME_EDITOR_BRANDS: 'editorbrands',
    PREMIUM_PLUS_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS: 'socialaccountlimitpremium',
    PREMIUM_PLUS_ONLY_FEATURE_IS_CUSTOM_DOMAIN_ENABLED: 'customdomain',
    PREMIUM_ONLY_FEATURE_NAME_TEAMS: 'teams',
    PREMIUM_PLUS_ONLY_FEATURE_NAME_CONTENT_PLANNER: 'contentplanner',
    PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS: 'removebackground',
    PREMIUM_ONLY_FEATURE_NAME_AI_SUBTITLES: 'aisubtitles',
    PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE: 'texttoimage',
    PREMIUM_ONLY_FEATURE_NAME_AI_VIDEO: 'aivideo',
    PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH: 'texttospeech',
    PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH_AI_WRITER: 'texttospeechaiwriter',
    PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS: 'removevideobackground',
    PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK: 'spellcheck',
    PREMIUM_ONLY_FEATURE_NAME_SCHEDULE_POST: 'scheduling',
    PREMIUM_ONLY_FEATURE_NAME_EMAIL_SENDS: 'emailsends',
    PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT: 'customfonts',
    PREMIUM_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS: 'socialmediaaccounts',
    PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_PERFORMANCE: 'emailperformance',
    PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_AUDIENCE: 'emailaudience',
    PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN: 'emailcampaigns',
    PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION: 'aicaptions',
    PREMIUM_ONLY_FEATURE_SOCIAL_POST_METRICS: 'socialpostmetrics',
    PREMIUM_ONLY_FEATURE_NAME_EMAIL_TEMPLATE_DOWNLOAD: 'emailtemplatedownload',
    PREMIUM_ONLY_FEATURE_NAME_CREDITS: 'credits',
    PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING: 'multiposting',
    PREMIUM_ONLY_FEATURE_NAME_POST_DOWNLOAD_SOCIAL_MEDIA_SIZES: 'postdownloadSMsizes',
    PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT: 'airewriteemailcampaigntextcomponentscontent',
    PREMIUM_ONLY_FEATURE_NAME_BRANDS: 'brands',
    PREMIUM_ONLY_FEATURE_NAME_MORE_EVENTS: 'moreevents',
    PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_EVENTS: 'moreeventspremiumplus',
    PREMIUM_PLUS_ONLY_FEATURE_EVENTS_REMOVE_WATERMARK: 'eventsremovewatermark',
    PREMIUM_ONLY_FEATURE_NAME_EVENTS_AI_WRITER: 'eventsaiwriter',
    PREMIUM_ONLY_FEATURE_NAME_EVENTS_GUESTS_INFORMATION: 'eventsguestinformation',
    PREMIUM_PLUS_ONLY_FEATURE_NAME_RECURRING_EVENTS: 'recurringevents',
    PREMIUM_ONLY_FEATURE_NAME_EVENTS_LIMITED_REGISTRATONS: 'eventslimitedregistrations',
    PREMIUM_ONLY_FEATURE_NAME_EVENTS_VIEW_ANALYTICS: 'eventsviewanalytics',
    PREMIUM_ONLY_FEATURE_NAME_EVENTS_REMINDERS: 'eventsreminders',
    FEATURE_NAME_POST_FREE_DOWNLOAD: 'postdownload',
    PREMIUM_ONLY_FEATURE_NAME_EVENT_ONE_CLICK_PROMOTION: 'eventoneclickpromo',
    PREMIUM_ONLY_UNLIMITED_PAGES_FEATURE: 'unlimitedpages',
    PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGN_AI_REDESIGN: 'emailcampaignairedesign',
    PREMIUM_ONLY_FEATURE_NAME_DESIGN_EDITOR_AI_REWRITE: 'designeditorairewrite',
    PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_CREATE_MORE: 'aicreatemore',
    PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_KEEP_CREATING: 'aikeepcreating',
    PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_AUDIO_INPUT: 'aichatvoiceinput',

    /**
     * Returns the GA4 custom dimension mapping for a premium feature
     * @param {string} featureName
     * @return {string}
     */
    getGA4TrackingPremiumEventDimension: function (featureName) {
        const GA4_TRACKING_PREMIUM_EVENT_DIMENSIONS = {
            [this.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS]: 'backgroundremoval_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS]: 'videobackgroundremoval_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_TEAMS]: 'teams_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT]: 'customfonts_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EMAIL_SENDS]: 'emailsends_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_SCHEDULE_POST]: 'schedulingposts_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_NAME_CONTENT_PLANNER]: 'contentplanner_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK]: 'spellcheck_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_CREDITS]: 'credits_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN]: 'emailcampaigns_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_BRANDS]: 'brands_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_BRANDS]: 'more-brand-kits-dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_IS_CUSTOM_DOMAIN_ENABLED]: 'customdomains_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_PERFORMANCE]: 'emailcampaignsperformance_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_AUDIENCE]: 'emailcampaignsaudience_dialog',
            [this.PREMIUM_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS]: 'socialmediaaccountspayg_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS]: 'socialmediaaccountspremium_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EMAIL_TEMPLATE_DOWNLOAD]: 'emailtemplatedownload_dialog',
            [this.PREMIUM_ONLY_FEATURE_SOCIAL_POST_METRICS]: 'socialpostmetrics_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION]: 'aicaptions_dialog',
            [this.PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING]: 'multiposting_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_POST_DOWNLOAD_SOCIAL_MEDIA_SIZES]: 'socialmediasizedownload_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT]: 'airewriteemailcampaigntextcomponentscontent_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_MORE_EVENTS]: 'moreevents_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_EVENTS]: 'moreeventspremiumplus_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_EVENTS_REMOVE_WATERMARK]: 'eventsremovewatermark_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENTS_AI_WRITER]: 'eventsaiwriter_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENTS_GUESTS_INFORMATION]: 'eventsguestinformation_dialog',
            [this.PREMIUM_PLUS_ONLY_FEATURE_NAME_RECURRING_EVENTS]: 'recurringevents_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENTS_LIMITED_REGISTRATONS]: 'eventslimitedregistrations_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENTS_VIEW_ANALYTICS]: 'eventsviewanalytics_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENTS_REMINDERS]: 'eventsreminders_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EVENT_ONE_CLICK_PROMOTION]: 'eventoneclickpromotion_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_SUBTITLES]: 'aisubtitles_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE]: 'texttoimage_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_VIDEO]: 'aivideo_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH]: 'texttospeech_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH_AI_WRITER]: 'texttospeechaiwriter_dialog',
            [this.FEATURE_NAME_POST_FREE_DOWNLOAD]: 'postdownload_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGN_AI_REDESIGN]: 'emailcampaignairedesign_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_DESIGN_EDITOR_AI_REWRITE]: 'designeditorairewrite_dialog',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_CREATE_MORE]: 'aicreatemore_dialogue',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_KEEP_CREATING]: 'aikeepcreating_dialogue',
            [this.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_AUDIO_INPUT]: 'aichatvoiceinput_dialogue',
            [this.PREMIUM_ONLY_UNLIMITED_PAGES_FEATURE]: 'unlimitedpages_dialog',
        };

        return GA4_TRACKING_PREMIUM_EVENT_DIMENSIONS[featureName];
    },

    /**
     * Constants defining intro dialogs for features
     */
    INTRO_FEATURE_NAME_CONTENT_PLANNER: 'intro-contentplanner',

    /**
     * Constants for cookies
     */
    PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE: 'premiumUpsellDialogShown',
    PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE_EXPIRATION_MINUTES: 30,

    /**
     * Displays a dialog telling the user the feature they were trying to use is available only for Premium Subscribers
     * @param {string} featureName The name of the feature that is unavailable to PAYG users (used as UTM source for GA tracking purposes)
     * @param {Function | undefined} onDialogLoaded
     * @param {boolean} shouldOpenInNewTab
     */
    showPremiumOnlyFeatureDialog: async function (featureName, onDialogLoaded, shouldOpenInNewTab = false) {
        if (typeof featureName !== 'string') {
            featureName = '';
        }

        let isUserEligibleForTrial = false;
        if (PMW.isUserLoggedIn()) {
            await PMW.readLocal('premium/isUserEligibleForTrial').then(function (response) {
                isUserEligibleForTrial = parseInt(response.isUserEligibleForTrial);
            });
        }

        PMW.openDialogV2('premium-only-dialog', '-standard', PMW.util.site_url('premium/premiumOnlyDialog'), {
            featureName: featureName,
            isEligibleForFreeTrial: PMW.premiumDialogs.isEligibleForFreeTrial(false) && isUserEligibleForTrial,
            shouldOpenInNewTab: shouldOpenInNewTab,
        }, function () {
            PMW.initPremiumOnlyDialog();
            if (typeof onDialogLoaded === 'function') {
                onDialogLoaded();
            }
        }, false, '.premium-dialog-custom-close, .premium-marketing-btn, .premium-custom-cross-btn', true);
    },
    initPremiumOnlyDialog: function () {
        const featureName = $('.js-premium-marketing-modal').attr('data-feature-name');
        if (featureName) {
            $('.js-premium-marketing-btn').one('click', function () {
                const t = $(this);
                if (t.length > 0 && t.parents('.js-premium-marketing-modal')) {
                    if (typeof PMW.gtm !== undefined && t.is('[ga4-click-name]')) {
                        PMW.gtm.trackGA4CustomClick(t.attr('ga4-click-name'));
                    }

                    const featureName = t.parents('.js-premium-marketing-modal').attr('data-feature-name');
                    if (featureName) {
                        window.dispatchEvent(new CustomEvent(PMW.getPremiumUpsellClickEvent(featureName), {}));
                    }
                }
            });

            $('.js-premium-marketing-secondary-btn').one('click', function (e) {
                e.preventDefault();
                const el = $(e.currentTarget);
                if (typeof PMW.gtm !== undefined && el.is('[ga4-click-name]')) {
                    PMW.gtm.trackGA4CustomClick(el.attr('ga4-click-name'));
                }

                if (el.attr('href')) {
                    window.location.href = el.attr('href');
                }
            });

            if (PMW.isIOSApp()) {
                $('.js-premium-trial-btn').addClass('_hidden');
            } else {
                $('.js-premium-trial-btn').one('click', function (e) {
                    PMW.premiumDialogs.openTrialDialog(false, null, null, featureName);
                    PMW.closeModalV2('premium-only-dialog');
                });
            }

            $('.js-view-plans-cta').one('click', function (e) {
                PMW.premiumDialogs.openPricingDialog(featureName);
                PMW.closeModalV2('premium-only-dialog');
            });

            $('.close-modal-v2').one('click', function () {
                PMW.closeModalV2('premium-only-dialog');
            });

            PMW.setCookieForUpsellDialogShown(featureName);

            const video = document.querySelector('.js-ad-graphic');
            if (!video) {
                return;
            }

            if (video.getAttribute('poster')) {
                video.classList.add('-loaded');
            } else {
                video.addEventListener('loadeddata', () => {
                    video.classList.add('-loaded');
                });
            }
        }
    },
    setCookieForUpsellDialogShown: function (featureName) {
        const upsellDialogGA4Event = this.getGA4TrackingPremiumEventDimension(featureName);
        if (!upsellDialogGA4Event) {
            return;
        }

        const expirationDays = this.PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE_EXPIRATION_MINUTES / (24 * 60);
        PMW.util.setCookie(this.PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE, upsellDialogGA4Event, expirationDays);
    },
    getPremiumUpsellClickEvent: function (featureName) {
        return 'premiumui:' + featureName;
    },
    getEventDialogCloseEvent: function () {
        return 'EVENT_DIALOG_CLOSE';
    },
    showPostFreeDownloadDialog: function (posterHashedId) {
        if (PMW.isStudent()) {
            return;
        }

        PMW.setCookieForUpsellDialogShown(this.FEATURE_NAME_POST_FREE_DOWNLOAD);

        PMW.openDialogV2('premium-post-free-download-dialog', '-standard',
            PMW.util.site_url('premium/premiumPostFreeDownloadDialog'),
            {posterHashedId}, null, true, '', true);
    },

    showEventsPostFreeDownloadDialog: function () {
        if (PMW.isStudent()) {
            return;
        }

        PMW.setCookieForUpsellDialogShown(this.FEATURE_NAME_POST_FREE_DOWNLOAD);

        PMW.openDialogV2('premium-only-dialog', '-standard js-events-post-download-dalog', PMW.util.site_url('premium/eventsUpsellPostDownloadDialog'), {}, function () {
            PMW.initPremiumOnlyDialog();
            $('.js-events-post-download-dalog').find('.js-create-event-cta').on(PMW.click, null, function () {
                $(this).addClass('-loading');
                PMW.gtm.trackGA4CustomEvent(PMW.gtm.EVENTS_CREATE_CLICK_GA_EVENT);
                PMW.gtm.trackGA4CustomEvent(PMW.gtm.EVENTS_POST_DOWNLOAD_DIALOG_GA_EVENT);
            });
        }, false, '', true);
    },

    /**
     * Displays the intro dialog for a premium feature
     * @param {string} featureName The name of the feature that is unavailable to PAYG users
     */
    showPremiumIntroFeatureDialog: function (featureName) {
        if (typeof featureName !== 'string') {
            featureName = '';
        }

        PMW.openDialogV2('premium-intro-dialog', '', PMW.util.site_url('premium/premiumIntroDialog'), {
            featureName: featureName,
        }, null, true, '.premium-dialog-custom-close, .premium-marketing-btn, .premium-custom-cross-btn', true);
    },
    /**
     * Displays a dialog telling the user the feature they were trying to use is available only for Premium Subscribers and a brief walk through of feature
     * @param {string} featureName The name of the feature that is unavailable to PAYG users for GA tracking purposes
     */
    showPremiumWalkthroughDialog: function (featureName = '') {
        let id = 'premium-walkthrough-dialog';

        function onContentLoad() {
            setBrandsWalkthroughCookie();
            PMW.onClick($(`#${id}`), '.js-premium-cta-container', function () {
                let activePanel = parseInt($(this).find('.js-premium-next')[0].value) + 1,
                    maxPanels = $(this).attr('data-value');
                if (activePanel < maxPanels) {
                    $('.js-premium-info-container').addClass('_hidden').removeClass('fadeIn');
                    $('.js-premium-graphic-container').addClass('_hidden').removeClass('fadeIn');
                    $('.js-premium-container-' + activePanel).removeClass('_hidden').addClass('fadeIn');
                } else {
                    PMW.closeModalV2.call(this, id);
                }
            });
            PMW.onClick($(`#${id}`), '.js-back-button', function () {
                let activePanel = parseInt($(this).find('.js-premium-back')[0].value) - 1;
                $('.js-premium-info-container').addClass('_hidden').removeClass('fadeIn');
                $('.js-premium-graphic-container').addClass('_hidden').removeClass('fadeIn');
                $('.js-premium-container-' + activePanel).removeClass('_hidden').addClass('fadeIn');
            });
        }

        function setBrandsWalkthroughCookie() {
            PMW.util.setCookie(postermywall.Core.COOKIE_BRANDS_WALKTHROUGH_DIALOG, '1', 365);
        }

        PMW.openDialogV2(id, '', PMW.util.site_url('premium/premiumWalkthroughDialog'), {featureName: featureName}, onContentLoad, false, '.premium-custom-cross-btn', false);
    },
    /**
     * Initializes the sticky nav to update styles when the page is scrolled.
     */
    initStickyNav: function () {
        var header = document.querySelector('header');

        // Since the header height is 60px, add the styles once the page has been scrolled 60px
        if (typeof IntersectionObserver !== 'undefined') {
            var sectionOneOptions = {
                rootMargin: '60px 0px 0px 0px',
                threshold: 1
            };

            var sectionOneObserver = new IntersectionObserver(function (
                    entries,
                    sectionOneObserver,
                ) {
                    entries.forEach(function (entry) {
                        var nav = document.getElementById('nav');
                        if (entry.isIntersecting) {
                            nav.classList.remove('-scrolled', '-sticky', 'navFadeIn');
                        } else {
                            nav.classList.add('navFadeIn', '-sticky', '-scrolled');
                        }
                    });
                },
                sectionOneOptions);

            sectionOneObserver.observe(header);
        }
    },

    /**
     * Initialises the sticky div.
     *
     * @param {jQuery} stickyDiv The sticky div selector.
     * @param {string} classNames The class that needs to be applied on the div once it sticks.
     * @param {Object} opts Optional parameter object.
     * @param {int} opts.topDistance Distance from the top of the screen where the div should stick. By default, sticks to the top of the page.
     * @param {jQuery} opts.finishDiv Section that should remove the sticky div class. By default, stays sticky till the end of the page.
     */
    initStickyDiv: function (stickyDiv, classNames, opts = {}) {
        let topDistance = opts.topDistance,
            finishDiv = opts.finishDiv,
            iOSObserverNavbar = null;

        if (typeof topDistance !== 'number') {
            topDistance = 0;
        }
        if (typeof finishDiv !== 'object') {
            finishDiv = null;
        }

        if (PMW.isIOS()) {
            iOSObserverNavbar = $('<div id="observer-navbar"></div>');
            iOSObserverNavbar.insertBefore(stickyDiv);
        }

        toggleStickyClass();
        window.addEventListener('scroll', PMW.debounce(toggleStickyClass, 10), {passive: true});

        function toggleStickyClass() {
            let offset = stickyDiv.height() + topDistance,
                hasFinalSectionReached = finishDiv && finishDiv[0].getBoundingClientRect().top <= offset;

            if (iOSObserverNavbar) {
                stickyDiv.toggleClass(classNames, Math.floor(iOSObserverNavbar[0].getBoundingClientRect().y) <= topDistance && !hasFinalSectionReached);
            } else {
                stickyDiv.toggleClass(classNames, Math.floor(stickyDiv[0].getBoundingClientRect().y) <= topDistance && !hasFinalSectionReached);
            }
        }
    },

    /**
     * Appends a cache-bust version to a URL. Defaults to PMW.VERSION (PMW_JS_VERSION,
     * manually bumped) for backward compatibility. Pass an explicit version to use
     * a content-derived hash like PMW.CARABINER_VERSION or PMW.WEBPACK_VERSION so
     * the bust value tracks asset content mechanically.
     * @param {string} url
     * @param {string|number} [version=PMW.VERSION]
     * @returns {string}
     * @private
     */
    addVersion: function (url, version) {
        const urlObj = new URL(url);
        urlObj.searchParams.set('_', version !== undefined ? version : PMW.VERSION);
        return urlObj.toString();
    },

    /**
     * Loads js from asset group and executes the callback
     * @param {string} assetGroupName
     * @param {function} successCallBack success callback
     * @param {function} [failCallBack=null] fail callback
     * @param {function} [alwaysCallBack=null] fail callback
     */
    getJsAssetGroup: function (assetGroupName, successCallBack, failCallBack, alwaysCallBack) {
        if (typeof failCallBack === 'undefined') {
            failCallBack = null;
        }

        if (typeof alwaysCallBack === 'undefined') {
            alwaysCallBack = null;
        }

        var url = this.addVersion(PMW.util.site_url('asset/getJsAssetGroup?assetgroup=' + assetGroupName), PMW.CARABINER_VERSION);
        return $.ajax({
            dataType: 'script',
            cache: true,
            url: url,
        }).done(function () {
            if (typeof successCallBack === 'function') {
                successCallBack();
            }
        }).fail(function (r) {
            console.log(r);
            if (typeof failCallBack === 'function') {
                failCallBack();
            } else {
                alert(i18next.t('pmwjs_problem_check_internet_connection_reload'));
            }
        }).always(function () {
            if (typeof alwaysCallBack === 'function') {
                alwaysCallBack();
            }
        });
    },

    /**
     * Loads js and css from asset group and executes a call back
     * @param {string} assetGroupName
     * @param {function} cb callback
     */
    getJsAndCssAssetGroup: function (assetGroupName, cb) {
        $.when($.ajax({
            dataType: 'script',
            cache: true,
            url: this.addVersion(PMW.util.site_url('asset/getJsAssetGroup?assetgroup=' + assetGroupName), PMW.CARABINER_VERSION),
        }), $.ajax({
            cache: true,
            url: this.addVersion(PMW.util.site_url('asset/getCssAssetGroup?assetgroup=' + assetGroupName), PMW.CARABINER_VERSION),
        }))
            .then(function (data, css) {
                $('<style>' + css + '</style>').appendTo('head');
                if ($.isFunction(cb)) {
                    cb();
                }
            })
            .fail(function () {
                alert(i18next.t('pmwjs_problem_check_internet_connection_reload'));
            });
    },
    /**
     * Adds call-to-action buttons to the modal specified. They're appended in the footer.
     *
     * @param {object} buttons The buttons to add
     * @param {object} modal The modal on which we want to add the buttons
     */
    initButtonsOnDialog: function (buttons, modal) {
        // create the buttons and set up their click events
        $.each(buttons, function (name, props) {
            modal.find('.footer .primary-actions').append(PMW.createButtonElement(name, props));
        });
    },

    /**
     * Creats html, attaches handler and returns the Jquery element for button
     * @param {string} name
     * @param {object|function} props
     * @returns {jQuery.fn.init|jQuery|HTMLElement}
     */
    createButtonElement: function (name, props) {
        var btn = $('<div class="btn primary medium"><button>' + name + '</button></div>');
        if (typeof props === 'function') {
            PMW.onClick(btn, null, props);
        } else if (typeof props === 'object') {
            if (typeof props.ch === 'function') {
                PMW.onClick(btn, null, props.ch);
            }

            if (typeof props.classes === 'string') {
                btn.addClass(props.classes);
            }

            if (typeof props.size === 'string') {
                btn.removeClass('medium');
                btn.addClass(props.size);
            }

            if (typeof props.type === 'string') {
                switch (props.type) {
                    case 'secondary':
                    case 'link':
                        btn.removeClass('btn-primary primary');
                        btn.addClass('secondary btn-secondary');

                        break;
                    case 'danger':
                        btn.removeClass('btn-primary primary');
                        btn.addClass('danger btn-danger');
                        break;
                }
            }
        }
        return btn;
    },
    /**
     * Adds a back button to the top left side of the modal
     *
     * @param {jQuery} modal
     * @param {ModalTopAction} backAction
     */
    addBackButtonToModal: function (modal, backAction) {
        PMW.removeBackButtonToModal(modal);

        var backBtn = '<div class="modal-header-action back ' + backAction.classes + '" title="' + i18next.t('pmwjs_back') + '"><i class="icon icon-arrow-left action-icon"></i></div>';
        modal.find('.header .title').addClass('-has-back').prepend(backBtn);
        PMW.onClick(modal.find('.header .title .back'), null, backAction.callback);
    },
    /**
     * Removes the back button
     *
     * @param {jQuery} modal
     */
    removeBackButtonToModal: function (modal) {
        modal.find('.modal-header-action.back').remove();
        modal.find('.header .title').removeClass('-has-back');
    },
    /**
     * Adds the given top actions to the modal dialog
     *
     * @param {ModalTopAction[]} topActions
     * @param {jQuery} modal
     */
    addTopActionsToModal: function (topActions, modal) {
        // add the .header section links, if any
        if (topActions.length > 0) {

            if (modal.find('.actions-responsive').length === 0) {
                var responsiveSettings = $('<div class="modal-header-actions modal-actions actions-responsive">' +
                    '<a href="javascript:void(0)" class="modal-header-action ">' +
                    '<i class="icon icon-menu-dots action-icon" title="Options"></i>' +
                    '</a>' +
                    '<div class="responsive-settings-container">' +
                    '</div>' +
                    '</div>');

                modal.find('.header .top-actions-bar').append(responsiveSettings);

                PMW.onClick($('.actions-responsive'), null, function () {
                    $(this).toggleClass('-open');
                });
            }

            if (modal.find('.top-actions-bar > .modal-header-actions').length === 0) {
                modal.find('.header .top-actions-bar').append($('<div class="modal-header-actions"></div>'));
            }

            /**
             * @param {ModalTopAction} topAction
             */
            $.each(topActions, function (i, topAction) {
                var actionItem = '',
                    iconClass = topAction.icon ? '-has-icon' : '';

                if (topAction.icon) {
                    actionItem = '<i class="icon ' + topAction.icon + ' action-icon" title="' + topAction.label + '"></i>' +
                        '<span class="action-label">' + topAction.label + '</span>';
                } else {
                    actionItem = '<span class="action-label">' + topAction.label + '</span>';
                }

                var h = $('<a href="javascript:void(0)" class="modal-header-action ' + iconClass + '">' +
                    actionItem +
                    '</a>');


                var hClone = h.clone();
                modal.find('.header .modal-header-actions:not(.actions-responsive)')
                    .addClass('-multiple-actions')
                    .append(h);

                modal.find('.responsive-settings-container').append(hClone);
                h.addClass(topAction.classes);
                hClone.addClass(topAction.classes);

                PMW.onClick(h, null, topAction.callback);
                PMW.onClick(hClone, null, topAction.callback);
            });

            this.monitorAndUpdateModalTopActionChange(modal);
        }
    },
    /**
     * Using MutationObserver, monitors if top actions are added/removed and adds
     * the appropriate classes to the modal
     * @param modal
     */
    monitorAndUpdateModalTopActionChange: function (modal) {
        var target = modal.find('.top-actions-bar .modal-header-actions'),
            targetNode = target.get(0),
            monitorModalTopActionChange = function () {
                let t = $(this);
                if (t.find('.top-actions-bar .modal-header-actions:not(.actions-responsive) .modal-header-action:visible').length > 1) {
                    target.addClass('-multiple-actions');
                } else {
                    target.removeClass('-multiple-actions');
                }
            }.bind(modal);

        if (targetNode) {
            var observer = PMW.attachAndGetMutationObserverToElement(targetNode, monitorModalTopActionChange, {
                childList: true,
                attributes: true,
            });
            modal.data('topActionObserver', observer);
        }
    },
    /**
     * Opens a modal dialog. Modal dialogs trigger the following custom events:
     * - 'modal:close' is triggered when the modal is closed, after the close animation has kicked off.
     * @param {string} idModal ID to use for the modal dialog
     * @param {string} [title] Title of the modal dialog
     * @param {object} [buttons] The key is set as the name of the button, the value can either be a function, which will be the
     * click handler or an object with keys:
     *  ch: {function} Click handler of the button
     *  classes: {string} CSS class(es) to add to the button
     * @param {boolean} [destroyOnClose=false] Pass as TRUE to destroy the modal on Close instead of just hiding
     * @param {string} [modalClass] Any classes to add to the .modal element
     * @param {boolean} [experimental=false] Pass as TRUE to use the experimental modal dialog which doesn't require JS for sizing or centering
     * @param {ModalTopAction[]} topActions An array of ModalTopAction objects
     * @returns {jQuery} The DOM element wrapped in jQuery that has id 'idModal'.
     */
    openModalDialog: function (idModal, title, buttons, destroyOnClose, modalClass, experimental, topActions) {
        if (typeof destroyOnClose === 'undefined') {
            destroyOnClose = false;
        }

        if (typeof topActions === 'undefined') {
            topActions = [];
        }

        var html = $('html'), body = $('body');

        body.addClass('-open-modal');

        if (typeof experimental === 'undefined' || html.hasClass('oldie') || html.hasClass('ie9')) {
            experimental = false;
        }

        if (typeof modalClass === 'undefined') {
            modalClass = '';
        }

        var modal, mc;
        // Ensure that only one modal window with this id is displayed
        if (typeof PMW.modals[idModal] === 'undefined') {
            PMW.modals[idModal] = destroyOnClose ? 'open-destroy' : 'open';
            body.append($('<div class="modal modal-open ' + (experimental ? 'vertical-align-wrap' : '') + '" id="' + idModal + '">' + (experimental ? '<div class="vertical-align">' : '') + '<div class="modal-content"><div class="header"><div class="title"><h2>' + title + '</h2></div><div class="top-actions-bar"><div class="modal-header-actions"><a href="javascript:void(0)" class="modal-header-action close modal-btn-close"><i class="icon icon-close" title="' + i18next.t('pmwjs_close') + '"></i></a></div></div></div><div class="content"><progress class="icon-circular-loader js-modal-loader modal-loader -medium _unmargin"></progress></div></div>' + (experimental ? '</div>' : '') + '</div>'));

            modal = $('#' + idModal).addClass(modalClass);

            this.animateOpenModalDialogContent(modal, MODAL_ANIMATION_TYPE_OPEN);
            this.addTopActionsToModal(topActions, modal);
            this.bindModalCloseEvents(idModal);

            // add the .footer section if there are buttons to be displayed
            if (!$.isEmptyObject(buttons)) {
                var footer = $('<div class="footer vertical-align-wrap"><div class="vertical-align"><div class="primary-actions"></div></div></div>');
                mc = modal.find('.modal-content');
                mc.append(footer);

                this.initButtonsOnDialog(buttons, modal);
            }

            modal.find('.close').on('click', this.closeModalDialog.bind(this, idModal));
        } else if (PMW.modals[idModal] === 'closed') {
            modal = $('#' + idModal);
            modal.addClass('modal-open');
            this.animateOpenModalDialogContent(modal, MODAL_ANIMATION_TYPE_OPEN);
        } else if (PMW.modals[idModal] === 'closing') {
            modal = $('#' + idModal);
            // TODO come back to this one day and use Deferreds
            PMW.queuedOpenModal = PMW.openModalDialog.bind(this, idModal, title, buttons, destroyOnClose);
        } else {
            modal = $('#' + idModal);
        }

        // Bring the dialog to the front
        PMW.bringToFrontOfModals(modal);
        this.monitorAndUpdateModalTopActionChange(modal);
        return modal;
    },
    /**
     * Checks if the given modal has the -full-height class added to it
     *
     * @param {jQuery} modal
     * @param {string} animationType
     * @returns {string}
     */
    getModalAnimationClass: function (modal, animationType) {
        if (PMW.doesModalNeedAnimationReversal(modal)) {
            if (animationType === MODAL_ANIMATION_TYPE_OPEN) {
                return 'animate-modal-dialog-open -reverse';
            } else {
                return 'animate-modal-dialog-close -reverse';
            }
        } else {
            if (animationType === MODAL_ANIMATION_TYPE_OPEN) {
                return 'animate-modal-dialog-open';
            } else {
                return 'animate-modal-dialog-close';
            }
        }
    },
    /**
     * Returns true if the modal has class (-full-height class currently) which require it to be loaded with reverse animation (slide down to up)
     *
     * @param {jQuery} modal
     * @returns {boolean}
     */
    doesModalNeedAnimationReversal: function (modal) {
        return modal.hasClass('-full-height');
    },
    /**
     * Animates the opening of the modal dialog
     *
     * @param {jQuery} modal
     * @param {string} animationType
     */
    animateOpenModalDialogContent: function (modal, animationType) {
        let modalAnimationClass = this.getModalAnimationClass(modal, animationType),
            modelContent = modal.find('.modal-content');
        modelContent.addClass(modalAnimationClass)
            .one(PMW.util.animationEndEvents, function () {
                modelContent.removeClass(modalAnimationClass);
            });
    },
    /**
     * @param {string} idModal The ID of the modal to bind the close events on
     * @param {function} customCloseModalFunction
     */
    bindModalCloseEvents: function (idModal, customCloseModalFunction) {
        if (typeof customCloseModalFunction === 'undefined') {
            customCloseModalFunction = null;
        }

        function bindCloseOnEscapeOnlyOnce() {
            var doc = $(document);

            if (!doc.data(PMW.getModalInitIdentifier(idModal))) {
                doc.on('keydown.modal', function (id, e) {
                    if ($('#' + idModal + ':not(.no-close)').hasClass('modal-open')) {
                        if (e.which === 27) { // escape key
                            if (typeof customCloseModalFunction === 'function') {
                                customCloseModalFunction();
                            } else {
                                PMW.closeModalDialog(id);
                            }
                        }
                    }
                }.bind(this, idModal));
            }

            doc.data(PMW.getModalInitIdentifier(idModal), true);
        }

        /**
         * Runs the callback if the click was outside of the modal .content
         * @param {string} id
         * @param e
         * @param {function} cb
         */
        function doIfClickedInAreaOutsideModal(id, e, cb) {
            if ($('#' + idModal).hasClass('modal-open')) {
                var dlgContentSelector = '#' + id + ' .modal-content',
                    tar = $(e.target),
                    modalEl = document.getElementById(idModal),
                    isClickInsideDialogButNotOnContent = tar.length > 0
                        && (tar.parents(dlgContentSelector).length === 0 && (tar.parents('#' + idModal).length > 0 || e.target === modalEl));

                if (isClickInsideDialogButNotOnContent) {
                    cb();
                }
            }
        }

        bindCloseOnEscapeOnlyOnce.call(this);
        var modalSelector = $('#' + idModal + ':not(.close-only-by-esc):not(.no-close)');

        var hasBeenClickedOutsideModal = false;

        modalSelector.on('mousedown touchstart', function (id, e) {
            doIfClickedInAreaOutsideModal.call(this, id, e, function () {
                hasBeenClickedOutsideModal = true;
            });
        }.bind(this, idModal));

        modalSelector.on('mouseup touchend', function (id, e) {
            doIfClickedInAreaOutsideModal.call(this, id, e, function (id) {
                if (hasBeenClickedOutsideModal) {
                    if (typeof customCloseModalFunction === 'function') {
                        customCloseModalFunction();
                    } else {
                        PMW.closeModalDialog(id);
                    }
                }

                hasBeenClickedOutsideModal = false;
            }.bind(this, id));
        }.bind(this, idModal));
    },
    /**
     * @param {string} id
     * @return {string}
     */
    getModalInitIdentifier: function (id) {
        return 'modal-id-' + id;
    },
    /**
     * Adds a warning text to the existing modal
     *
     * @param {jQuery} modal
     * @param {string} text
     */
    addModalWarning: function (modal, text) {
        modal.find('.header').after('<div class="modal-message-strip -warning"><span><i class="icon icon-info"></i>' + text + '</span></div>');
    },
    /**
     * Adds a error text to the existing modal
     *
     * @param {jQuery} modal
     * @param {string} text
     */
    addModalError: function (modal, text) {
        modal.find('.header').after('<div class="modal-message-strip -error"><span><i class="icon icon-exclamation-triangle"></i>' + text + '</span></div>');
    },
    /**
     * Removes the error message currently displayed near the top of a modal.
     * @param modal
     */
    removeModalMessage: function (modal) {
        modal.find('.modal-message-strip').slideUp(300).remove();
    },
    /**
     * Adds tabs to the header of the modal dialog
     *
     * @param {jQuery} modal
     * @param {ModalTopAction[]} tabs
     */
    addTabsToDialog: function (modal, tabs) {
        var tabsHTML = $('<div class="modal-header-tabs"></div>'),
            header = modal.find('.header');
        header.addClass('has-tabs');

        /**
         * @param {ModalTopAction} tab
         */
        $.each(tabs, function (i, tab) {
            var tabHTML = $('<h5 class="modal-header-tab ' + tab.classes + '">' + tab.label + '</h5>');

            tabsHTML.append(tabHTML);
            PMW.onClick(tabHTML, null, function () {
                $(this).addClass('selected').siblings('.modal-header-tab').removeClass('selected');
                tab.callback.call(this);
            });
        });

        header.prepend(tabsHTML);
    },
    /**
     * Destroys the modal dialog. Reopening it will require for the content to be loaded again.
     * @param idModal
     * @returns {*|Deferred}
     */
    destroyModalDialog: function (idModal) {
        PMW.modals[idModal] = 'open-destroy';
        return PMW.closeModalDialog(idModal);
    },
    /**
     * @param {jQuery} modal The dialog for which we want to disconnect the mutation observer
     */
    disconnectTopActionObserver: function (modal) {
        let topActionObserver = modal.data('topActionObserver');

        if (topActionObserver && typeof topActionObserver === 'object') {
            topActionObserver.disconnect();
        }
    },
    /**
     * Closes a modal dialog
     * @param {string} idModal ID of the modal dialog to close
     *
     * @return {Deferred}
     */
    closeModalDialog: function (idModal) {
        var dfd = jQuery.Deferred();
        var modal = $('#' + idModal),
            mc = modal.find('.modal-content'),
            destroy = false;

        if (PMW.modals[idModal] === 'closing') {
            dfd.resolve();
            return dfd.promise();
        }

        if (PMW.modals[idModal] === 'open-destroy') {
            destroy = true;
        }

        PMW.modals[idModal] = 'closing';

        // Remove active tooltips
        let tooltipEl = $('.tooltip');
        tooltipEl.length > 0 ? tooltipEl.remove() : $.noop();

        let closeModalAnimationClass = PMW.getModalAnimationClass(modal, MODAL_ANIMATION_TYPE_CLOSE);
        modal.addClass('modal-closing');
        mc.addClass(closeModalAnimationClass)
            .one(PMW.util.animationEndEvents, function () {
                modal.removeClass('modal-open');
                mc.removeClass(closeModalAnimationClass);

                if (destroy) {
                    modal.remove();
                    delete PMW.modals[idModal];
                    dfd.resolve();
                } else {
                    PMW.modals[idModal] = 'closed';
                    dfd.resolve();
                }
                if ($.isFunction(PMW.queuedOpenModal)) {
                    PMW.queuedOpenModal();
                    PMW.queuedOpenModal = null;
                }

                if ($('.modal.modal-open').length === 0) {
                    $('body').removeClass('-open-modal');
                }
            });

        modal.trigger('modal:close');
        window.dispatchEvent(new CustomEvent(PMW.getEventDialogCloseEvent(), {}));

        PMW.disconnectTopActionObserver(modal);

        return dfd.promise();
    },
    /**
     * Opens a css-driveb dialog. Width/height/opening/closing are to be controlled via CSS. This function only
     * appends to the DOM and adds classes on different events.
     *
     * @param {string} id
     * @param {string} modalClasses Classes to append to the dialog
     * @param {string} htmlContentURL The URL to fetch the HTML content from
     * @param {object} urlParams The params to post to the htmlContentURL
     * @param {function} [cb] Callback function
     * @param {boolean} [showClose] If true, shows the close button on the top-right
     * @param {string} [handleForCustomCloseButton] The element handle on which closeModal is to be bound
     * @param {boolean} [allowCloseWhenClickedOutsideModal] Whether or not the modal should close when a user clicks anywhere outside the modal
     * @param {function} [onCloseCallback] Callback function when modal is closed
     */
    openDialogV2: function (id, modalClasses, htmlContentURL, urlParams, cb, showClose, handleForCustomCloseButton, allowCloseWhenClickedOutsideModal, onCloseCallback) {
        var body = $('body'),
            mc;

        if (typeof cb === 'undefined' || cb === null) {
            cb = function () {
            };
        }

        if (typeof onCloseCallback === 'undefined' || onCloseCallback === null) {
            onCloseCallback = function () {
            };
        }

        if (typeof showClose === 'undefined') {
            showClose = true;
        }

        if (typeof handleForCustomCloseButton === 'undefined') {
            handleForCustomCloseButton = null;
        }

        mc = '<div id="' + id + '" class="modal-v2 -waiting ' + modalClasses + ' "><div class="content"></div></div>';

        body
            .addClass('-open-modal')
            .append(mc);

        var modal = $('#' + id);
        PMW.bringToFrontOfModals(modal);

        PMW.load(modal.find('.content'), htmlContentURL, urlParams, function (id, showClose, handleForCustomCloseButton) {
            // open the modal once the content is loaded
            modal.addClass('-open');
            var t = $('#' + id).find('.content');

            t.parents('.modal-v2').removeClass('-waiting');
            var fc = t.find('>:first-child'),
                elementsToTriggerClose = [];
            fc.addClass('-open');

            if (showClose) {
                fc.append('<i tabindex="0" class="hidden-phone hidden-tablet close-modal-v2 pmw-icon size-icon-24 -dark  icon-close -square"></i>' +
                    '<i tabindex="0" class="hidden-desktop close-modal-v2 pmw-icon size-icon-20 -dark  icon-close -square"></i>');
                elementsToTriggerClose.push(t.find('.close-modal-v2'));
            }

            // For a custom element triggering Close
            if (typeof handleForCustomCloseButton === 'string') {
                elementsToTriggerClose.push($(handleForCustomCloseButton));
            }

            $(elementsToTriggerClose).each(function (id, i, handle) {
                PMW.onClick(handle, null, PMW.closeModalV2.bind(this, id, onCloseCallback));
            }.bind(this, id));

            // Adding namespace for document events so that they can be removed on close
            $(document).on('keydown.modalv2', function (id, e) {
                if (e.which === 27) { // escape key
                    PMW.closeModalV2.call(this, id, onCloseCallback);
                }
            }.bind(this, id))
                .on('click.modalv2', function (id, e) {
                    var dlgContentSelector = '#' + id + ' .content:first-child';

                    if ($(e.target).parents(dlgContentSelector).length === 0 && allowCloseWhenClickedOutsideModal) {
                        PMW.closeModalV2.call(this, id, onCloseCallback);
                    }
                }.bind(this, id));

            cb();
        }.bind(this, id, showClose, handleForCustomCloseButton));
    },

    /**
     * Closes the v2 type modal dialog
     * @param {string} id
     * @param {function} [cb]
     */
    closeModalV2: function (id, cb) {
        if (typeof cb === 'undefined' || cb === null) {
            cb = function () {
            };
        }

        var el = $('#' + id),
            zoomOutEl = el.find('>.content').find('>:first-child');


        if ($('.modal.modal-open').length === 0) {
            $('body').removeClass('-open-modal');
        }

        zoomOutEl.on(PMW.util.animationEndEvents, function () {
            el.addClass('-close');
            el.remove();
            cb();
        });

        el.addClass('-closing');
        zoomOutEl.addClass('-close');

        // Remove the click handler from the document element (attached in openDialogV2)
        $(document).off('keydown.modalv2')
            .off('click.modalv2');
    },
    /**
     * Initializes the reusable dropdown widget. On clicking a dropdown-content-item, the item is selected and all its
     * siblings are deselected.
     * The dropdown triggers the "update" event on change.
     *
     * To have a CTA in the dropdown (one that is not selected on click, rather acts as a button),
     * you must have a class -cta appended to it.
     * @param id
     * @param {function} [cb] optional callback function to run when an option from dropdown is selected
     */
    initDropDown: function (id, cb) {
        const jqSel = $('#' + id),
            p = jqSel.parent('.pmw-dropdown'),
            lbl = jqSel.siblings('.dropdown-label'),
            ic = lbl.find('.dropdown-icon');

        if (lbl.attr('data-value')) {
            jqSel.attr('data-value', lbl.attr('data-value'));
        }
        $(document).mouseup(function (e) {
            if ((!($(e.target).parents('.pmw-dropdown').length > 0 || jqSel.find('.dropdown-label').is(e.target))) && jqSel.prop('checked')) {
                jqSel.prop('checked', false).change();
            }
        });

        jqSel.on('value', function (e, v) {
            var p = jqSel.parent('.pmw-dropdown'),
                selVal = p.find('.dropdown-content-item[data-value="' + v + '"]');

            if (selVal.length > 0) {
                ic.appendTo(p.find('.dropdown-label').html(selVal.html()));
                selVal.addClass('dropdown-selected').siblings().removeClass('dropdown-selected');
                jqSel.attr('data-value', v);
                jqSel.trigger('update');
            }
        });

        PMW.onClick(lbl, null, function () {
            jqSel.prop('checked', !jqSel.prop('checked')).change();
        });

        PMW.onClick(jqSel.parent('.pmw-dropdown'), '.dropdown-content-item', selectPmwDropdownOption);

        /**
         * Selects a PMW dropdown and redirects to relevant page if available
         */
        function selectPmwDropdownOption() {
            let t = $(this);
            if (!t.hasClass('-cta') && !t.hasClass('js-external-option')) {
                t.addClass('dropdown-selected').siblings().removeClass('dropdown-selected');

                let dropdownLabel = p.find('.dropdown-label:visible');

                if (dropdownLabel.hasClass('-button')) {
                    dropdownLabel.find('.btn-text').html(t.html());
                } else {
                    ic.appendTo(dropdownLabel.html(t.html()));
                }

                jqSel.attr('data-value', $(this).attr('data-value'));

                jqSel.prop('checked', false).change();
                jqSel.trigger('update');
                if (typeof t.attr('data-href') !== 'undefined' && t.attr('data-href') !== '') {
                    let url = t.attr('data-href'),
                        isHashUrl = '#' === url.substring(0, 1);

                    if (isHashUrl) {
                        window.location.hash = url;
                    } else {
                        window.location.href = url;
                    }
                }
            }

            if (typeof cb === 'function') {
                cb(t);
            }
        }

        jqSel.on('change', function () {
            ic.toggleClass('-inverted');
            p.toggleClass('-open');
        });

        p.find('.dropdown-content-item[data-value="' + p.find('.dropdown-label').attr('data-value') + '"]').addClass('dropdown-selected');
    },
    /**
     * Returns true if user agent string contains `convertify`.
     * Note: When mobile app is being used then user agent contains the string `convertify`
     */
    isMobileApp: function () {
        return navigator.userAgent.toLowerCase().indexOf('convertify') !== -1;
    },

    /**
     * Returns true if the current screen width is a "mobile" screen
     * A mobile phone screen is typically 480px wide or less.
     * @returns {boolean}
     */
    isMobileScreenWidth: function () {
        return $(window).width() <= PMW.MOBILE_SCREEN_BREAKPOINT;
    },

    /**
     * Returns true of screen width is below ipad screen breakpoint
     *
     * @returns {boolean}
     */
    isIpadScreenWidth: function () {
        let screenWidth = $(window).width();
        return screenWidth > PMW.MOBILE_SCREEN_BREAKPOINT && screenWidth <= PMW.TABLET_SCREEN_BREAKPOINT;
    },
    /**
     * Updates the title displayed for the modal dialog
     * @param {jQuery} modal
     * @param {string} title
     * @param {boolean} isCaseSensitive
     */
    updateModalDialogTitle: function (modal, title, isCaseSensitive = false) {
        let titleEl = modal.find('.title h2');
        titleEl.text(title);

        if (isCaseSensitive) {
            titleEl.addClass('-no-text-transform');
        }
    },
    /**
     * Initializes the lazyload asset group + lazyloads all graphics with the class pmw-lazy and with a data-src set.
     */
    initAndLazyloadGraphics: function () {
        PMW.util.require(typeof PMW.initLazyLoadGraphics === 'undefined', 'lazyloadGraphics', false, function () {
            PMW.initLazyLoadGraphics();
        });
    },
    /**
     * Creates and attaches the .footer div (used for buttons) to the modal if it does not exist already
     * @param {jQuery} modal
     */
    appendFooterToModalDialog: function (modal) {
        if (modal.find('.footer').length === 0) {
            var mc = modal.find('.modal-content'),
                footer = $('<div class="footer vertical-align-wrap"><div class="vertical-align"><div class="primary-actions"></div></div></div>'),
                content = mc.find('.content');

            mc.append(footer);

            // set the height of the .content section to be the modal dialog's height minus the .header height, .footer height and any padding the .content has
            if (!modal.hasClass('-auto')) {
                content.innerHeight(mc.outerHeight() - modal.find('.header').outerHeight(true) - footer.outerHeight(true) - parseInt(content.css('padding-top').replace('px', ''), 10) - parseInt(content.css('padding-bottom').replace('px', ''), 10));
            }
        }
    },
    /**
     * Creates buttons, sets up their click events and attaches them to the .primary-actions div of the modal passed
     * @param {jQuery} modal
     * @param {object} buttons
     * @param {boolean} [replace=true] Pass as FALSE if new buttons should appear alongside existing buttons
     */
    createModalDialogButtons: function (modal, buttons, replace) {
        // ensure that the .footer div exists
        PMW.appendFooterToModalDialog(modal);

        // remove any existing buttons if replace is not passed as FALSE
        if (typeof replace === 'undefined' || replace === true) {
            modal.find('.primary-actions .btn').remove();
        }

        PMW.initButtonsOnDialog(buttons, modal);
    },
    /**
     * Opens a free edit dialog, gives the options to create the poster for free or to place a new order.
     * @param {object} response
     * @param {puremvc.Facade} [facade] Reference to the PureMVC facade if this dialog is being opened from the PosterMaker.
     * It is used for sending notifications to the framework for showing loading animations.
     */
    freeEditDialog: function (response, facade) {
        const idOrder = response.idOrder;
        const checkout = response.checkout;
        let buttons = {};

        buttons[i18next.t('pmwjs_recreate_for_free')] = function () {
            // Give time for the Google Analytics event to get tracked
            setTimeout(function () {
                window.location.href = PMW.util.site_url('cart/downloadregen/' + idOrder);
            }, 250);
        };
        buttons[i18next.t('pmwjs_place_new_order')] = function () {
            if (response.url) {
                PMW.postCheckoutProcessing(response);
            } else {
                PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(parseInt(checkout.priceId), parseInt(checkout.id), checkout.posterHash, parseInt(checkout.idProduct));
            }
            PMW.closeModalDialog('free-edit');
        };

        const modal = PMW.openModalDialog('free-edit', i18next.t('pmwjs_free_edit'), buttons, false, PMW.isPosterMaker() ? '-flex' : '');
        if (modal.find('.content p').length === 0) {
            modal.find('.content').html('<p>' + i18next.t('pmwjs_recently_purchased_allow_three_edits') + '</p>');
        }
    },
    /**
     * After a user checks out, the response is processed in this function.
     * It checks whether the order is being regenerated, if there is a valid
     * promo code or if an error took place.
     * @param {object} response
     * @param {jQuery} [form=jQuery] The form that was submitted
     * @param {function} [callback] A callback to fire on successful payment
     */
    postCheckoutProcessing: function (response, form, callback) {
        form = typeof form === 'undefined' ? $('.js-form-buy-download') : form;
        var em = i18next.t('pmwjs_error_processing_request_try_again');

        if (PMW && $.isFunction(PMW.setLoading)) {
            PMW.setLoading(null);
            if (form) {
                form.find('[type="submit"]').prop('disabled', '').parent().removeClass('disabled');
            }
        }

        if (response == null) {
            alert(em);
        } else {
            if (response.message) {
                if (response.premium > 0) {
                    PMW.premiumContentBlockMessage();
                } else if (form) {
                    form.find('.promo-error').removeClass('_hidden').html(response.message);
                }
            } else if (response.url) {
                window.location.href = response.url;
            } else if (response.checkout) {
                if (typeof callback === 'function') {
                    response.checkout.is = 1;
                    response.checkout.cb = callback;
                    PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(response.checkout.priceId, response.checkout.id, response.checkout.posterHash, response.checkout.idProduct, true, callback);
                } else {
                    PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(response.checkout.priceId, response.checkout.id, response.checkout.posterHash, response.checkout.idProduct);
                }
            } else {
                alert(em);
            }
        }
    },
    /**
     * Asynchronously loads HTML for the AddThis sharing toolbox
     * and initializes it according to the attributes on the passed
     * element (wrapped by jQuery). The element must be rendered using
     * the /parts/addthis_toolbox PHP view, using the 'toolboxStub' flag.
     * @param {jQuery} el
     * @param {Function} [cb=null] A callback function for when the toolbox has been initialized
     */
    loadAddThisToolbox: function (el, cb) {
        cb = cb || null;
        var u = el.attr('data-url');
        if (u != null && u.length > 0) {

            PMW.load(el, PMW.util.site_url('addthis/toolbox'), {
                url: u,
                title: el.attr('data-title'),
                count: el.attr('data-count'),
                compact: el.attr('data-compact'),
                track: el.attr('data-track'),
                style: el.attr('data-style'),
                loadScript: (typeof addthis === 'undefined'),
            }, function () {
                window.addthis_config = {
                    data_track_addressbar: true,
                    lang: PMW.util.getLanguage(),
                };
                if (typeof addthis === 'undefined') {
                    $.getScript('https://s7.addthis.com/js/300/addthis_widget.js#pubid=jafferhaider&async=1', function () {
                        if (typeof addthis !== 'undefined') {
                            PMW.initAddThis(el.attr('data-gaCategory'), el.attr('data-gaAction'), cb);
                        }
                    }.bind(this));
                } else {
                    PMW.initAddThis(el.attr('data-gaCategory'), el.attr('data-gaAction'), cb);
                }
            });
        }
    },
    /**
     * Initializes the AddThis toolbox
     * @param {function} cb A callback function for when the toolbox has been initialized
     */
    initAddThis: function (cb) {
        if (!PMW.addThisInit) {
            PMW.addThisInit = true;

            addthis.init();
            // When in the PosterBuilder, use the PMW.sharingAfterAction variable to send the appropriate values to Google Analytics
            // TODO figure out a way to do this without global variables
            // Tracking temporarily disabled on 8th Nov 2023
            // if (PMW.isPosterMaker()) {
            //     addthis.addEventListener('addthis.menu.share', function (e) {
            //         PMW.gtm.trackGA4CustomEvent('PosterBuilder_' + PMW.sharingAfterAction);
            //     });
            // }
        }

        addthis.toolbox('.addthis_toolbox_parent');

        if ($.isFunction(cb)) {
            cb();
        }
    },
    /**
     * Returns a function, that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.
     * @param {Function} func
     * @param {Number} wait
     * @param {boolean} [immediate]
     * @returns {Function}
     */
    debounce: function (func, wait, immediate) {
        var timeout, result;

        var later = function (context, args) {
            timeout = null;
            if (args) result = func.apply(context, args);
        };

        var debounced = restArgs(function (args) {
            var callNow = immediate && !timeout;
            if (timeout) clearTimeout(timeout);
            if (callNow) {
                timeout = setTimeout(later, wait);
                result = func.apply(this, args);
            } else if (!immediate) {
                timeout = PMW.delay(later, wait, this, args);
            }

            return result;
        });

        debounced.clear = function () {
            clearTimeout(timeout);
            timeout = null;
        };

        return debounced;
    },
    // Delays a function for the given number of milliseconds, and then calls
    // it with the arguments supplied.
    delay: restArgs(function (func, wait, args) {
        return setTimeout(function () {
            return func.apply(null, args);
        }, wait);
    }),
    /**
     * Ensures that the cache only contains data from the latest version of the code.
     * @param {string} namespace Determines which cache we are using, i.e. pm, web
     * @param {number} v
     */
    initializeCache: function (namespace, v) {
        PMW.cacheNamespace = namespace;

        var version = this.getCacheItem('version');

        if (version === null || (version !== null && version.number !== v)) {
            this.clearNamespaceFromCache(PMW.cacheNamespace);
            this.setCacheItem('version', {
                number: v,
            });
        }
    },
    /**
     * @param {RequestHelper} request
     * @param {string|null} gaEventType
     */
    loadIntroSlides: function (request, gaEventType = null) {
        PMW.util.require((typeof PMW.openIntroSlides === 'undefined'),
            'introSlides', true, function () {
                PMW.openIntroSlides(request, gaEventType);
            });
    },
    /**
     * Saves an item in the cache. Attaches an expiry date to it.
     * @param {String} k The cache key
     * @param {*} data The data to store
     * @param {Number} [e] The time till this cache item expires in milliseconds.
     */
    setCacheItem: function (k, data, e) {
        var v = {
            d: data,
            e: (typeof e === 'number' ? e : PMW.DEFAULT_CACHE_EXPIRY) + new Date().getTime(),
        };

        var value = JSON.stringify(v);
        k = PMW.cacheNamespace + '.' + k;
        if (window.localStorage) {
            try {
                window.localStorage.setItem(k, value);
            } catch (ex) {
                // we've probably reached memory limit
                PMW.fallbackCache_[k] = value;
            }
        } else {
            PMW.fallbackCache_[k] = value;
        }
    },
    /**
     * Retrieves a cached item with versioning. Returns null if v (version) invalidates cached version
     * @param {String} k key of the cached item
     * @param {String} vck versioned cache key, cache key for the version
     * @param {*} v version, current/latest version
     * @returns {null|*}
     */
    getVersionedCacheItem: function (k, vck, v) {
        let cachedVersion = this.getCacheItem(vck);
        if (!cachedVersion) {
            this.setCacheItem(vck, v);
            return null;
        }
        if (cachedVersion < v) {
            return null;
        }
        return this.getCacheItem(k);
    },
    /**
     * Deletes a cached item.
     * @param {String} k The cache key
     * @returns {*}
     */
    deleteCacheItem: function (k) {
        k = PMW.cacheNamespace + '.' + k;
        if (window.localStorage) {
            window.localStorage.removeItem(k);

            if (typeof PMW.fallbackCache_[k] !== 'undefined') {
                delete PMW.fallbackCache_[k];
            }
        }
    },
    copyToClipboard: async function (text) {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(text);
        } else {
            PMW.copyTextToCustomClipboard(text);
        }
    },
    readFromClipboard: async function () {
        if (navigator.clipboard) {
            return navigator.clipboard.readText();
        } else {
            return PMW.readTextFromCustomClipboard();
        }
    },
    copyTextToCustomClipboard: function (text) {
        window.sessionStorage.setItem(CLIPBOARD_FALLBACK_KEY, text);
    },
    readTextFromCustomClipboard: function () {
        return window.sessionStorage.getItem(CLIPBOARD_FALLBACK_KEY);
    },
    /**
     * Retrieves a cached item. Returns null if it has expired or cannot be found.
     * @param {String} k The cache key
     * @returns {*}
     */
    getCacheItem: function (k) {
        var v;

        k = PMW.cacheNamespace + '.' + k;
        if (window.localStorage) {
            v = window.localStorage.getItem(k);

            // check fallbackCache, which would've been used if localStorage is full.
            if (v === null && typeof PMW.fallbackCache_[k] === 'string') {
                v = PMW.fallbackCache_[k];
            }
        } else {
            if (typeof PMW.fallbackCache_[k] === 'string') {
                v = PMW.fallbackCache_[k];
            }
        }

        if (v !== null && v.length > 0) {
            try {
                v = JSON.parse(v);
                if (v.e && v.e > new Date().getTime()) {
                    return v.d;
                }
                // Delete item if it has expired
                window.localStorage.removeItem(k);
            } catch (e) {
                PMW.log('getCacheItem encountered parse error for key: ' + k + ' string: ' + v);
                return null;
            }
        }
        return null;
    },
    /**
     * Clears all entries of the namespace from the cache.
     * @param {string} [namespace] Defaults to the current PMW.cacheNamespace
     */
    clearNamespaceFromCache: function (namespace) {
        namespace = typeof namespace === 'undefined' ? PMW.cacheNamespace : namespace;

        var namespaceLength = namespace.length;

        if (window.localStorage) {
            Object.keys(window.localStorage)
                .forEach(function (key) {
                    if (key.substring(0, namespaceLength) == namespace) {
                        window.localStorage.removeItem(key);
                    }
                });
        }

        Object.keys(PMW.fallbackCache_)
            .forEach(function (key) {
                if (key.substring(0, namespaceLength) == namespace) {
                    PMW.fallbackCache_.removeItem(key);
                }
            });
    },
    /**
     * Performs a GET request.
     * @param {String} uri The controller/method URI
     * @param {*} [data]
     * @param {String} [key]
     * @param {number} [cacheControlAge]
     * @param {number} [cacheExpiry]
     * @returns {Deferred}
     */
    getRequest: function (uri, data, key, cacheControlAge, cacheExpiry) {
        return PMW.getRequestAtUrl(PMW.util.site_url(uri), data, key, cacheControlAge, cacheExpiry);
    },
    getRequestAtUrl: function (url, data, key, cacheControlAge, cacheExpiry = undefined) {
        const useCache = typeof key !== 'undefined';

        if (useCache) {
            const d = PMW.getCacheItem(key);

            if (d !== null) {
                const def = $.Deferred();
                const p = def.promise();
                def.resolveWith(this, [d]);
                return p;
            }
        }

        let ajaxOpts = {
            type: 'GET',
            url: url,
            data: data,
            success: function (data) {
                if (useCache) {
                    PMW.setCacheItem(key, data, cacheExpiry);
                }
            },
        };

        if (cacheControlAge !== undefined && !window.PMW.isStorybook()) {
            ajaxOpts.cache = true;
            ajaxOpts.headers = {
                'Cache-Control': 'max-age=' + cacheControlAge,
            };
        }

        addLocalOnlyParamsToAjax(ajaxOpts);
        PMW.addAjaxParamsForSubdomainToSubfolderRequests(ajaxOpts);
        return jQuery.ajax(ajaxOpts);
    },
    /**
     * Initializes the internationalization library, loading the language we require
     * @param {string} lang The language to load
     * @param {function} callback
     * CodeReviewAmer: Duplication between this function and the loadLanguage one in AppProxy. Any plans on keeping this DRY?
     */
    initI18next: function (lang, callback) {
        if (window.PMW.isI18nextLoading) {
            if (typeof (callback) === 'function') {
                document.addEventListener(PMW.i18next_LOADED_EVENT, () => callback(), {once: true});
            }
            return;
        }

        const onSuccess = () => {
            window.PMW.isI18nextLoading = false;
            document.dispatchEvent(new CustomEvent(PMW.i18next_LOADED_EVENT));
            if (typeof (callback) === 'function') {
                callback();
            }
        }

        if (window.i18next && window.i18next.isInitialized) {
            onSuccess();
            return;
        }

        window.PMW.isI18nextLoading = true;
        var namespace = 'postermywalljs',
            i18nextOptions = {
                useLocalStorage: false,
                fallbackLng: 'en',
                lng: lang,
                ns: [namespace],
                defaultNS: namespace,
                resources: {},
                interpolation: {
                    escapeValue: false,
                },
            };

        // Get the language data. `?_=<hash>` is PMW.LANG_VERSION; including it
        // in the local-cache key so a translation-only deploy invalidates the
        // localStorage entry on the next page load instead of waiting for the
        // 3-day TTL or next PMW_JS_VERSION bump.
        var langPath = 'asset/getLanguage/' + encodeURIComponent(lang) + '/' + encodeURIComponent(namespace);
        var langUrl = PMW.LANG_VERSION ? langPath + '?_=' + encodeURIComponent(PMW.LANG_VERSION) : langPath;
        var langCacheKey = PMW.LANG_VERSION ? 'lang_' + lang + '_' + PMW.LANG_VERSION : 'lang_' + lang;
        this.getRequest(langUrl, null, langCacheKey)
            .done(function (data) {
                if (data === false) {
                    PMW.showFatalError('core.js::initI18next data is false ' + window.location.href);
                } else {
                    // Evict orphan lang entries from previous deploys: both
                    // the legacy unsuffixed `lang_<lang>` (pre-LANG_VERSION
                    // code) and stale `lang_<lang>_<oldhash>` siblings.
                    if (PMW.LANG_VERSION && window.localStorage) {
                        var prefix = PMW.cacheNamespace + '.lang_' + lang;
                        var keepKey = PMW.cacheNamespace + '.' + langCacheKey;
                        Object.keys(window.localStorage).forEach(function (k) {
                            if (k.indexOf(prefix) === 0 && k !== keepKey) {
                                window.localStorage.removeItem(k);
                            }
                        });
                    }

                    // Add the data into the resources parameter
                    Object.keys(data).forEach(function (key) {
                        i18nextOptions['resources'][key] = [namespace];
                        i18nextOptions['resources'][key][namespace] = data[key];
                    });

                    // Init the i18next instance
                    i18next.init(i18nextOptions, function (err, t) {
                        if (err) {
                            PMW.showFatalError('core.js::initI18next ' + err + ' '.window.location.href);
                        } else {
                            onSuccess();
                        }
                    });
                }
            }.bind(this))
            .fail(function (xhr, textStatus) {
                PMW.showFatalError('core.js::initI18next getLanguage request failed. ' + textStatus + ' ' + xhr.status + ' ' + window.location.href);
            });
    },
    /**
     * Shows an error message when we couldn't load something critical, i.e. language
     * @param {string} err
     */
    showFatalError: function (err) {
        PMW.log(err);

        // Un-internationalized error message because we couldn't load the language.
        if (window.confirm('Sorry, there was a problem. Press OK to reload the page.')) {
            window.location.reload(true);
        }
    },
    /**
     * Copies the string 'str' into the clipboard and animates a tooltip, if given.
     * @param {string} str
     * @param {jQuery} [tooltip]
     * @param {string} [fadeInDirection = 'up'] either 'up' or 'down'
     */
    copyTextToClipboard: function (str, tooltip, fadeInDirection = 'up') {
        let el = document.createElement('textarea'),
            fadeClass = fadeInDirection === 'up' ? 'fadeInUp' : 'fadeInDown';

        el.value = str;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);

        if (tooltip != null) {
            tooltip.addClass(fadeClass + ' animated fast');
            setTimeout(function () {
                tooltip.removeClass(fadeClass + ' animated fast');
            }, 2000);
        }
    },
    /**
     * @param {HTMLElement} el The element to watch for mutations
     * @param {function} func The function to run on mutations
     * @param {object} opts The properties for the mutation observer
     *
     * @returns {MutationObserver}
     */
    attachAndGetMutationObserverToElement: function (el, func, opts) {
        let observer = new MutationObserver(func);
        observer.observe(el, opts);
        return observer;
    },
    /**
     * Brings the element el to the front by setting it's z-index
     * 1 more than the z-index of any modal that is open.
     * @param {jQuery} el
     */
    bringToFrontOfModals: function (el) {
        var zi = $('.modal, .modal-v2, .ReactModal__Overlay').map(function () {
            return $(this).css('z-index');
        });

        if (zi.length > 0) {
            zi = zi.sort();
            var max = zi[zi.length - 1];

            if (max >= el.css('z-index')) {
                el.css('z-index', parseInt(max, 10) + 1);
            }
        }
    },
    showPendingMarketingPopup: function () {
        if (typeof PMW.marketing !== 'undefined') {
            PMW.marketing.showPendingMarketingPopups();
        }
    },

    getDefaultSmallProfilePictureUrl: function () {
        return PMW.util.asset_url('images/default_profile_picture_small.png');
    },

    isDesignTipsPageOpen: function () {
        return (window.location.hash.includes('#/designtips') || window.location.href.includes('tips') || window.location.href.includes('templateSubmissionTips'));
    },

    /**
     * add headers/ajax params to the ajax object for a cross-domain request from a url with the language code as a subdomain
     * to a url with the language code as a subfolder
     *
     * @param {Object} ajaxOpts
     * @param {Boolean} isCustomXhrObject
     */
    addAjaxParamsForSubdomainToSubfolderRequests: function (ajaxOpts, isCustomXhrObject = false) {
        if (PMW.isLocal() || !PMW.areSubdomainToSubfolderAjaxParamsNeeded) {
            return;
        }

        let validDomains = ['dabeta.postermywall.com', 'da.localpostermywall.com', 'da.postermywall.com'];
        if (validDomains.includes(window.location.hostname)) {
            if (isCustomXhrObject) {
                ajaxOpts.withCredentials = true;
            } else {
                ajaxOpts.xhrFields = {
                    withCredentials: true,
                };
            }
        }
    },
    redirectForStudentUserIfNeeded: function () {
        if (!PMW.isStudent() || PMW.isPosterMaker()) {
            return;
        }

        window.location.href = PMW.util.site_url('posters/mine#/designs');
        history.replaceState('', '', PMW.util.site_url('posters/mine#/designs'));
    }
});


/**
 * add headers/ajax params to the ajax object only if the environment is the local dev environment
 * @param {Object} ajaxOpts
 */
function addLocalOnlyParamsToAjax(ajaxOpts) {
    if (PMW.isLocal()) {
        ajaxOpts.xhrFields = {
            withCredentials: true,
        };
        ajaxOpts.crossDomain = true;
        ajaxOpts.headers = {
            ...ajaxOpts.headers !== undefined ? ajaxOpts.headers : {},
            'X-Requested-With': 'XMLHttpRequest',
        };
    }
}

if (Modernizr && Modernizr.addTest) {
    Modernizr.addTest('edge', function () {
        return !!navigator.userAgent.match(/edge/i);
    });
}

PMW.init();

function addGATrackingForDeskopNav() {
    $('.js-header-search-submit-icon').click(() => {
        const searchTerm = $('.js-search-field-input').val();
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'desktop'
        });
    });

    $('#nav-search-form').submit((event) => {
        const form = event.target;
        const formData = new FormData(form);
        const searchTermInputName = 's';

        const searchTerm = formData.get(searchTermInputName);
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'desktop'
        });
    });
}

function addGATrackingForTabletNav() {
    $('.js-mobile-header-search-submit-icon').click(() => {
        const searchTerm = $('.js-mobile-search-field-input').val();
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'tablet'
        });
    });

    $('#mobile-search-form').submit((event) => {
        const form = event.target;
        const formData = new FormData(form);
        const searchTermInputName = 's';

        const searchTerm = formData.get(searchTermInputName);
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'tablet'
        });
    });
}

function addGATrackingForMobileFloatingNav() {
    $('.js-search-icon').click(() => {
        const searchTerm = $('.js-nav-input-field').val();
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'mobile'
        });
    });

    $('#floating-nav-search-form').submit((event) => {
        const form = event.target;
        const formData = new FormData(form);
        const searchTermInputName = 's';

        const searchTerm = formData.get(searchTermInputName);
        PMW.gtm.trackGA4CustomEvent(PMW.gtm.NAVBAR_SEARCH, {
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.SEARCH_TERM]: searchTerm,
            [PMW.gtm.GA4_EVENT_PARAM_NAMES.PLATFORM]: 'mobile'
        });
    });
}

function initNotifications() {
    if (!window.PMW.isUserLoggedIn()) {
        return;
    }

    const notifRootExists = !!document.getElementById('js-notifications-button');
    if (typeof window.PMW.initNotificationsDropdown === 'function') {
        window.PMW.initNotificationsDropdown();
        return;
    }

    if (notifRootExists) {
        window.PMW.util.requireFrontendBundle('notifications', () => {
            window.PMW.initNotificationsDropdown();
        });
    }
}

function openCheckoutForRedirectBasedPayment() {
    const sessionId = new URLSearchParams(window.location.search).get('csid');
    if (sessionId !== null && sessionId !== '') {
        window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, function () {
            const url = new URL(window.location.href);
            url.searchParams.delete('csid');
            window.history.replaceState(null, '', url.toString());
            PMW.stripeCheckout.showCheckoutForPaymentProcessing(sessionId);
        });
    }
}

const LP_SUTM_COOKIE_NAME = 'pmw_lp_sutm';
// 2h in days — matches sess_expiration. The cookie's max-age is refreshed on every page load
// while the user is active, so it slides with the session the same way PHP's session does:
// alive as long as activity continues, expires after ~2h of inactivity.
const LP_SUTM_COOKIE_TTL_DAYS = 7200 / 86400;

function captureAttributionCookieIfNeeded() {
    const existing = document.cookie.split(';')
        .map(c => c.trim())
        .find(c => c.startsWith(LP_SUTM_COOKIE_NAME + '='));

    if (existing) {
        // First-touch within the user's session wins — never overwrite an existing cookie even
        // if the URL carries different utm_* params. Refresh its max-age so the cookie slides
        // with continued activity. Once the user is idle for longer than the TTL, the browser
        // deletes the cookie and a subsequent URL utm can capture a fresh attribution.
        let value;
        try {
            value = decodeURIComponent(existing.substring(LP_SUTM_COOKIE_NAME.length + 1));
        } catch (_) {
            // Malformed %-sequence — abandon the refresh. The cookie will hit its own max-age
            // and disappear; we don't want to overwrite from a possibly empty URL fallback.
            return;
        }
        PMW.util.setCookie(LP_SUTM_COOKIE_NAME, encodeURIComponent(value), LP_SUTM_COOKIE_TTL_DAYS);
        return;
    }

    // No existing cookie: if the URL carries utm_* params, persist them so the backend can
    // attribute later auth / purchase events to this campaign. Cookie value format matches the
    // Lovable LP shape: timestamp~campaign~source~medium~content.
    const urlParams = new URLSearchParams(window.location.search);
    const utmCampaign = urlParams.get('utm_campaign') ?? '';
    const utmSource = urlParams.get('utm_source') ?? '';
    const utmMedium = urlParams.get('utm_medium') ?? '';
    const utmContent = urlParams.get('utm_content') ?? '';
    if (utmCampaign === '' && utmSource === '' && utmMedium === '' && utmContent === '') {
        return;
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const value = `${timestamp}~${utmCampaign}~${utmSource}~${utmMedium}~${utmContent}`;
    PMW.util.setCookie(LP_SUTM_COOKIE_NAME, encodeURIComponent(value), LP_SUTM_COOKIE_TTL_DAYS);
}

function initFooterLangSelector() {
    const btn = $('.js-dropdown-trigger');
    const menu = $('.js-dropdown-menu');
    const searchInput = $('.js-search-input');
    const list = $('.js-lang-list');
    const selectedText = $('.js-selected-lang');
    const chevron = btn.find('.js-chevron');

    const items = list.find('.js-lang-item');
    const noResults = list.find('.js-no-results');

    let isOpen = false;

    function toggleDropdown() {
        isOpen = !isOpen;

        if (isOpen) {
            menu.removeClass('_hidden');
            chevron.addClass('rotate-180');
            btn.addClass('open');
            searchInput.val('');
            noResults.hide();

            setTimeout(() => searchInput.trigger('focus'), 100);
        } else {
            menu.addClass('_hidden');
            chevron.removeClass('rotate-180');
            btn.removeClass('open');
            setTimeout(() => btn.blur(), 100); // Focus out search input
        }
    }

    PMW.onClick(btn, null, function (e) {
        e.stopPropagation();
        toggleDropdown();
    });

    searchInput.on('input', function () {
        const query = $(this).val().toLowerCase();
        let matchCount = 0;

        items.each(function () {
            const item = $(this);
            const text = item.text().toLowerCase();

            if (text.indexOf(query) > -1) {
                item.show();
                matchCount++;
            } else {
                item.hide();
            }
        });

        if (matchCount === 0) {
            noResults.removeClass('_hidden');
        } else {
            noResults.addClass('_hidden');
        }
    });

    list.on('click', '.language-item', function () {
        const item = $(this);
        const text = item.text();

        selectedText.text(text);

        items.removeClass('selected');
        item.addClass('selected');

        toggleDropdown();
        window.location.href = item.attr('data-url');
    });

    $(document).on('click', function (e) {
        if (
            isOpen &&
            !menu.is(e.target) &&
            menu.has(e.target).length === 0 &&
            !btn.is(e.target) &&
            btn.has(e.target).length === 0
        ) {
            toggleDropdown();
        }
    });

    menu.on('click', function (e) {
        e.stopPropagation()
    });
}

function initMobileFooterColumns() {
    PMW.onClick($('.js-title-container'), null, function () {
        if ($(window).width() > 650) {
            return;
        }

        const titleContainer = $(this);
        const itemsContainer = titleContainer.siblings('.js-items-container');
        const chevron = titleContainer.find('.js-chevron');

        if (titleContainer.hasClass('-open')) {
            chevron.addClass('rotate-180');
            titleContainer.removeClass('-open');
            itemsContainer.removeClass('-slide-down');
        } else {
            chevron.removeClass('rotate-180');
            titleContainer.addClass('-open');
            itemsContainer.addClass('-slide-down');
        }
    });
}

function initFooter() {
    initFooterLangSelector();
    initMobileFooterColumns();
}

document.addEventListener('DOMContentLoaded', function () {

    // Catering for cached JS
    if (typeof PMW.bindAccessibilityEvents !== 'undefined') {
        PMW.bindAccessibilityEvents();
    }
    // attaches formSubmit to submit handler of all the forms which do not have any class name `js-form`.
    // Note: All forms which get intercepted by js should have a class with name js-form.
    $('form:not(".js-form")').each(function () {
        $(this).submit(PMW.formSubmit);
    });

    captureAttributionCookieIfNeeded();
    openCheckoutForRedirectBasedPayment();

    initFooter();
});
